import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpParams, HttpResponse, HttpErrorResponse, HttpEventType, HttpClientModule } from '@angular/common/http';
import { of, throwError, Subject, map as map$1, firstValueFrom, EMPTY, Observable, forkJoin, NEVER } from 'rxjs';
import { tap, startWith, map, expand, reduce, finalize, switchMap, catchError } from 'rxjs/operators';
import * as i0 from '@angular/core';
import { EventEmitter, Injectable, InjectionToken, NgModule } from '@angular/core';

var PathSegmentNames;
(function (PathSegmentNames) {
    PathSegmentNames["batch"] = "batch";
    PathSegmentNames["metadata"] = "metadata";
    PathSegmentNames["entitySet"] = "entitySet";
    PathSegmentNames["singleton"] = "singleton";
    PathSegmentNames["type"] = "type";
    PathSegmentNames["property"] = "property";
    PathSegmentNames["navigationProperty"] = "navigationProperty";
    PathSegmentNames["reference"] = "reference";
    PathSegmentNames["value"] = "value";
    PathSegmentNames["count"] = "count";
    PathSegmentNames["function"] = "function";
    PathSegmentNames["action"] = "action";
})(PathSegmentNames || (PathSegmentNames = {}));
var QueryOptionNames;
(function (QueryOptionNames) {
    QueryOptionNames["select"] = "select";
    QueryOptionNames["expand"] = "expand";
    QueryOptionNames["compute"] = "compute";
    QueryOptionNames["filter"] = "filter";
    QueryOptionNames["search"] = "search";
    QueryOptionNames["transform"] = "transform";
    QueryOptionNames["orderBy"] = "orderBy";
    QueryOptionNames["top"] = "top";
    QueryOptionNames["skip"] = "skip";
    QueryOptionNames["skiptoken"] = "skiptoken";
    QueryOptionNames["format"] = "format";
    QueryOptionNames["levels"] = "levels";
    QueryOptionNames["count"] = "count";
})(QueryOptionNames || (QueryOptionNames = {}));
const NONE_PARSER = {
    deserialize: (value) => value,
    serialize: (value) => value,
    encode: (value) => value,
};
//#endregion

const $ID = '$id';
const ODATA_ID = '@odata.id';
// SEGMENTS
const $METADATA = '$metadata';
const $BATCH = '$batch';
const $REF = '$ref';
const $VALUE = '$value';
const $COUNT = '$count';
const $QUERY = '$query';
const $INLINECOUNT = '$inlinecount';
// HTTP HEADERS
const IF_MATCH_HEADER = 'If-Match';
const IF_NONE_MATCH_HEADER = 'If-None-Match';
const CONTENT_TYPE = 'Content-Type';
const CACHE_CONTROL = 'Cache-Control';
const HTTP11 = 'HTTP/1.1';
const ACCEPT = 'Accept';
const PREFER = 'Prefer';
const ODATA_VERSION = 'OData-Version';
const ODATA_VERSION_HEADERS = [
    ODATA_VERSION,
    ODATA_VERSION.toLowerCase(),
    'dataserviceversion',
];
const ETAG_HEADER = 'ETag';
const ETAG_HEADERS = [ETAG_HEADER, ETAG_HEADER.toLowerCase()];
const LOCATION_HEADER = 'Location';
const LOCATION_HEADERS = [
    LOCATION_HEADER,
    LOCATION_HEADER.toLowerCase(),
];
const ODATA_ENTITYID = 'OData-EntityId';
const ODATA_ENTITYID_HEADERS = [
    ODATA_ENTITYID,
    ODATA_ENTITYID.toLowerCase(),
];
const PREFERENCE_APPLIED = 'Preference-Applied';
const PREFERENCE_APPLIED_HEADERS = [
    PREFERENCE_APPLIED,
    PREFERENCE_APPLIED.toLowerCase(),
];
const RETRY_AFTER = 'Retry-After';
const RETRY_AFTER_HEADERS = [RETRY_AFTER, RETRY_AFTER.toLowerCase()];
// HTTP HEADER VALUES
const APPLICATION_JSON = 'application/json';
const APPLICATION_HTTP = 'application/http';
const TEXT_PLAIN = 'text/plain';
const MULTIPART_MIXED = 'multipart/mixed';
const MULTIPART_MIXED_BOUNDARY = 'multipart/mixed;boundary=';
const CONTENT_TRANSFER_ENCODING = 'Content-Transfer-Encoding';
const CONTENT_ID = 'Content-ID';
const MAX_AGE = 'max-age';
// VERSIONS
const VERSION_4_0 = '4.0';
const VERSION_3_0 = '3.0';
const VERSION_2_0 = '2.0';
const DEFAULT_VERSION = VERSION_4_0;
const BINARY = 'binary';
const BOUNDARY_PREFIX_SUFFIX = '--';
const BATCH_PREFIX = 'batch_';
const CHANGESET_PREFIX = 'changeset_';
const DEFAULT_METADATA = 'minimal';
const DEFAULT_STRIP_METADATA = 'full';
const DEFAULT_FETCH_POLICY = 'network-only';
const DEFAULT_TIMEOUT = 60; // Time in seconds
const CALLABLE_BINDING_PARAMETER = 'bindingParameter';
const XSSI_PREFIX = /^\)\]\}',?\n/;
// URL PARTS
const QUERY_SEPARATOR = '?';
const PARAM_SEPARATOR = '&';
const VALUE_SEPARATOR = '=';
const PATH_SEPARATOR = '/';
const ODATA_PARAM_PREFIX = '$';
const ODATA_ALIAS_PREFIX = '@';
const NEWLINE = '\r\n';
const NEWLINE_REGEXP = /\r?\n/;
const CACHE_KEY_SEPARATOR = ':';
// Models
const CID_FIELD_NAME = '_cid';
// Standard vocabularies for annotating OData services
// http://docs.oasis-open.org/odata/odata-vocabularies/v4.0/csprd01/odata-vocabularies-v4.0-csprd01.html
const COMPUTED = /.*Computed$/;
const OPTIMISTIC_CONCURRENCY = /.*OptimisticConcurrency$/;
const DESCRIPTION = /.*Description$/;
const LONG_DESCRIPTION = /.*LongDescription$/;

class ODataCache {
    constructor({ timeout = DEFAULT_TIMEOUT }) {
        this.timeout = timeout;
        this.entries = new Map();
    }
    /**
     * Using the resource on the request build an array of string to identify the scope of the request
     * @param req The request with the resource to build the scope
     * @returns Array of string to identify the scope of the request
     */
    scope(req) {
        const segments = req.resource.cloneSegments();
        return segments.segments({ key: true }).reduce((acc, s) => {
            if (s.name === PathSegmentNames.entitySet)
                acc = [...acc, s.path()];
            return acc;
        }, ['request']);
    }
    /**
     * Using the odata context on the response build an array of string to identify the tags of the response
     * @param res The response to build the tags
     * @returns Array of string to identify the tags of the response
     */
    tags(res) {
        const tags = [];
        const context = res.context;
        if (context.entitySet) {
            tags.push(context.key ? `${context.entitySet}(${context.key})` : context.entitySet);
        }
        if (context.type)
            tags.push(context.type);
        return tags;
    }
    /**
     * Build an entry from a payload and some options
     * @param payload The payload to store in the cache
     * @param timeout The timeout for the entry
     * @param tags The tags for the entry
     * @returns The entry to store in the cache
     */
    buildEntry(payload, { timeout, tags }) {
        return {
            payload,
            lastRead: Date.now(),
            timeout: timeout || this.timeout,
            tags: tags || [],
        };
    }
    /**
     * Build a key from store an entry in the cache
     * @param names The names of the entry
     * @returns The key for the entry
     */
    buildKey(names) {
        return names.join(CACHE_KEY_SEPARATOR);
    }
    /**
     * Put some payload in the cache
     * @param name The name for the entry
     * @param payload The payload to store in the cache
     * @param timeout The timeout for the entry
     * @param scope The scope for the entry
     * @param tags The tags for the entry
     */
    put(name, payload, { timeout, scope, tags, } = {}) {
        const entry = this.buildEntry(payload, { timeout, tags });
        const key = this.buildKey([...(scope || []), name]);
        this.entries.set(key, entry);
        this.forget();
    }
    /**
     * Return the payload from the cache if it exists and is not expired
     * @param name The name of the entry
     * @param scope The scope of the entry
     * @returns The payload of the entry
     */
    get(name, { scope } = {}) {
        const key = this.buildKey([...(scope || []), name]);
        const entry = this.entries.get(key);
        return entry !== undefined && !this.isExpired(entry)
            ? entry.payload
            : undefined;
    }
    /**
     * Remove all cache entries that are matching with the given options
     * @param options The options to forget
     */
    forget({ name, scope = [], tags = [], } = {}) {
        if (name !== undefined)
            scope.push(name);
        const key = scope.length > 0 ? this.buildKey(scope) : undefined;
        this.entries.forEach((entry, k) => {
            if (this.isExpired(entry) || // Expired
                (key !== undefined && k.startsWith(key)) || // Key
                (tags.length > 0 && tags.some((t) => entry.tags.indexOf(t) !== -1)) // Tags
            ) {
                this.entries.delete(k);
            }
        });
    }
    /**
     * Remove all cache entries
     */
    flush() {
        this.entries = new Map();
    }
    /**
     * Check if the entry is expired
     * @param entry The cache entry
     * @returns Boolean indicating if the entry is expired
     */
    isExpired(entry) {
        return entry.lastRead < Date.now() - (entry.timeout || this.timeout) * 1000;
    }
    /**
     * Using the request, handle the fetching of the response
     * @param req The request to fetch
     * @param res$ Observable of the response
     * @returns
     */
    handleRequest(req, res$) {
        return req.isFetch()
            ? this.handleFetch(req, res$)
            : req.isMutate()
                ? this.handleMutate(req, res$)
                : res$;
    }
    handleFetch(req, res$) {
        const policy = req.fetchPolicy;
        const cached = this.getResponse(req);
        if (policy === 'no-cache') {
            return res$;
        }
        if (policy === 'cache-only') {
            if (cached) {
                return of(cached);
            }
            else {
                return throwError(() => new Error('No Cached'));
            }
        }
        if (policy === 'cache-first' ||
            policy === 'cache-and-network' ||
            policy === 'network-only') {
            res$ = res$.pipe(tap((res) => {
                if (res.options.cacheability !== 'no-store')
                    this.putResponse(req, res);
            }));
        }
        return cached !== undefined && policy !== 'network-only'
            ? policy === 'cache-and-network'
                ? res$.pipe(startWith(cached))
                : of(cached)
            : res$;
    }
    handleMutate(req, res$) {
        const requests = req.isBatch()
            ? req.resource
                .requests()
                .filter((r) => r.isMutate())
            : [req];
        for (var r of requests) {
            const scope = this.scope(r);
            this.forget({ scope });
        }
        return res$;
    }
}

class ODataInMemoryCache extends ODataCache {
    constructor({ timeout } = {}) {
        super({ timeout });
    }
    /**
     * Store the response in the cache
     * @param req The request with the resource to store the response
     * @param res The response to store in the cache
     */
    putResponse(req, res) {
        let scope = this.scope(req);
        let tags = this.tags(res);
        this.put(req.cacheKey, res, {
            timeout: res.options.maxAge,
            scope,
            tags,
        });
    }
    /**
     * Restore the response from the cache
     * @param req The request with the resource to get the response
     * @returns The response from the cache
     */
    getResponse(req) {
        let scope = this.scope(req);
        return this.get(req.cacheKey, { scope });
    }
}

const COMPARISON_OPERATORS = ['eq', 'ne', 'gt', 'ge', 'lt', 'le'];
const LOGICAL_OPERATORS = ['and', 'or', 'not'];
const COLLECTION_OPERATORS = ['any', 'all'];
const BOOLEAN_FUNCTIONS = ['startswith', 'endswith', 'contains'];
const SUPPORTED_EXPAND_PROPERTIES = [
    'expand',
    'levels',
    'select',
    'top',
    'count',
    'orderby',
    'filter',
];
const FUNCTION_REGEX = /\((.*)\)/;
const INDEXOF_REGEX = /(?!indexof)\((\w+)\)/;
var StandardAggregateMethods;
(function (StandardAggregateMethods) {
    StandardAggregateMethods["sum"] = "sum";
    StandardAggregateMethods["min"] = "min";
    StandardAggregateMethods["max"] = "max";
    StandardAggregateMethods["average"] = "average";
    StandardAggregateMethods["countdistinct"] = "countdistinct";
})(StandardAggregateMethods || (StandardAggregateMethods = {}));
var QueryCustomTypes;
(function (QueryCustomTypes) {
    QueryCustomTypes[QueryCustomTypes["Raw"] = 0] = "Raw";
    QueryCustomTypes[QueryCustomTypes["Alias"] = 1] = "Alias";
    QueryCustomTypes[QueryCustomTypes["Duration"] = 2] = "Duration";
    QueryCustomTypes[QueryCustomTypes["Binary"] = 3] = "Binary";
})(QueryCustomTypes || (QueryCustomTypes = {}));
//https://docs.oasis-open.org/odata/odata/v4.01/odata-v4.01-part2-url-conventions.html#sec_QueryOptions
const raw = (value) => ({
    type: QueryCustomTypes.Raw,
    value,
});
const alias = (value, name) => ({
    type: QueryCustomTypes.Alias,
    value,
    name,
});
const duration = (value) => ({
    type: QueryCustomTypes.Duration,
    value,
});
const binary = (value) => ({
    type: QueryCustomTypes.Binary,
    value,
});
const isQueryCustomType = (value) => typeof value === 'object' &&
    'type' in value &&
    value.type in QueryCustomTypes;
const ITEM_ROOT = '';
function builder ({ select, search, skiptoken, format, top, skip, filter, transform, orderBy, key, count, expand, action, func, aliases, escape, } = {}) {
    const [path, params] = buildPathAndQuery({
        select,
        search,
        skiptoken,
        format,
        top,
        skip,
        filter,
        transform,
        orderBy,
        key,
        count,
        expand,
        action,
        func,
        aliases,
        escape,
    });
    return buildUrl(path, params);
}
function buildPathAndQuery({ select, search, skiptoken, format, top, skip, filter, transform, orderBy, key, count, expand, action, func, aliases, escape, } = {}) {
    let path = '';
    aliases = aliases || [];
    const query = {};
    // key is not (null, undefined)
    if (key != undefined) {
        path += `(${normalizeValue(key, { aliases, escape })})`;
    }
    if (select) {
        query.$select = Array.isArray(select) ? select.join(',') : select;
    }
    if (search) {
        query.$search = search;
    }
    if (skiptoken) {
        query.$skiptoken = skiptoken;
    }
    if (format) {
        query.$format = format;
    }
    if (filter || typeof count === 'object') {
        query.$filter = buildFilter(typeof count === 'object' ? count : filter, {
            aliases,
            escape,
        });
    }
    if (transform) {
        query.$apply = buildTransforms(transform, { aliases, escape });
    }
    if (expand) {
        query.$expand = buildExpand(expand, { aliases, escape });
    }
    if (orderBy) {
        query.$orderby = buildOrderBy(orderBy);
    }
    if (count) {
        if (typeof count === 'boolean') {
            query.$count = true;
        }
        else {
            path += '/$count';
        }
    }
    if (typeof top === 'number') {
        query.$top = top;
    }
    if (typeof skip === 'number') {
        query.$skip = skip;
    }
    if (action) {
        path += `/${action}`;
    }
    if (func) {
        if (typeof func === 'string') {
            path += `/${func}()`;
        }
        else if (typeof func === 'object') {
            const [funcName] = Object.keys(func);
            const funcArgs = normalizeValue(func[funcName], {
                aliases,
                escape,
            });
            path += `/${funcName}(${funcArgs})`;
        }
    }
    if (aliases.length > 0) {
        Object.assign(query, aliases.reduce((acc, alias) => Object.assign(acc, {
            [`@${alias.name}`]: normalizeValue(alias.value, {
                escape,
            }),
        }), {}));
    }
    // Filter empty values
    const params = Object.entries(query)
        .filter(([, value]) => value !== undefined && value !== '')
        .reduce((acc, [key, value]) => Object.assign(acc, { [key]: value }), {});
    return [path, params];
}
function renderPrimitiveValue(key, val, { aliases, escape, }) {
    return `${key} eq ${normalizeValue(val, { aliases, escape })}`;
}
function buildFilter(filters = {}, { aliases, propPrefix, escape, }) {
    return (Array.isArray(filters) ? filters : [filters]).reduce((acc, filter) => {
        if (filter) {
            const builtFilter = buildFilterCore(filter, {
                aliases,
                propPrefix,
                escape,
            });
            if (builtFilter) {
                acc.push(builtFilter);
            }
        }
        return acc;
    }, []).join(' and ');
    function buildFilterCore(filter = {}, { aliases, propPrefix, escape, }) {
        let filterExpr = '';
        if (typeof filter === 'string') {
            // Use raw filter string
            filterExpr = filter;
        }
        else if (filter && typeof filter === 'object') {
            const filtersArray = Object.keys(filter).reduce((result, filterKey) => {
                const value = filter[filterKey];
                let propName = '';
                if (propPrefix) {
                    if (filterKey === ITEM_ROOT) {
                        propName = propPrefix;
                    }
                    else if (INDEXOF_REGEX.test(filterKey)) {
                        propName = filterKey.replace(INDEXOF_REGEX, (_, $1) => $1.trim() === ITEM_ROOT
                            ? `(${propPrefix})`
                            : `(${propPrefix}/${$1.trim()})`);
                    }
                    else if (FUNCTION_REGEX.test(filterKey)) {
                        propName = filterKey.replace(FUNCTION_REGEX, (_, $1) => $1.trim() === ITEM_ROOT
                            ? `(${propPrefix})`
                            : `(${propPrefix}/${$1.trim()})`);
                    }
                    else {
                        propName = `${propPrefix}/${filterKey}`;
                    }
                }
                else {
                    propName = filterKey;
                }
                if (filterKey === ITEM_ROOT && Array.isArray(value)) {
                    return result.concat(value.map((arrayValue) => renderPrimitiveValue(propName, arrayValue, { escape, aliases })));
                }
                if (['number', 'string', 'boolean'].indexOf(typeof value) !== -1 ||
                    value instanceof Date ||
                    value === null) {
                    // Simple key/value handled as equals operator
                    result.push(renderPrimitiveValue(propName, value, { aliases, escape }));
                }
                else if (Array.isArray(value)) {
                    const op = filterKey;
                    const builtFilters = value
                        .map((v) => buildFilter(v, { aliases, propPrefix, escape }))
                        .filter((f) => f)
                        .map((f) => LOGICAL_OPERATORS.indexOf(op) !== -1 ? `(${f})` : f);
                    if (builtFilters.length) {
                        if (LOGICAL_OPERATORS.indexOf(op) !== -1) {
                            if (builtFilters.length) {
                                if (op === 'not') {
                                    result.push(parseNot(builtFilters));
                                }
                                else {
                                    result.push(`(${builtFilters.join(` ${op} `)})`);
                                }
                            }
                        }
                        else {
                            result.push(builtFilters.join(` ${op} `));
                        }
                    }
                }
                else if (LOGICAL_OPERATORS.indexOf(propName) !== -1) {
                    const op = propName;
                    const builtFilters = Object.keys(value).map((valueKey) => buildFilterCore({ [valueKey]: value[valueKey] }, { aliases, escape }));
                    if (builtFilters.length) {
                        if (op === 'not') {
                            result.push(parseNot(builtFilters));
                        }
                        else {
                            result.push(`${builtFilters.join(` ${op} `)}`);
                        }
                    }
                }
                else if (typeof value === 'object') {
                    if ('type' in value) {
                        result.push(renderPrimitiveValue(propName, value, { aliases, escape }));
                    }
                    else {
                        const operators = Object.keys(value);
                        operators.forEach((op) => {
                            if (COMPARISON_OPERATORS.indexOf(op) !== -1) {
                                result.push(`${propName} ${op} ${normalizeValue(value[op], {
                                    aliases,
                                    escape,
                                })}`);
                            }
                            else if (LOGICAL_OPERATORS.indexOf(op) !== -1) {
                                if (Array.isArray(value[op])) {
                                    result.push(value[op]
                                        .map((v) => '(' +
                                        buildFilterCore(v, {
                                            aliases,
                                            propPrefix: propName,
                                            escape,
                                        }) +
                                        ')')
                                        .join(` ${op} `));
                                }
                                else {
                                    result.push('(' +
                                        buildFilterCore(value[op], {
                                            aliases,
                                            propPrefix: propName,
                                            escape,
                                        }) +
                                        ')');
                                }
                            }
                            else if (COLLECTION_OPERATORS.indexOf(op) !== -1) {
                                const collectionClause = buildCollectionClause(filterKey.toLowerCase(), value[op], op, propName);
                                if (collectionClause) {
                                    result.push(collectionClause);
                                }
                            }
                            else if (op === 'has') {
                                result.push(`${propName} ${op} ${normalizeValue(value[op], {
                                    aliases,
                                    escape,
                                })}`);
                            }
                            else if (op === 'in') {
                                const resultingValues = Array.isArray(value[op])
                                    ? value[op]
                                    : value[op].value.map((typedValue) => ({
                                        type: value[op].type,
                                        value: typedValue,
                                    }));
                                result.push(propName +
                                    ' in (' +
                                    resultingValues
                                        .map((v) => normalizeValue(v, { aliases, escape }))
                                        .join(',') +
                                    ')');
                            }
                            else if (BOOLEAN_FUNCTIONS.indexOf(op) !== -1) {
                                // Simple boolean functions (startswith, endswith, contains)
                                result.push(`${op}(${propName},${normalizeValue(value[op], {
                                    aliases,
                                    escape,
                                })})`);
                            }
                            else {
                                // Nested property
                                const filter = buildFilterCore(value, {
                                    aliases,
                                    propPrefix: propName,
                                    escape,
                                });
                                if (filter) {
                                    result.push(filter);
                                }
                            }
                        });
                    }
                }
                else if (value === undefined) {
                    // Ignore/omit filter if value is `undefined`
                }
                else {
                    throw new Error(`Unexpected value type: ${value}`);
                }
                return result;
            }, []);
            filterExpr = filtersArray.join(' and ');
        } /* else {
            throw new Error(`Unexpected filters type: ${filter}`);
          } */
        return filterExpr;
    }
    function buildCollectionClause(lambdaParameter, value, op, propName) {
        let clause = '';
        if (typeof value === 'string' || value instanceof String) {
            clause = getStringCollectionClause(lambdaParameter, value, op, propName);
        }
        else if (value) {
            // normalize {any:[{prop1: 1}, {prop2: 1}]} --> {any:{prop1: 1, prop2: 1}}; same for 'all',
            // simple values collection: {any:[{'': 'simpleVal1'}, {'': 'simpleVal2'}]} --> {any:{'': ['simpleVal1', 'simpleVal2']}}; same for 'all',
            const filterValue = Array.isArray(value)
                ? value.reduce((acc, item) => {
                    if (item.hasOwnProperty(ITEM_ROOT)) {
                        if (!acc.hasOwnProperty(ITEM_ROOT)) {
                            acc[ITEM_ROOT] = [];
                        }
                        acc[ITEM_ROOT].push(item[ITEM_ROOT]);
                        return acc;
                    }
                    return { ...acc, ...item };
                }, {})
                : value;
            const filter = buildFilterCore(filterValue, {
                aliases,
                propPrefix: lambdaParameter,
                escape,
            });
            clause = `${propName}/${op}(${filter ? `${lambdaParameter}:${filter}` : ''})`;
        }
        return clause;
    }
}
function getStringCollectionClause(lambdaParameter, value, collectionOperator, propName) {
    let clause = '';
    const conditionOperator = collectionOperator == 'all' ? 'ne' : 'eq';
    clause = `${propName}/${collectionOperator}(${lambdaParameter}: ${lambdaParameter} ${conditionOperator} '${value}')`;
    return clause;
}
function escapeIllegalChars(string) {
    string = string.replace(/%/g, '%25');
    string = string.replace(/\+/g, '%2B');
    string = string.replace(/\//g, '%2F');
    string = string.replace(/\?/g, '%3F');
    string = string.replace(/#/g, '%23');
    string = string.replace(/&/g, '%26');
    string = string.replace(/'/g, "''");
    return string;
}
function normalizeValue(value, { aliases, escape = false, } = {}) {
    if (typeof value === 'string') {
        return escape ? `'${escapeIllegalChars(value)}'` : `'${value}'`;
    }
    else if (value instanceof Date) {
        return value.toISOString();
    }
    else if (typeof value === 'number') {
        return value;
    }
    else if (Array.isArray(value)) {
        return `[${value
            .map((d) => normalizeValue(d, { aliases, escape }))
            .join(',')}]`;
    }
    else if (value === null) {
        return value;
    }
    else if (typeof value === 'object') {
        switch (value.type) {
            case QueryCustomTypes.Raw:
                return value.value;
            case QueryCustomTypes.Duration:
                return `duration'${value.value}'`;
            case QueryCustomTypes.Binary:
                return `binary'${value.value}'`;
            case QueryCustomTypes.Alias:
                // Store
                if (Array.isArray(aliases)) {
                    if (value.name === undefined) {
                        value.name = `a${aliases.length + 1}`;
                    }
                    aliases.push(value);
                }
                return `@${value.name}`;
            default:
                return Object.entries(value)
                    .filter(([, v]) => v !== undefined)
                    .map(([k, v]) => `${k}=${normalizeValue(v, { aliases, escape })}`)
                    .join(',');
        }
    }
    return value;
}
function buildExpand(expands, { aliases, escape = false }) {
    if (typeof expands === 'number') {
        return expands;
    }
    else if (typeof expands === 'string') {
        if (expands.indexOf('/') === -1) {
            return expands;
        }
        // Change `Foo/Bar/Baz` to `Foo($expand=Bar($expand=Baz))`
        return expands
            .split('/')
            .reverse()
            .reduce((results, item, index, arr) => {
            if (index === 0) {
                // Inner-most item
                return `$expand=${item}`;
            }
            else if (index === arr.length - 1) {
                // Outer-most item, don't add `$expand=` prefix (added above)
                return `${item}(${results})`;
            }
            else {
                // Other items
                return `$expand=${item}(${results})`;
            }
        }, '');
    }
    else if (Array.isArray(expands)) {
        return `${expands
            .map((e) => buildExpand(e, { aliases, escape }))
            .join(',')}`;
    }
    else if (typeof expands === 'object') {
        const expandKeys = Object.keys(expands);
        if (expandKeys.some((key) => SUPPORTED_EXPAND_PROPERTIES.indexOf(key.toLowerCase()) !== -1)) {
            return expandKeys
                .map((key) => {
                let value;
                switch (key) {
                    case 'filter':
                        value = buildFilter(expands[key], {
                            aliases,
                            escape,
                        });
                        break;
                    case 'orderBy':
                        value = buildOrderBy(expands[key]);
                        break;
                    case 'levels':
                    case 'count':
                    case 'top':
                    case 'skip':
                        value = `${expands[key]}`;
                        break;
                    default:
                        value = buildExpand(expands[key], { aliases, escape });
                }
                return `$${key.toLowerCase()}=${value}`;
            })
                .join(';');
        }
        else {
            return expandKeys
                .map((key) => {
                const builtExpand = buildExpand(expands[key], { aliases, escape });
                return builtExpand ? `${key}(${builtExpand})` : key;
            })
                .join(',');
        }
    }
    return '';
}
function buildTransforms(transforms, { aliases, escape = false }) {
    // Wrap single object an array for simplified processing
    const transformsArray = Array.isArray(transforms) ? transforms : [transforms];
    const transformsResult = transformsArray.reduce((result, transform) => {
        const { aggregate, filter, groupBy, ...rest } = transform;
        // TODO: support as many of the following:
        //   topcount, topsum, toppercent,
        //   bottomsum, bottomcount, bottompercent,
        //   identity, concat, expand, search, compute, isdefined
        const unsupportedKeys = Object.keys(rest);
        if (unsupportedKeys.length) {
            throw new Error(`Unsupported transform(s): ${unsupportedKeys}`);
        }
        if (aggregate) {
            result.push(`aggregate(${buildAggregate(aggregate)})`);
        }
        if (filter) {
            const builtFilter = buildFilter(filter, { aliases, escape });
            if (builtFilter) {
                result.push(`filter(${buildFilter(builtFilter, { aliases, escape })})`);
            }
        }
        if (groupBy) {
            result.push(`groupby(${buildGroupBy(groupBy, { aliases, escape })})`);
        }
        return result;
    }, []);
    return transformsResult.join('/') || undefined;
}
function buildAggregate(aggregate) {
    // Wrap single object in an array for simplified processing
    const aggregateArray = Array.isArray(aggregate) ? aggregate : [aggregate];
    return aggregateArray
        .map((aggregateItem) => {
        return typeof aggregateItem === 'string'
            ? aggregateItem
            : Object.keys(aggregateItem).map((aggregateKey) => {
                const aggregateValue = aggregateItem[aggregateKey];
                // TODO: Are these always required?  Can/should we default them if so?
                if (!aggregateValue.with) {
                    throw new Error(`'with' property required for '${aggregateKey}'`);
                }
                if (!aggregateValue.as) {
                    throw new Error(`'as' property required for '${aggregateKey}'`);
                }
                return `${aggregateKey} with ${aggregateValue.with} as ${aggregateValue.as}`;
            });
    })
        .join(',');
}
function buildGroupBy(groupBy, { aliases, escape = false }) {
    if (!groupBy.properties) {
        throw new Error(`'properties' property required for groupBy`);
    }
    let result = `(${groupBy.properties.join(',')})`;
    if (groupBy.transform) {
        result += `,${buildTransforms(groupBy.transform, { aliases, escape })}`;
    }
    return result;
}
function buildOrderBy(orderBy, prefix = '') {
    if (Array.isArray(orderBy)) {
        return orderBy
            .map((value) => Array.isArray(value) &&
            value.length === 2 &&
            ['asc', 'desc'].indexOf(value[1]) !== -1
            ? value.join(' ')
            : value)
            .map((v) => `${prefix}${v}`)
            .join(',');
    }
    else if (typeof orderBy === 'object') {
        return Object.entries(orderBy)
            .map(([k, v]) => buildOrderBy(v, `${k}/`))
            .map((v) => `${prefix}${v}`)
            .join(',');
    }
    return `${prefix}${orderBy}`;
}
function buildUrl(path, params) {
    // This can be refactored using URL API. But IE does not support it.
    const queries = Object.entries(params).map(([key, value]) => `${key}=${value}`);
    return queries.length ? `${path}?${queries.join('&')}` : path;
}
function parseNot(builtFilters) {
    return `not(${builtFilters.join(' and ')})`;
}

const ISO_REGEX = /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))/;
const Dates = {
    isoStringToDate(value) {
        if (typeof value === 'string' && value.search(ISO_REGEX) === 0) {
            return new Date(value);
        }
        else if (Array.isArray(value)) {
            return value.map((v) => this.isoStringToDate(v));
        }
        else if (value && value.constructor === Object) {
            return Object.keys(value)
                .map((key) => [key, this.isoStringToDate(value[key])])
                .reduce((acc, v) => Object.assign(acc, { [v[0]]: v[1] }), {});
        }
        return value;
    },
};

const DURATION_REGEX = /^(-|\+)?P(?:([-+]?[0-9,.]*)Y)?(?:([-+]?[0-9,.]*)M)?(?:([-+]?[0-9,.]*)W)?(?:([-+]?[0-9,.]*)D)?(?:T(?:([-+]?[0-9,.]*)H)?(?:([-+]?[0-9,.]*)M)?(?:([-+]?[0-9,.]*)S)?)?$/;
const Durations = {
    toDuration(v) {
        const matches = DURATION_REGEX.exec(v);
        if (!matches || v.length < 3) {
            throw new TypeError(`duration invalid: "${v}". Must be a ISO 8601 duration. See https://en.wikipedia.org/wiki/ISO_8601#Durations`);
        }
        let duration = {};
        duration.sign = matches[1] === '-' ? -1 : 1;
        return [
            'years',
            'months',
            'weeks',
            'days',
            'hours',
            'minutes',
            'seconds',
        ].reduce((acc, name, index) => {
            const v = parseFloat(matches[index + 2]);
            if (!Number.isNaN(v))
                acc[name] = v;
            return acc;
        }, duration);
    },
    toString(v) {
        return [
            v.sign === -1 ? '-' : '',
            'P',
            v.years ? v.years + 'Y' : '',
            v.months ? v.months + 'M' : '',
            v.weeks ? v.weeks + 'W' : '',
            v.days ? v.days + 'D' : '',
            'T',
            v.hours ? v.hours + 'H' : '',
            v.minutes ? v.minutes + 'M' : '',
            v.seconds ? v.seconds + 'S' : '',
        ].join('');
    },
};

const Enums = {
    names(Enum) {
        return Object.values(Enum).filter((v) => typeof v === 'string');
    },
    values(Enum) {
        return Object.values(Enum).filter((v) => typeof v === 'number');
    },
    toValue(Enum, value) {
        if (value in Enum)
            return typeof value === 'string' ? Enum[value] : value;
        return undefined;
    },
    toValues(Enum, value) {
        if (typeof value === 'number') {
            return this.values(Enum).filter((v) => (value & v) === v);
        }
        if (typeof value === 'string') {
            value = value.split(',').map((o) => o.trim());
        }
        if (Array.isArray(value) && value.every((v) => v in Enum)) {
            return value.map((o) => this.toValue(Enum, o));
        }
        return [];
    },
    toName(Enum, value) {
        if (value in Enum)
            return typeof value === 'number' ? Enum[value] : value;
        return undefined;
    },
    toNames(Enum, value) {
        if (typeof value === 'number') {
            return this.values(Enum)
                .filter((v) => (value & v) === v)
                .map((v) => this.toName(Enum, v));
        }
        if (typeof value === 'string') {
            value = value.split(',').map((o) => o.trim());
        }
        if (Array.isArray(value) && value.every((v) => v in Enum)) {
            return value.map((o) => this.toName(Enum, o));
        }
        return [];
    },
};

function cloneSymbol(targe) {
    return Object(Symbol.prototype.valueOf.call(targe));
}
function cloneReg(targe) {
    const reFlags = /\w*$/;
    const result = new targe.constructor(targe.source, reFlags.exec(targe));
    result.lastIndex = targe.lastIndex;
    return result;
}
const Types = {
    rawType(value) {
        return Object.prototype.toString.call(value).slice(8, -1);
    },
    isPlainObject(value) {
        if (this.rawType(value) !== 'Object') {
            return false;
        }
        const prototype = Object.getPrototypeOf(value);
        return prototype === null || prototype === Object.prototype;
    },
    isFunction(value) {
        return typeof value === 'function';
    },
    isArray(value) {
        return Array.isArray(value);
    },
    isMap(value) {
        return this.rawType(value) === 'Map';
    },
    isEmpty(value) {
        return (value === undefined ||
            value === null ||
            (typeof value === 'string' && !value.length) ||
            (value instanceof Date && isNaN(value.valueOf())) ||
            (Types.isMap(value) && !value.size) ||
            (Types.isArray(value) && !value.length) ||
            (Types.isFunction(value.isEmpty) && value.isEmpty()) ||
            (Types.isArray(value) &&
                value.every((v) => Types.isEmpty(v))) ||
            (Types.isPlainObject(value) &&
                !Object.keys(value).filter((k) => value.hasOwnProperty(k)).length));
    },
    isEqual(value1, value2) {
        function getType(obj) {
            return Object.prototype.toString.call(obj).slice(8, -1).toLowerCase();
        }
        function areDatesEqual() {
            return value1.getTime() === value2.getTime();
        }
        function areArraysBufferEqual() {
            if (value1.byteLength !== value2.byteLength) {
                return false;
            }
            var view1 = new DataView(value1);
            var view2 = new DataView(value2);
            var i = value1.byteLength;
            while (i--) {
                if (view1.getUint8(i) !== view2.getUint8(i)) {
                    return false;
                }
            }
            return true;
        }
        function areArraysEqual() {
            // Check length
            if (value1.length !== value2.length)
                return false;
            // Check each item in the array
            for (let i = 0; i < value1.length; i++) {
                if (!Types.isEqual(value1[i], value2[i]))
                    return false;
            }
            // If no errors, return true
            return true;
        }
        function areObjectsEqual() {
            if (Object.keys(value1).length !== Object.keys(value2).length)
                return false;
            // Check each item in the object
            for (let key in value1) {
                if (Object.prototype.hasOwnProperty.call(value1, key)) {
                    if (!Types.isEqual(value1[key], value2[key]))
                        return false;
                }
            }
            // If no errors, return true
            return true;
        }
        function areFunctionsEqual() {
            return value1.toString() === value2.toString();
        }
        function arePrimativesEqual() {
            return value1 === value2;
        }
        // Get the object type
        let type = getType(value1);
        // If the two items are not the same type, return false
        if (type !== getType(value2))
            return false;
        // Compare based on type
        if (type === 'date')
            return areDatesEqual();
        if (type === 'arraybuffer')
            return areArraysBufferEqual();
        if (type === 'array')
            return areArraysEqual();
        if (type === 'object')
            return areObjectsEqual();
        if (type === 'function')
            return areFunctionsEqual();
        return arePrimativesEqual();
    },
    clone(target) {
        const constrFun = target.constructor;
        switch (this.rawType(target)) {
            case 'Boolean':
            case 'Number':
            case 'String':
            case 'Error':
            case 'Date':
                return new constrFun(target);
            case 'RegExp':
                return cloneReg(target);
            case 'Symbol':
                return cloneSymbol(target);
            case 'Function':
                return target;
            default:
                return null;
        }
    },
};

const Http = {
    //Merge Headers
    mergeHttpHeaders(...values) {
        let headers = new HttpHeaders();
        values.forEach((value) => {
            if (value instanceof HttpHeaders) {
                value.keys().forEach((key) => {
                    headers = (value.getAll(key) || []).reduce((acc, v) => acc.append(key, v), headers);
                });
            }
            else if (Types.isPlainObject(value)) {
                Object.entries(value).forEach(([key, value]) => {
                    headers = (Array.isArray(value) ? value : [value]).reduce((acc, v) => acc.append(key, v), headers);
                });
            }
        });
        return headers;
    },
    // Merge Params
    mergeHttpParams(...values) {
        let params = new HttpParams();
        values.forEach((value) => {
            if (value instanceof HttpParams) {
                value.keys().forEach((key) => {
                    params = (value.getAll(key) || []).reduce((acc, v) => acc.append(key, v), params);
                });
            }
            else if (Types.isPlainObject(value)) {
                Object.entries(value).forEach(([key, value]) => {
                    params = (Array.isArray(value) ? value : [value]).reduce((acc, v) => acc.append(key, v), params);
                });
            }
        });
        return params;
    },
    // Split Params
    splitHttpParams(params, keys) {
        let other = new HttpParams();
        params.keys().forEach((key) => {
            if (keys.includes(key)) {
                other = (params.getAll(key) || []).reduce((acc, v) => acc.append(key, v), other);
                params = params.delete(key);
            }
        });
        return [params, other];
    },
    // Without Params
    withoutHttpParams(params, keys) {
        return keys.reduce((acc, key) => acc.delete(key), params);
    },
    resolveHeaderKey(headers, options) {
        if (headers instanceof HttpHeaders) {
            return headers.keys().find((k) => options.indexOf(k) !== -1);
        }
        else if (Types.isPlainObject(headers)) {
            return Object.keys(headers).find((k) => options.indexOf(k) !== -1);
        }
        return undefined;
    },
    headerValue(header) {
        let res = header.split(';')[0].trim();
        res = res.split(':')[1].trim();
        return res;
    },
    parseResponseStatus(statusLine) {
        const status = statusLine.split(' ')[0];
        const code = parseInt(statusLine.split(' ')[1], 10);
        const message = statusLine.split(' ')[2];
        return { status, code, message };
    },
    boundaryDelimiter(contentType) {
        const contentTypeParts = contentType.split(';');
        if (contentTypeParts.length === 2) {
            const boundary = contentType.split(';')[1].trim();
            const boundaryDelimiter = BOUNDARY_PREFIX_SUFFIX + boundary.split('=')[1];
            return boundaryDelimiter;
        }
        else {
            return '';
        }
    },
    boundaryEnd(boundaryDelimiter) {
        if (!boundaryDelimiter.length) {
            return '';
        }
        const boundaryEnd = boundaryDelimiter + BOUNDARY_PREFIX_SUFFIX;
        return boundaryEnd;
    },
};

function forEach(array, iteratee) {
    let index = -1;
    const length = array.length;
    while (++index < length) {
        iteratee(array[index], index);
    }
    return array;
}
const Objects = {
    set(obj, path, value) {
        // Check if path is string or array. Regex : ensure that we do not have '.' and brackets.
        const pathArray = (Types.isArray(path) ? path : path.match(/([^[.\]])+/g));
        pathArray.reduce((acc, key, i) => {
            if (acc[key] === undefined)
                acc[key] = {};
            if (i === pathArray.length - 1)
                acc[key] = value;
            return acc[key];
        }, obj);
    },
    get(obj, path, def) {
        // Check if path is string or array. Regex : ensure that we do not have '.' and brackets.
        const pathArray = (Types.isArray(path) ? path : path.match(/([^[.\]])+/g));
        // Find value if exist return otherwise return undefined value;
        return (pathArray.reduce((prevObj, key) => prevObj && prevObj[key], obj) || def);
    },
    unset(obj, path) {
        // Check if path is string or array. Regex : ensure that we do not have '.' and brackets.
        const pathArray = (Types.isArray(path) ? path : path.match(/([^[.\]])+/g));
        pathArray.reduce((acc, key, i) => {
            if (i === pathArray.length - 1)
                delete acc[key];
            return acc[key];
        }, obj);
    },
    has(obj, path) {
        // Check if path is string or array. Regex : ensure that we do not have '.' and brackets.
        const pathArray = (Types.isArray(path) ? path : path.match(/([^[.\]])+/g));
        return !!pathArray.reduce((prevObj, key) => prevObj && prevObj[key], obj);
    },
    merge(target, source) {
        const merge = (target, source) => {
            for (let attr in source) {
                let value = source[attr];
                if (value !== null && Types.isPlainObject(value) && attr in target) {
                    merge(target[attr], value);
                }
                else if (target[attr] !== value) {
                    target[attr] = value;
                }
            }
        };
        merge(target, source);
        return target;
    },
    equal(object1, object2) {
        const keys1 = Object.keys(object1);
        const keys2 = Object.keys(object2);
        if (keys1.length !== keys2.length) {
            return false;
        }
        for (const key of keys1) {
            const val1 = object1[key];
            const val2 = object2[key];
            const areObjects = Types.isPlainObject(val1) && Types.isPlainObject(val2);
            if ((areObjects && !Objects.equal(val1, val2)) ||
                (!areObjects && val1 !== val2)) {
                return false;
            }
        }
        return true;
    },
    difference(object1, object2) {
        if (!object2 || !Types.isPlainObject(object2)) {
            return object1;
        }
        var diffs = {};
        var key;
        var arraysMatch = function (arr1, arr2) {
            if (arr1.length !== arr2.length)
                return false;
            for (var i = 0; i < arr1.length; i++) {
                if (arr1[i] !== arr2[i])
                    return false;
            }
            return true;
        };
        var compare = function (item1, item2, key) {
            if (item2 === undefined) {
                diffs[key] = null;
                return;
            }
            if (typeof item1 !== typeof item2) {
                diffs[key] = item2;
                return;
            }
            if (Types.isPlainObject(item1)) {
                var objDiff = Objects.difference(item1, item2);
                if (Object.keys(objDiff).length > 0) {
                    diffs[key] = objDiff;
                }
                return;
            }
            if (Array.isArray(item1)) {
                if (!arraysMatch(item1, item2)) {
                    diffs[key] = item2;
                }
                return;
            }
            if (item1 !== item2) {
                diffs[key] = item2;
            }
        };
        for (key in object1) {
            if (object1.hasOwnProperty(key)) {
                compare(object1[key], object2[key], key);
            }
        }
        for (key in object2) {
            if (object2.hasOwnProperty(key)) {
                if (!object1[key] && object1[key] !== object2[key]) {
                    diffs[key] = object2[key];
                }
            }
        }
        return diffs;
    },
    resolveKey(key, { single = true } = {}) {
        const type = Types.rawType(key);
        if (['number', 'string'].indexOf(type) !== -1)
            return key;
        if (type !== 'Map' && type !== 'Object') {
            return undefined;
        }
        const values = type === 'Map' ? Array.from(key.values()) : Object.values(key);
        if (values.length === 1 && single) {
            // Single primitive key value
            key = values[0];
            return !Types.isEmpty(key) ? key : undefined;
        }
        else if (values.some((v) => v === undefined)) {
            // Compose key, needs all values
            return undefined;
        }
        else {
            const obj = type === 'Map' ? Object.fromEntries(key) : key;
            return !Types.isEmpty(obj) ? obj : undefined;
        }
    },
    clone(target, map) {
        if (map === undefined)
            map = new WeakMap();
        // clone primitive types
        if (typeof target != 'object' || target == null) {
            return target;
        }
        if ('clone' in target) {
            // target is a cloneable object
            return target.clone();
        }
        const type = Types.rawType(target);
        let cloneTarget = null;
        if (map.get(target)) {
            return map.get(target);
        }
        map.set(target, cloneTarget);
        if (type != 'Set' && type != 'Map' && type != 'Array' && type != 'Object') {
            return Types.clone(target);
        }
        // clone Set
        if (type == 'Set') {
            cloneTarget = new Set();
            target.forEach((value) => {
                cloneTarget.add(this.clone(value, map));
            });
            return cloneTarget;
        }
        // clone Map
        if (type == 'Map') {
            cloneTarget = new Map();
            target.forEach((value, key) => {
                cloneTarget.set(key, this.clone(value, map));
            });
            return cloneTarget;
        }
        // clone Array
        if (type == 'Array') {
            cloneTarget = new Array();
            forEach(target, (value, index) => {
                cloneTarget[index] = this.clone(value, map);
            });
        }
        // clone normal Object
        if (type == 'Object') {
            cloneTarget = new Object();
            forEach(Object.keys(target), (key, index) => {
                cloneTarget[key] = this.clone(target[key], map);
            });
        }
        return cloneTarget;
    },
};

const OData = {
    // Merge callables parameters
    mergeCallableParameters(callables) {
        const areEqual = (a, b) => a.name === b.name &&
            Objects.equal((a.parameters || {})[CALLABLE_BINDING_PARAMETER] || {}, (b.parameters || {})[CALLABLE_BINDING_PARAMETER] || {});
        return callables.reduce((acc, config) => {
            if (acc.every((c) => !areEqual(c, config))) {
                config = callables
                    .filter((c) => areEqual(c, config))
                    .reduce((acc, c) => {
                    acc.parameters = Object.assign(acc.parameters || {}, c.parameters || {});
                    return acc;
                }, config);
                return [...acc, config];
            }
            return acc;
        }, []);
    },
};

// From https://github.com/adamhalasz/uniqid
var glast;
function now() {
    let time = Date.now();
    let last = glast || time;
    return (glast = time > last ? time : last + 1);
}
const Strings = {
    uniqueId(prefix, suffix) {
        return (prefix ? prefix : '') + now().toString(36) + (suffix ? suffix : '');
    },
    titleCase(text) {
        const result = text.replace(/([a-z])([A-Z])/g, '$1 $2');
        return result
            .split(' ')
            .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
            .join(' ');
    },
};

const Urls = {
    parseQueryString(query) {
        return query.split(PARAM_SEPARATOR).reduce((acc, param) => {
            let index = param.indexOf(VALUE_SEPARATOR);
            if (index !== -1)
                Object.assign(acc, {
                    [param.substring(0, index)]: param.substring(index + 1),
                });
            return acc;
        }, {});
    },
    escapeIllegalChars(string) {
        string = string.replace(/%/g, '%25');
        string = string.replace(/\+/g, '%2B');
        string = string.replace(/\//g, '%2F');
        string = string.replace(/\?/g, '%3F');
        string = string.replace(/#/g, '%23');
        string = string.replace(/&/g, '%26');
        string = string.replace(/'/g, "''");
        return string;
    },
};

class Expression {
    constructor({ children, } = {}) {
        this._children = children || [];
    }
    get [Symbol.toStringTag]() {
        return 'Expression';
    }
    children() {
        return this._children;
    }
    length() {
        return this._children.length;
    }
    toJSON() {
        return {
            children: this._children.map((c) => c.toJSON()),
        };
    }
}

class Field$1 {
    constructor(name = '') {
        this.name = name;
    }
    static factory(name = '') {
        return new Proxy({ _name: name }, new Field$1());
    }
    get(target, key) {
        let name = target['_name'];
        if (key === 'render') {
            return ({ prefix }) => prefix ? `${prefix}/${name}` : name;
        }
        else if (key === 'clone') {
            return () => Field$1.factory(name);
        }
        else if (key === Symbol.toStringTag) {
            return () => 'Field';
        }
        else if (key === 'toJSON') {
            return () => ({
                $type: Types.rawType(this),
                name: name,
            });
        }
        else {
            name = name ? `${name}/${key}` : key;
            return new Proxy({ _name: name }, this);
        }
    }
    has(target, key) {
        return ['toJSON', 'clone', 'render'].includes(key) || key in target;
    }
}
function applyMixins(derivedCtor, constructors) {
    constructors.forEach((baseCtor) => {
        Object.getOwnPropertyNames(baseCtor.prototype).forEach((name) => {
            Object.defineProperty(derivedCtor.prototype, name, Object.getOwnPropertyDescriptor(baseCtor.prototype, name) ||
                Object.create(null));
        });
    });
}
function render(value, { aliases, normalize, escape, prefix, } = {}) {
    if (typeof value === 'function') {
        return render(value(syntax), { aliases, normalize, prefix });
    }
    if (typeof value === 'object' &&
        value !== null &&
        value.render !== undefined) {
        return render(value.render({ aliases, escape, prefix }), {
            aliases,
            normalize,
            escape,
            prefix,
        });
    }
    return normalize ? normalizeValue(value, { aliases, escape }) : value;
}
class Function {
    constructor(name, values, normalize = true, escape = false) {
        this.name = name;
        this.values = values;
        this.normalize = normalize;
        this.escape = escape;
    }
    get [Symbol.toStringTag]() {
        return 'Function';
    }
    toJSON() {
        return {
            $type: Types.rawType(this),
            name: this.name,
            values: this.values,
            normalize: this.normalize,
        };
    }
    render({ aliases, escape, prefix, }) {
        let [field, ...values] = this.values;
        field = render(field, { aliases, escape, prefix });
        const params = [
            field,
            ...values.map((v) => render(v, { aliases, escape, prefix, normalize: this.normalize })),
        ];
        return `${this.name}(${params.join(', ')})`;
    }
    clone() {
        return new Function(this.name, this.values.map((v) => Objects.clone(v)), this.normalize, this.escape);
    }
}
class StringAndCollectionFunctions {
    concat(field, value, normalize) {
        return new Function('concat', [field, value], normalize);
    }
    contains(field, value, normalize) {
        return new Function('contains', [field, value], normalize);
    }
    endsWith(field, value, normalize) {
        return new Function('endswith', [field, value], normalize);
    }
    indexOf(field, value, normalize) {
        return new Function('indexof', [field, value], normalize);
    }
    length(value, normalize) {
        return new Function('length', [value], normalize);
    }
    startsWith(field, value, normalize) {
        return new Function('startswith', [field, value], normalize);
    }
    subString(field, start, length) {
        let values = [field, start];
        if (length !== undefined) {
            values.push(length);
        }
        return new Function('substring', values);
    }
}
class CollectionFunctions {
    hasSubset(s1, s2) {
        return new Function('hassubset', [s1, s2]);
    }
    hasSubsequence(s1, s2) {
        return new Function('hassubsequence', [s1, s2]);
    }
}
class StringFunctions {
    matchesPattern(value, pattern) {
        return new Function('matchesPattern', [value, pattern]);
    }
    toLower(value) {
        return new Function('tolower', [value]);
    }
    toUpper(value) {
        return new Function('toupper', [value]);
    }
    trim(value) {
        return new Function('trim', [value]);
    }
}
class DateAndTimeFunctions {
    date(value) {
        return new Function('date', [value]);
    }
    day(value) {
        return new Function('day', [value]);
    }
    fractionalseconds(value) {
        return new Function('fractionalseconds', [value]);
    }
    hour(value) {
        return new Function('hour', [value]);
    }
    maxdatetime(value) {
        return new Function('maxdatetime', [value]);
    }
    mindatetime(value) {
        return new Function('mindatetime', [value]);
    }
    minute(value) {
        return new Function('minute', [value]);
    }
    month(value) {
        return new Function('month', [value]);
    }
    now() {
        return new Function('now', []);
    }
    second(value) {
        return new Function('second', [value]);
    }
    time(value) {
        return new Function('time', [value]);
    }
    totaloffsetminutes(value) {
        return new Function('totaloffsetminutes', [value]);
    }
    totalseconds(value) {
        return new Function('totalseconds', [value]);
    }
    year(value) {
        return new Function('year', [value]);
    }
}
class ArithmeticFunctions {
    ceiling(value) {
        return new Function('ceiling', [value]);
    }
    floor(value) {
        return new Function('floor', [value]);
    }
    round(value) {
        return new Function('round', [value]);
    }
}
class TypeFunctions {
    cast(value, type) {
        return new Function('cast', [value, type]);
    }
    isof(value, type) {
        return new Function('isof', [value, type]);
    }
}
class GeoFunctions {
    geoDistance(value, point, normalize) {
        return new Function('geo.distance', [value, point], normalize);
    }
    geoIntersects(value, polygon, normalize) {
        return new Function('geo.intersects', [value, polygon], normalize);
    }
    geoLength(line, normalize) {
        return new Function('geo.length', [line], normalize);
    }
}
class ConditionalFunctions {
    case(condition, value) {
        return new Function('case', [condition, value]);
    }
}
class Operator {
    constructor(op, values, normalize = true) {
        this.op = op;
        this.values = values;
        this.normalize = normalize;
    }
    get [Symbol.toStringTag]() {
        return 'Operator';
    }
    toJSON() {
        return {
            $type: Types.rawType(this),
            op: this.op,
            values: this.values,
            normalize: this.normalize,
        };
    }
    render({ aliases, escape, prefix, }) {
        let [left, right] = this.values;
        left = render(left, { aliases, escape, prefix });
        if (right !== undefined) {
            right = Array.isArray(right)
                ? `(${right
                    .map((v) => render(v, {
                    aliases,
                    escape,
                    prefix,
                    normalize: this.normalize,
                }))
                    .join(',')})`
                : render(right, {
                    aliases,
                    escape,
                    prefix,
                    normalize: this.normalize,
                });
            return `${left} ${this.op} ${right}`;
        }
        return `${this.op}(${left})`;
    }
    clone() {
        return new Operator(this.op, this.values.map((v) => Objects.clone(v)), this.normalize);
    }
}
class LogicalOperators {
    eq(left, right, normalize) {
        return new Operator('eq', [left, right], normalize);
    }
    ne(left, right, normalize) {
        return new Operator('ne', [left, right], normalize);
    }
    gt(left, right, normalize) {
        return new Operator('gt', [left, right], normalize);
    }
    ge(left, right, normalize) {
        return new Operator('ge', [left, right], normalize);
    }
    lt(left, right, normalize) {
        return new Operator('lt', [left, right], normalize);
    }
    le(left, right, normalize) {
        return new Operator('le', [left, right], normalize);
    }
    /*
    and(left: any, right: any, normalize?: boolean) {
      return new Operator('and', [left, right], normalize);
    }
    or(left: any, right: any, normalize?: boolean) {
      return new Operator('or', [left, right], normalize);
    }
    */
    not(value, normalize) {
        return new Operator('not', [value], normalize);
    }
    has(left, right, normalize) {
        return new Operator('has', [left, right], normalize);
    }
    in(left, right, normalize) {
        return new Operator('in', [left, right], normalize);
    }
}
class ArithmeticOperators {
    add(left, right, normalize) {
        return new Operator('add', [left, right], normalize);
    }
    sub(left, right, normalize) {
        return new Operator('sub', [left, right], normalize);
    }
    mul(left, right, normalize) {
        return new Operator('mul', [left, right], normalize);
    }
    div(left, right, normalize) {
        return new Operator('div', [left, right], normalize);
    }
    mod(left, right, normalize) {
        return new Operator('mod', [left, right], normalize);
    }
    neg(value, normalize) {
        return new Operator('-', [value], normalize);
    }
}
class Grouping {
    constructor(group) {
        this.group = group;
    }
    get [Symbol.toStringTag]() {
        return 'Grouping';
    }
    toJSON() {
        return {
            $type: Types.rawType(this),
            group: this.group.toJSON(),
        };
    }
    render({ aliases, escape, prefix, }) {
        return `(${render(this.group, { aliases, escape, prefix })})`;
    }
    clone() {
        return new Grouping(Objects.clone(this.group));
    }
}
class Lambda {
    constructor(op, values, alias) {
        this.op = op;
        this.values = values;
        this.alias = alias;
    }
    get [Symbol.toStringTag]() {
        return 'Lambda';
    }
    toJSON() {
        return {
            $type: Types.rawType(this),
            op: this.op,
            values: this.values,
            alias: this.alias,
        };
    }
    render({ aliases, escape, prefix, }) {
        let [left, right] = this.values;
        left = render(left, { aliases, escape, prefix });
        if (right) {
            let alias = this.alias || left.split('/').pop().toLowerCase()[0];
            return `${left}/${this.op}(${alias}:${render(right, {
                aliases,
                escape,
                prefix: alias,
            })})`;
        }
        else {
            return `${left}/${this.op}()`;
        }
    }
    clone() {
        return new Lambda(this.op, this.values.map((v) => Objects.clone(v)), this.alias);
    }
}
class LambdaOperators {
    any(field, value, alias) {
        return new Lambda('any', [field, value], alias);
    }
    all(field, value, alias) {
        return new Lambda('all', [field, value], alias);
    }
}
class ODataOperators {
}
applyMixins(ODataOperators, [
    LogicalOperators,
    ArithmeticOperators,
    LambdaOperators,
]);
const operators = new ODataOperators();
class ODataFunctions {
}
applyMixins(ODataFunctions, [
    StringAndCollectionFunctions,
    CollectionFunctions,
    StringFunctions,
    DateAndTimeFunctions,
    ArithmeticFunctions,
    TypeFunctions,
    GeoFunctions,
    ConditionalFunctions,
]);
const functions = new ODataFunctions();
class ODataSyntax {
}
applyMixins(ODataSyntax, [ODataOperators, ODataFunctions]);
const syntax = new ODataSyntax();

class ComputeExpression extends Expression {
    constructor({ children, names, } = {}) {
        super({ children });
        this.names = names || [];
    }
    static compute(opts, current) {
        return opts({
            t: Field$1.factory(),
            e: () => new ComputeExpression(),
        }, current);
    }
    render({ aliases, escape, prefix, } = {}) {
        let children = this._children.map((n) => n.render({ aliases, escape, prefix }));
        return this.names
            .map((name, index) => `${children[index]} as ${name}`)
            .join(',');
    }
    clone() {
        return new ComputeExpression({
            children: this._children.map((c) => c.clone()),
            names: [...this.names],
        });
    }
    _add(name, node) {
        this.names.push(name);
        this._children.push(node);
        return this;
    }
    field(name, opts) {
        const node = opts({
            o: operators,
            f: functions,
        });
        return this._add(name, node);
    }
}

class FilterExpression extends Expression {
    constructor({ children, connector, negated, } = {}) {
        super({ children });
        this._connector = connector || 'and';
        this._negated = negated || false;
    }
    static filter(opts, current) {
        return opts({
            t: Field$1.factory(),
            e: (connector = 'and') => new FilterExpression({ connector }),
            o: operators,
            f: functions,
        }, current);
    }
    toJSON() {
        return {
            children: this._children.map((c) => c.toJSON()),
            connector: this._connector,
            negated: this._negated,
        };
    }
    connector() {
        return this._connector;
    }
    negated() {
        return this._negated;
    }
    render({ aliases, escape, prefix, } = {}) {
        let content = this._children
            .map((n) => n.render({ aliases, escape, prefix }))
            .join(` ${this._connector} `);
        if (this._negated) {
            content = `not (${content})`;
        }
        return content;
    }
    clone() {
        return new FilterExpression({
            children: this._children.map((c) => c.clone()),
            connector: this._connector,
            negated: this._negated,
        });
    }
    _add(node, connector) {
        if (connector !== undefined && this._connector !== connector) {
            let children = [];
            if (this._children.length > 0) {
                if (this._children.length === 1) {
                    children = [...this._children];
                }
                else {
                    let exp = new FilterExpression({
                        children: this._children,
                        connector: this._connector,
                        negated: this._negated,
                    });
                    if (exp.length() > 1) {
                        children.push(new Grouping(exp));
                    }
                    else {
                        children.push(exp);
                    }
                }
            }
            if (node instanceof FilterExpression &&
                (node.connector() === connector || node.length() === 1)) {
                children = [...children, ...node.children()];
            }
            else {
                children.push(new Grouping(node));
            }
            this._connector = connector;
            this._children = children;
        }
        else if (node instanceof FilterExpression &&
            !node.negated() &&
            (node.connector() === connector || node.length() === 1)) {
            this._children = [...this._children, ...node.children()];
        }
        else {
            this._children.push(node instanceof FilterExpression && !node.negated()
                ? new Grouping(node)
                : node);
        }
        return this;
    }
    or(exp) {
        return this._add(exp, 'or');
    }
    and(exp) {
        return this._add(exp, 'and');
    }
    not(exp) {
        const notExp = new FilterExpression({
            children: exp.children(),
            connector: exp.connector(),
            negated: true,
        });
        return this._add(notExp, this._connector);
    }
    eq(left, right, normalize) {
        return this._add(operators.eq(left, right, normalize));
    }
    ne(left, right, normalize) {
        return this._add(operators.ne(left, right, normalize));
    }
    gt(left, right, normalize) {
        return this._add(operators.gt(left, right, normalize));
    }
    ge(left, right, normalize) {
        return this._add(operators.ge(left, right, normalize));
    }
    lt(left, right, normalize) {
        return this._add(operators.lt(left, right, normalize));
    }
    le(left, right, normalize) {
        return this._add(operators.le(left, right, normalize));
    }
    has(left, right, normalize) {
        return this._add(operators.has(left, right, normalize));
    }
    in(left, right, normalize) {
        return this._add(operators.in(left, right, normalize));
    }
    contains(left, right, normalize) {
        return this._add(functions.contains(left, right, normalize));
    }
    startsWith(left, right, normalize) {
        return this._add(functions.startsWith(left, right, normalize));
    }
    endsWith(left, right, normalize) {
        return this._add(functions.endsWith(left, right, normalize));
    }
    any(left, opts, alias) {
        let exp = undefined;
        if (opts !== undefined) {
            exp = opts({
                t: Field$1.factory(),
                e: (connector = 'and') => new FilterExpression({ connector }),
            });
        }
        return this._add(syntax.any(left, exp, alias));
    }
    all(left, opts, alias) {
        const exp = opts({
            t: Field$1.factory(),
            e: (connector = 'and') => new FilterExpression({ connector }),
        });
        return this._add(syntax.all(left, exp, alias));
    }
    isof(left, type) {
        return this._add(syntax.isof(left, type));
    }
}

class OrderByField {
    constructor(field, order) {
        this.field = field;
        this.order = order;
    }
    get [Symbol.toStringTag]() {
        return 'OrderByField';
    }
    toJSON() {
        return {
            field: this.field.toJSON(),
            order: this.order,
        };
    }
    render({ aliases, escape, prefix, }) {
        return `${render(this.field, { aliases, escape, prefix })} ${this.order}`;
    }
    clone() {
        return new OrderByField(this.field.clone(), this.order);
    }
}
class OrderByExpression extends Expression {
    constructor({ children, } = {}) {
        super({ children });
    }
    static orderBy(opts, current) {
        return opts({
            t: Field$1.factory(),
            e: () => new OrderByExpression(),
        }, current);
    }
    _add(node) {
        this._children.push(node);
        return this;
    }
    render({ aliases, escape, prefix, } = {}) {
        let content = this._children
            .map((n) => n.render({ aliases, escape, prefix }))
            .join(`,`);
        return content;
    }
    clone() {
        return new OrderByExpression({
            children: this._children.map((c) => c.clone()),
        });
    }
    ascending(field) {
        return this._add(new OrderByField(field, 'asc'));
    }
    descending(field) {
        return this._add(new OrderByField(field, 'desc'));
    }
}

class SearchTerm {
    constructor(value) {
        this.value = value;
    }
    get [Symbol.toStringTag]() {
        return 'SearchTerm';
    }
    toJSON() {
        return {
            $type: Types.rawType(this),
            value: this.value,
        };
    }
    render({ aliases, escape, prefix, }) {
        return `${render(this.value, { aliases, escape, prefix })}`;
    }
    clone() {
        return new SearchTerm(this.value);
    }
}
class SearchExpression extends Expression {
    constructor({ children, connector, negated, } = {}) {
        super({ children });
        this._connector = connector || 'AND';
        this._negated = negated || false;
    }
    static search(opts, current) {
        return opts({
            e: (connector = 'AND') => new SearchExpression({ connector }),
        }, current);
    }
    _add(node, connector) {
        if (connector !== undefined && this._connector !== connector) {
            let children = [];
            if (this._children.length > 0) {
                if (this._children.length === 1) {
                    children = [...this._children];
                }
                else {
                    let exp = new SearchExpression({
                        children: this._children,
                        connector: this._connector,
                        negated: this._negated,
                    });
                    if (exp.length() > 1) {
                        children.push(new Grouping(exp));
                    }
                    else {
                        children.push(exp);
                    }
                }
            }
            if (node instanceof SearchExpression &&
                (node.connector() === connector || node.length() === 1)) {
                children = [...children, ...node.children()];
            }
            else {
                children.push(new Grouping(node));
            }
            this._connector = connector;
            this._children = children;
        }
        else if (node instanceof SearchExpression &&
            !node.negated() &&
            (node.connector() === connector || node.length() === 1)) {
            this._children = [...this._children, ...node.children()];
        }
        else {
            this._children.push(node instanceof SearchExpression && !node.negated()
                ? new Grouping(node)
                : node);
        }
        return this;
    }
    render({ aliases, escape, prefix, } = {}) {
        let content = this._children
            .map((n) => n.render({ aliases, escape, prefix }))
            .join(` ${this._connector} `);
        return content;
    }
    clone() {
        return new SearchExpression({
            children: this._children.map((c) => c.clone()),
            connector: this._connector,
            negated: this._negated,
        });
    }
    toJSON() {
        return {
            children: this._children.map((c) => c.toJSON()),
            connector: this._connector,
            negated: this._negated,
        };
    }
    connector() {
        return this._connector;
    }
    negated() {
        return this._negated;
    }
    or(exp) {
        return this._add(exp, 'OR');
    }
    and(exp) {
        return this._add(exp, 'AND');
    }
    not(exp) {
        const notExp = new SearchExpression({
            children: exp.children(),
            connector: exp.connector(),
            negated: true,
        });
        return this._add(notExp, this._connector);
    }
    term(value) {
        return this._add(new SearchTerm(value));
    }
}

class SelectExpression extends Expression {
    constructor({ children, } = {}) {
        super({ children });
    }
    static select(opts, current) {
        return opts({
            t: Field$1.factory(),
            e: () => new SelectExpression(),
        }, current);
    }
    render({ aliases, escape, prefix, } = {}) {
        return this._children
            .map((n) => n.render({ aliases, escape, prefix }))
            .join(',');
    }
    clone() {
        return new SelectExpression({
            children: this._children.map((c) => c.clone()),
        });
    }
    _add(node) {
        this._children.push(node);
        return this;
    }
    field(field) {
        return this._add(field);
    }
}

class ExpandField {
    constructor(field, values = {}) {
        this.field = field;
        this.values = values;
    }
    get [Symbol.toStringTag]() {
        return 'ExpandField';
    }
    toJSON() {
        return {
            field: this.field.toJSON(),
        };
    }
    render({ aliases, escape, prefix, }) {
        const params = [
            QueryOptionNames.select,
            QueryOptionNames.expand,
            QueryOptionNames.filter,
            QueryOptionNames.search,
            QueryOptionNames.orderBy,
            QueryOptionNames.skip,
            QueryOptionNames.top,
            QueryOptionNames.levels,
        ]
            .filter((key) => !Types.isEmpty(this.values[key]))
            .reduce((acc, key) => {
            let value = this.values[key];
            if (Types.rawType(value).endsWith('Expression')) {
                value = value.render({ aliases, prefix, escape });
            }
            return Object.assign(acc, { [key]: value });
        }, {});
        let expand = `${render(this.field, { aliases, escape, prefix })}`;
        if (!Types.isEmpty(params)) {
            expand = `${expand}(${Object.keys(params)
                .map((key) => `$${key}=${params[key]}`)
                .join(';')})`;
        }
        return expand;
    }
    clone() {
        const values = Object.keys(this.values).reduce((acc, key) => Object.assign(acc, { [key]: Objects.clone(this.values[key]) }), {});
        return new ExpandField(this.field.clone(), values);
    }
    select(opts) {
        return this.option(QueryOptionNames.select, SelectExpression.select(opts, this.values[QueryOptionNames.select]));
    }
    expand(opts) {
        return this.option(QueryOptionNames.expand, ExpandExpression.expand(opts, this.values[QueryOptionNames.expand]));
    }
    filter(opts) {
        return this.option(QueryOptionNames.filter, FilterExpression.filter(opts, this.values[QueryOptionNames.filter]));
    }
    search(opts) {
        return this.option(QueryOptionNames.search, SearchExpression.search(opts, this.values[QueryOptionNames.search]));
    }
    orderBy(opts) {
        return this.option(QueryOptionNames.orderBy, OrderByExpression.orderBy(opts, this.values[QueryOptionNames.orderBy]));
    }
    skip(n) {
        return this.option(QueryOptionNames.skip, n);
    }
    top(n) {
        return this.option(QueryOptionNames.top, n);
    }
    levels(n) {
        return this.option(QueryOptionNames.levels, n);
    }
    // Option Handler
    option(name, opts) {
        if (opts !== undefined)
            this.values[name] = opts;
        return this.values[name];
    }
}
class ExpandExpression extends Expression {
    constructor({ children, } = {}) {
        super({ children });
    }
    static expand(opts, current) {
        return opts({
            t: Field$1.factory(),
            e: () => new ExpandExpression(),
        }, current);
    }
    render({ aliases, escape, prefix, } = {}) {
        return this._children
            .map((n) => n.render({ aliases, escape, prefix }))
            .join(',');
    }
    clone() {
        return new ExpandExpression({
            children: this._children.map((c) => c.clone()),
        });
    }
    _add(node) {
        this._children.push(node);
        return this;
    }
    field(field, opts) {
        let node = new ExpandField(field);
        if (opts !== undefined)
            opts(node);
        return this._add(node);
    }
}

class ODataQueryOptionHandler {
    constructor(o, n) {
        this.o = o;
        this.n = n;
    }
    /**
     * The name of the managed odata query option.
     */
    get name() {
        return this.n;
    }
    /**
     * Converts the managed odata query option to a json object.
     * @returns {any}
     */
    toJSON() {
        return this.o.get(this.n);
    }
    /**
     * Returns a boolean indicating if the managed odata query option is empty.
     * @returns True if the managed odata query option is empty.
     */
    empty() {
        return Types.isEmpty(this.o.get(this.n));
    }
    //#region Primitive Value
    /**
     * Get or Set the value of the managed odata query option.
     * @param v The value to set.
     * @returns
     */
    value(v) {
        if (v !== undefined)
            this.o.set(this.n, v);
        return this.o.get(this.n);
    }
    //#endregion
    //#region Array Value
    assertArray() {
        if (!Types.isArray(this.o.get(this.n)))
            this.o.set(this.n, this.o.has(this.n) ? [this.o.get(this.n)] : []);
        return this.o.get(this.n);
    }
    /**
     * Push value to the managed odata query option.
     * @param value Value to push
     */
    push(value) {
        this.assertArray().push(value);
    }
    /**
     * Remove value from the managed odata query option.
     * @param value Value to remove
     */
    remove(value) {
        this.o.set(this.n, this.assertArray().filter((v) => v !== value));
        // If only one and not is array... down to value
        if (this.o.get(this.n).length === 1 &&
            !Types.isArray(this.o.get(this.n)[0]))
            this.o.set(this.n, this.o.get(this.n)[0]);
    }
    /**
     * Return value at index of the managed odata query option.
     * @param index Index of the value
     * @returns The value
     */
    at(index) {
        return this.assertArray()[index];
    }
    some(predicate) {
        return this.assertArray().some(predicate);
    }
    every(predicate) {
        return this.assertArray().every(predicate);
    }
    find(predicate) {
        return this.assertArray().find(predicate);
    }
    //#endregion
    //#region HashMap Value
    assertObject(create) {
        if (!Types.isArray(this.o.get(this.n)) &&
            Types.isPlainObject(this.o.get(this.n))) {
            return this.o.get(this.n);
        }
        let arr = this.assertArray();
        let obj = arr.find((v) => Types.isPlainObject(v));
        if (!obj && create) {
            obj = {};
            arr.push(obj);
        }
        return obj;
    }
    /**
     * Set the value for path in the managed odata query option.
     * @param path Path for set the value
     * @param value Value to set
     */
    set(path, value) {
        let obj = this.assertObject(true);
        Objects.set(obj, path, value);
    }
    /**
     * Get the value for path from the managed odata query option.
     * @param path The path from get the value
     * @param def Default if not found
     * @returns
     */
    get(path, def) {
        let obj = this.assertObject(false) || {};
        return Objects.get(obj, path, def);
    }
    /**
     * Unset the value for path in the managed odata query option.
     * @param path
     */
    unset(path) {
        let obj = this.assertObject(true);
        Objects.unset(obj, path);
        if (Types.isArray(this.o.get(this.n))) {
            this.o.set(this.n, this.o.get(this.n).filter((v) => !Types.isEmpty(v)));
            if (this.o.get(this.n).length === 1)
                this.o.set(this.n, this.o.get(this.n)[0]);
        }
    }
    /**
     * Test if the managed odata query option has the value.
     * @param path The path fot test if the value is set
     * @returns Boolean indicating if the value is set
     */
    has(path) {
        let obj = this.assertObject(false) || {};
        return Objects.has(obj, path);
    }
    /**
     * Merge values from object into the managed odata query option.
     * @param values Object to merge
     * @returns
     */
    assign(values) {
        let obj = this.assertObject(true);
        return Objects.merge(obj, values);
    }
    //#endregion
    /**
     * Clear the managed odata query option.
     */
    clear() {
        this.o.delete(this.n);
    }
}
class ODataQueryOptionsHandler {
    constructor(options) {
        this.options = options;
    }
    /**
     * Create a raw odata value
     * @param value The value to raw
     * @returns The raw value
     */
    raw(value) {
        return raw(value);
    }
    /**
     * Create a new odata alias parameter
     * @link https://docs.oasis-open.org/odata/odata/v4.01/odata-v4.01-part2-url-conventions.html#sec_ParameterAliases
     * @param value The value of the alias
     * @param name The name of the alias
     * @returns The alias
     */
    alias(value, name) {
        return alias(value, name);
    }
    /**
     * Create a duration odata value
     * @param value The value to duration
     * @returns The duration value
     */
    duration(value) {
        return duration(value);
    }
    /**
     * Create a binary odata value
     * @param value The value to binary
     * @returns The binary value
     */
    binary(value) {
        return binary(value);
    }
    /**
     * Normalize the given value to a valid odata value
     * @param value The value to normalize
     * @returns The normalized value
     */
    normalize(value) {
        return normalizeValue(value);
    }
    select(opts) {
        if (Types.isFunction(opts)) {
            return this.options.expression(QueryOptionNames.select, SelectExpression.select(opts, this.options.expression(QueryOptionNames.select)));
        }
        return this.options.option(QueryOptionNames.select, opts);
    }
    expand(opts) {
        if (Types.isFunction(opts)) {
            return this.options.expression(QueryOptionNames.expand, ExpandExpression.expand(opts, this.options.expression(QueryOptionNames.expand)));
        }
        return this.options.option(QueryOptionNames.expand, opts);
    }
    compute(opts) {
        if (Types.isFunction(opts)) {
            return this.options.expression(QueryOptionNames.compute, ComputeExpression.compute(opts, this.options.expression(QueryOptionNames.compute)));
        }
        return this.options.option(QueryOptionNames.compute, opts);
    }
    format(opts) {
        return this.options.option(QueryOptionNames.format, opts);
    }
    transform(opts) {
        return this.options.option(QueryOptionNames.transform, opts);
    }
    search(opts) {
        if (Types.isFunction(opts)) {
            return this.options.expression(QueryOptionNames.search, SearchExpression.search(opts, this.options.expression(QueryOptionNames.search)));
        }
        return this.options.option(QueryOptionNames.search, opts);
    }
    filter(opts) {
        if (Types.isFunction(opts)) {
            return this.options.expression(QueryOptionNames.filter, FilterExpression.filter(opts, this.options.expression(QueryOptionNames.filter)));
        }
        return this.options.option(QueryOptionNames.filter, opts);
    }
    orderBy(opts) {
        if (Types.isFunction(opts)) {
            return this.options.option(QueryOptionNames.orderBy, OrderByExpression.orderBy(opts, this.options.expression(QueryOptionNames.orderBy)));
        }
        return this.options.option(QueryOptionNames.orderBy, opts);
    }
    top(opts) {
        return this.options.option(QueryOptionNames.top, opts);
    }
    skip(opts) {
        return this.options.option(QueryOptionNames.skip, opts);
    }
    skiptoken(opts) {
        return this.options.option(QueryOptionNames.skiptoken, opts);
    }
    /**
     * Shortcut for set $top, $skip, $skiptoken.
     * @param param0 skip or top or skiptoken
     */
    paging({ skip, skiptoken, top, } = {}) {
        if (skiptoken !== undefined) {
            if (skiptoken !== null) {
                this.skiptoken(skiptoken);
            }
            else {
                this.options.remove(QueryOptionNames.skiptoken);
            }
        }
        if (skip !== undefined) {
            if (skip !== null) {
                this.skip(skip);
            }
            else {
                this.options.remove(QueryOptionNames.skip);
            }
        }
        if (top !== undefined) {
            if (top !== null) {
                this.top(top);
            }
            else {
                this.options.remove(QueryOptionNames.top);
            }
        }
    }
    /**
     * Shortcut for clear pagination by unset $top, $skip, $skiptoken.
     */
    clearPaging() {
        this.options.remove(QueryOptionNames.skip);
        this.options.remove(QueryOptionNames.top);
        this.options.remove(QueryOptionNames.skiptoken);
    }
    /**
     * Shortcut for clear query.
     */
    clear() {
        this.options.clear();
    }
    /**
     * Retrun the query.
     */
    query() {
        return this.options.toQueryArguments();
    }
    /**
     * Apply the given query options to the current query.
     * @param query The query to be applied.
     */
    apply(query) {
        if (query.select !== undefined) {
            if (query.select instanceof SelectExpression) {
                this.options.expression(QueryOptionNames.select, query.select);
            }
            else if (query.select !== null) {
                this.options.option(QueryOptionNames.select, query.select);
            }
            else {
                this.options.remove(QueryOptionNames.select);
            }
        }
        if (query.expand !== undefined) {
            if (query.expand instanceof ExpandExpression) {
                this.options.expression(QueryOptionNames.expand, query.expand);
            }
            else if (query.expand !== null) {
                this.options.option(QueryOptionNames.expand, query.expand);
            }
            else {
                this.options.remove(QueryOptionNames.expand);
            }
        }
        if (query.compute !== undefined) {
            if (query.compute instanceof ComputeExpression) {
                this.options.expression(QueryOptionNames.compute, query.compute);
            }
            else if (query.compute !== null) {
                this.options.option(QueryOptionNames.compute, query.compute);
            }
            else {
                this.options.remove(QueryOptionNames.compute);
            }
        }
        if (query.transform !== undefined) {
            if (query.transform !== null) {
                this.options.option(QueryOptionNames.transform, query.transform);
            }
            else {
                this.options.remove(QueryOptionNames.transform);
            }
        }
        if (query.search !== undefined) {
            if (query.search instanceof SearchExpression) {
                this.options.expression(QueryOptionNames.search, query.search);
            }
            else if (query.search !== null) {
                this.options.option(QueryOptionNames.search, query.search);
            }
            else {
                this.options.remove(QueryOptionNames.search);
            }
        }
        if (query.filter !== undefined) {
            if (query.filter instanceof FilterExpression) {
                this.options.expression(QueryOptionNames.filter, query.filter);
            }
            else if (query.filter !== null) {
                this.options.option(QueryOptionNames.filter, query.filter);
            }
            else {
                this.options.remove(QueryOptionNames.filter);
            }
        }
        if (query.orderBy !== undefined) {
            if (query.orderBy instanceof OrderByExpression) {
                this.options.expression(QueryOptionNames.orderBy, query.orderBy);
            }
            else if (query.orderBy !== null) {
                this.options.option(QueryOptionNames.orderBy, query.orderBy);
            }
            else {
                this.options.remove(QueryOptionNames.orderBy);
            }
        }
        this.paging(query);
    }
}

class ODataQueryOptions {
    constructor(values) {
        this.values =
            values instanceof Map
                ? values
                : new Map(Object.entries(values || {}));
    }
    // Params
    pathAndParams(escape = false) {
        let aliases = [];
        let options = [
            QueryOptionNames.select,
            QueryOptionNames.filter,
            QueryOptionNames.search,
            QueryOptionNames.compute,
            QueryOptionNames.transform,
            QueryOptionNames.orderBy,
            QueryOptionNames.top,
            QueryOptionNames.skip,
            QueryOptionNames.skiptoken,
            QueryOptionNames.expand,
            QueryOptionNames.format,
            QueryOptionNames.levels,
            QueryOptionNames.count,
        ]
            .filter((key) => !Types.isEmpty(this.values.get(key)))
            .reduce((acc, key) => {
            let value = this.values.get(key);
            if (Types.rawType(value).endsWith('Expression') ||
                (Types.isArray(value) &&
                    value.some((v) => Types.rawType(v).endsWith('Expression')))) {
                value = Types.isArray(value)
                    ? value.map((v) => Types.rawType(v).endsWith('Expression')
                        ? v.render({ aliases })
                        : v)
                    : value.render({ aliases });
            }
            return Object.assign(acc, { [key]: value });
        }, {});
        return buildPathAndQuery({ ...options, aliases, escape });
    }
    toString() {
        const [path, params] = this.pathAndParams();
        return (path +
            Object.entries(params)
                .filter(([, value]) => !Types.isEmpty(value))
                .map(([key, value]) => `${key}=${value}`)
                .join('&'));
    }
    toJSON() {
        return [...this.values.keys()].reduce((acc, key) => {
            let value = this.values.get(key);
            value = 'toJSON' in value ? value.toJSON() : value;
            return Object.assign(acc, { [key]: value });
        }, {});
    }
    toQueryArguments() {
        return {
            select: this.values.get(QueryOptionNames.select) || null,
            expand: this.values.get(QueryOptionNames.expand) || null,
            transform: this.values.get(QueryOptionNames.transform) || null,
            compute: this.values.get(QueryOptionNames.compute) || null,
            search: this.values.get(QueryOptionNames.search) || null,
            filter: this.values.get(QueryOptionNames.filter) || null,
            orderBy: this.values.get(QueryOptionNames.orderBy) || null,
            top: this.values.get(QueryOptionNames.top) || null,
            skip: this.values.get(QueryOptionNames.skip) || null,
            skiptoken: this.values.get(QueryOptionNames.skiptoken) || null,
            levels: this.values.get(QueryOptionNames.levels) || null,
            count: this.values.get(QueryOptionNames.count) || null,
        };
    }
    clone() {
        return new ODataQueryOptions(Objects.clone(this.values));
    }
    // Set Renderable
    expression(key, exp) {
        if (exp !== undefined)
            this.values.set(key, exp);
        return this.values.get(key);
    }
    // Option Handler
    option(key, opts) {
        if (opts !== undefined)
            this.values.set(key, opts);
        return new ODataQueryOptionHandler(this.values, key);
    }
    // Query Options tools
    has(key) {
        return this.values.has(key);
    }
    remove(...keys) {
        [...this.values.keys()]
            .filter((k) => keys.indexOf(k) !== -1)
            .forEach((key) => {
            this.values.delete(key);
        });
    }
    keep(...keys) {
        [...this.values.keys()]
            .filter((k) => keys.indexOf(k) === -1)
            .forEach((key) => {
            this.values.delete(key);
        });
    }
    // Clear
    clear() {
        this.values.clear();
    }
}

class SegmentHandler {
    constructor(segment) {
        this.segment = segment;
    }
    get name() {
        return this.segment.name;
    }
    type(value) {
        if (value !== undefined)
            this.segment.type = value;
        return this.segment.type;
    }
    path(value) {
        if (value !== undefined)
            this.segment.path = value;
        return this.segment.path;
    }
    key(value) {
        if (value !== undefined)
            this.segment.key = value;
        return this.segment.key;
    }
    hasKey() {
        return !Types.isEmpty(this.segment.key);
    }
    clearKey() {
        delete this.segment.key;
    }
    parameters(value) {
        if (value !== undefined)
            this.segment.parameters = value;
        return this.segment.parameters;
    }
    hasParameters() {
        return !Types.isEmpty(this.segment.parameters);
    }
    clearParameters() {
        delete this.segment.parameters;
    }
}
class ODataPathSegmentsHandler {
    constructor(segments) {
        this.segments = segments;
    }
    entitySet() {
        return this.segments.get(PathSegmentNames.entitySet);
    }
    singleton() {
        return this.segments.get(PathSegmentNames.singleton);
    }
    action() {
        return this.segments.get(PathSegmentNames.action);
    }
    function() {
        return this.segments.get(PathSegmentNames.function);
    }
    keys(values) {
        return this.segments.keys(values);
    }
    property() {
        return this.segments.get(PathSegmentNames.property);
    }
    navigationProperty() {
        return this.segments.get(PathSegmentNames.navigationProperty);
    }
}

function pathSegmentsBuilder(segment, escape = false) {
    if (segment.name === PathSegmentNames.function) {
        let [path, params] = segment.parameters
            ? buildPathAndQuery({
                func: { [segment.path]: segment.parameters },
                escape,
            })
            : buildPathAndQuery({ func: segment.path, escape });
        if (path.startsWith(PATH_SEPARATOR)) {
            path = path.slice(1);
        }
        return [path, params];
    }
    else {
        let key = segment.key;
        // HACK: Check guid string
        if (typeof key === 'string' &&
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(key)) {
            key = raw(key);
        }
        let [path, params] = key ? buildPathAndQuery({ key, escape }) : ['', {}];
        return [segment.path + path, params];
    }
}
class ODataPathSegments {
    constructor(segments) {
        this._segments = segments || [];
    }
    pathAndParams(escape = false) {
        const result = this._segments.reduce((acc, segment) => {
            const [path, params] = pathSegmentsBuilder(segment, escape);
            acc.paths.push(path);
            acc.params = Object.assign(acc.params, params);
            return acc;
        }, { paths: [], params: {} });
        return [result.paths.join(PATH_SEPARATOR), result.params];
    }
    types({ key = false } = {}) {
        return this.segments({ key })
            .map((s) => s.type())
            .filter((t) => t !== undefined);
    }
    keys(values) {
        const segments = this.segments({ key: true });
        if (values !== undefined) {
            segments.forEach((segment, index) => {
                const key = values[index];
                if (key === undefined) {
                    segment.clearKey();
                }
                else {
                    segment.key(key);
                }
            });
        }
        return segments.map((s) => s.key());
    }
    toString() {
        const [path, params] = this.pathAndParams();
        return (path +
            Object.entries(params)
                .filter(([, value]) => value)
                .map(([key, value]) => `${key}=${value}`)
                .join('&'));
    }
    toJSON() {
        return this._segments.map((s) => {
            let json = {
                name: s.name,
                path: s.path,
            };
            if (s.type !== undefined)
                json.type = s.type;
            if (s.key !== undefined)
                json.key = s.key;
            if (s.parameters !== undefined)
                json.parameters = s.parameters;
            return json;
        });
    }
    clone() {
        const segments = Objects.clone(this._segments);
        return new ODataPathSegments(segments);
    }
    find(predicate) {
        //Backward Find
        return [...this._segments].reverse().find(predicate);
    }
    segments({ key = false } = {}) {
        let segments = [...this._segments];
        if (key)
            segments = segments.filter((s) => [
                PathSegmentNames.entitySet,
                PathSegmentNames.navigationProperty,
                PathSegmentNames.property,
            ].indexOf(s.name) !== -1);
        return segments.map((s) => new SegmentHandler(s));
    }
    first({ key = false } = {}) {
        const segments = this.segments({ key });
        return segments.length > 0 ? segments[0] : undefined;
    }
    last({ key = false } = {}) {
        const segments = this.segments({ key });
        return segments.length > 0 ? segments[segments.length - 1] : undefined;
    }
    add(name, path) {
        const segment = { name, path };
        this._segments.push(segment);
        return new SegmentHandler(segment);
    }
    get(name) {
        let segment = this.find((s) => s.name === name);
        if (segment === undefined)
            throw Error(`No Segment for name ${name} was found`);
        return new SegmentHandler(segment);
    }
}

class ODataRequest {
    constructor(init) {
        this._method = init.method;
        this.resource = init.resource;
        this.api = init.api;
        this.reportProgress = init.reportProgress;
        this.observe = init.observe;
        this.context = init.context;
        // Response Type
        this._responseType = init.responseType;
        // The Body
        this._body = init.body !== undefined ? init.body : null;
        if (this._body !== null)
            this._body = this.resource.serialize(this._body);
        this.withCredentials =
            init.withCredentials === undefined
                ? this.api.options.withCredentials
                : init.withCredentials;
        this.fetchPolicy = init.fetchPolicy || this.api.options.fetchPolicy;
        this.bodyQueryOptions = [
            ...(this.api.options.bodyQueryOptions || []),
            ...(init.bodyQueryOptions || []),
        ];
        // The Path and Params from resource
        const [resourcePath, resourceParams] = this.resource.pathAndParams();
        this._path = resourcePath;
        //#region Headers
        const customHeaders = {};
        if (typeof init.etag === 'string') {
            if (this.api.options.etag.ifMatch &&
                ['PUT', 'PATCH', 'DELETE'].indexOf(this._method) !== -1)
                customHeaders[IF_MATCH_HEADER] = init.etag;
            else if (this.api.options.etag.ifNoneMatch &&
                ['GET'].indexOf(this._method) !== -1)
                customHeaders[IF_NONE_MATCH_HEADER] = init.etag;
        }
        const accept = [];
        // Metadata
        if (this.api.options.accept?.metadata !== undefined)
            accept.push(`odata.metadata=${this.api.options.accept?.metadata}`);
        // IEEE754
        if (this.api.options.accept?.ieee754Compatible !== undefined)
            accept.push(`IEEE754Compatible=${this.api.options.accept?.ieee754Compatible}`);
        // streaming
        if (this.api.options.accept?.streaming !== undefined)
            accept.push(`streaming=${this.api.options.accept?.streaming}`);
        // ExponentialDecimals
        if (this.api.options.accept?.exponentialDecimals !== undefined)
            accept.push(`ExponentialDecimals=${this.api.options.accept?.exponentialDecimals}`);
        if (accept.length > 0)
            customHeaders[ACCEPT] = [
                `application/json;${accept.join(';')}`,
                'text/plain',
                '*/*',
            ];
        const prefer = [];
        // Return
        if (this.api.options.prefer?.return !== undefined &&
            ['POST', 'PUT', 'PATCH'].indexOf(this._method) !== -1)
            prefer.push(`return=${this.api.options.prefer?.return}`);
        // MaxPageSize
        if (this.api.options.prefer?.maxPageSize !== undefined &&
            ['GET'].indexOf(this._method) !== -1)
            prefer.push(`odata.maxpagesize=${this.api.options.prefer?.maxPageSize}`);
        // Annotations
        if (this.api.options.prefer?.includeAnnotations !== undefined &&
            ['GET'].indexOf(this._method) !== -1)
            prefer.push(`odata.include-annotations=${this.api.options.prefer?.includeAnnotations}`);
        if (this.api.options.prefer?.continueOnError === true &&
            ['POST'].indexOf(this._method) !== -1)
            prefer.push(`odata.continue-on-error`);
        if (prefer.length > 0)
            customHeaders[PREFER] = prefer;
        this._headers = Http.mergeHttpHeaders(this.api.options.headers, customHeaders, init.headers || {});
        //#endregion
        //#region Params
        const customParams = {};
        if (['POST', 'PUT', 'PATCH'].indexOf(this._method) !== -1 &&
            '$select' in resourceParams) {
            customParams['$select'] = resourceParams['$select'];
        }
        if (['POST'].indexOf(this._method) !== -1 && '$expand' in resourceParams) {
            customParams['$expand'] = resourceParams['$expand'];
        }
        if (['GET'].indexOf(this._method) !== -1) {
            Object.assign(customParams, resourceParams);
        }
        const params = Http.mergeHttpParams(this.api.options.params, customParams, init.params || {});
        this._params =
            this._responseType === 'entity'
                ? Http.withoutHttpParams(params, [
                    '$filter',
                    '$orderby',
                    '$count',
                    '$skip',
                    '$top',
                ])
                : params;
        //#endregion
    }
    static factory(api, method, resource, options) {
        const apiOptions = api.options;
        let params = options.params || {};
        if (options.withCount) {
            params = Http.mergeHttpParams(params, apiOptions.helper.countParam());
        }
        let etag = options.etag;
        if (etag === undefined && Types.isPlainObject(options.body)) {
            etag = apiOptions.helper.etag(options.body);
        }
        return new ODataRequest({
            method,
            etag,
            api,
            resource,
            params,
            context: options.context,
            body: options.body,
            observe: options.observe,
            headers: options.headers,
            reportProgress: options.reportProgress,
            responseType: options.responseType,
            fetchPolicy: options.fetchPolicy,
            withCredentials: options.withCredentials,
            bodyQueryOptions: options.bodyQueryOptions,
        });
    }
    get responseType() {
        return this._responseType &&
            ['property', 'entity', 'entities'].indexOf(this._responseType) !== -1
            ? 'json'
            : this._responseType === 'value'
                ? 'text'
                : this._responseType;
    }
    get path() {
        return this.isQueryBody() ? `${this._path}/${$QUERY}` : this._path;
    }
    get method() {
        return this.isQueryBody() ? 'POST' : this._method;
    }
    get body() {
        return this.isQueryBody()
            ? Http.splitHttpParams(this._params, this.bodyQueryOptions.map((name) => `$${name}`))[1].toString()
            : this._body;
    }
    get params() {
        return this.isQueryBody()
            ? Http.splitHttpParams(this._params, this.bodyQueryOptions.map((name) => `$${name}`))[0]
            : this._params;
    }
    get headers() {
        return this.isQueryBody()
            ? Http.mergeHttpHeaders(this._headers, { CONTENT_TYPE: TEXT_PLAIN })
            : this._headers;
    }
    get pathWithParams() {
        return this.params.keys().length > 0
            ? `${this.path}?${this.params}`
            : this.path;
    }
    get url() {
        return `${this.api.serviceRootUrl}${this.path}`;
    }
    get urlWithParams() {
        return `${this.api.serviceRootUrl}${this.pathWithParams}`;
    }
    get cacheKey() {
        return this._params.keys().length > 0
            ? `${this._path}?${this._params}`
            : this._path;
    }
    isQueryBody() {
        return (this._method === 'GET' &&
            this.bodyQueryOptions.length > 0 &&
            this.bodyQueryOptions.some((name) => this._params.has(`$${name}`)));
    }
    isBatch() {
        return this.path.endsWith($BATCH);
    }
    isFetch() {
        return ['GET'].indexOf(this._method) !== -1;
    }
    isMutate() {
        return ['PUT', 'PATCH', 'POST', 'DELETE'].indexOf(this._method) !== -1;
    }
}

const COLLECTION = /Collection\(([\w\.]+)\)/;
const PROPERTY = /([\w\d\-_]+)\(([\'\w\d\-_=]+)\)/;
const EXPAND = /([\w\d\-_]+)\(([\w\d\,\(\)]+)\)/;
const ODataVersionBaseHelper = {
    entity(data) {
        return data;
    },
    entities(data) {
        return data[this.VALUE];
    },
    property(data) {
        return this.VALUE in data ? data[this.VALUE] : data;
    },
    functions(annots) {
        const funcs = new Map();
        [...annots.keys()]
            .filter((key) => key.startsWith(this.ODATA_FUNCTION_PREFIX))
            .forEach((key) => funcs.set(key.substring(this.ODATA_FUNCTION_PREFIX.length), annots.get(key)));
        return funcs;
    },
    properties(annots) {
        const props = new Map();
        [...annots.keys()]
            .filter((key) => key.indexOf(this.ODATA_ANNOTATION_PREFIX) > 0)
            .forEach((key) => {
            let name = key.substring(0, key.indexOf(this.ODATA_ANNOTATION_PREFIX));
            let prop = props.has(name) ? props.get(name) : new Map();
            prop.set(key.substring(key.indexOf(this.ODATA_ANNOTATION_PREFIX)), annots.get(key));
            props.set(name, prop);
        });
        return props;
    },
    id(annots) {
        return annots instanceof Map
            ? annots.get(this.ODATA_ID)
            : annots[this.ODATA_ID];
    },
    etag(annots) {
        return annots instanceof Map
            ? annots.get(this.ODATA_ETAG)
            : annots[this.ODATA_ETAG];
    },
    type(annots) {
        let type = annots instanceof Map
            ? annots.get(this.ODATA_TYPE)
            : annots[this.ODATA_TYPE];
        if (!type)
            return undefined;
        type = type.substring(1);
        const matches = COLLECTION.exec(type);
        if (matches)
            return matches[1].indexOf('.') === -1 ? `Edm.${matches[1]}` : matches[1];
        return type;
    },
    mediaEtag(annots) {
        return annots.has(this.ODATA_MEDIA_ETAG)
            ? decodeURIComponent(annots.get(this.ODATA_MEDIA_ETAG))
            : undefined;
    },
    metadataEtag(annots) {
        return annots.has(this.ODATA_METADATA_ETAG)
            ? decodeURIComponent(annots.get(this.ODATA_METADATA_ETAG))
            : undefined;
    },
    count(annots) {
        return annots.has(this.ODATA_COUNT)
            ? Number(annots.get(this.ODATA_COUNT))
            : undefined;
    },
    annotations(value) {
        const annots = new Map();
        Object.entries(value)
            .filter(([key]) => key.indexOf(this.ODATA_ANNOTATION_PREFIX) !== -1 ||
            key.startsWith(this.ODATA_FUNCTION_PREFIX))
            .forEach(([key, value]) => annots.set(key, value));
        return annots;
    },
    attributes(value, metadata) {
        return Object.entries(value)
            .filter(([k]) => metadata === 'none' ||
            (metadata === 'minimal' &&
                (k.indexOf(this.ODATA_ANNOTATION_PREFIX) === -1 ||
                    k.startsWith(this.ODATA_ANNOTATION_PREFIX)) &&
                !k.startsWith(this.ODATA_FUNCTION_PREFIX)) ||
            (metadata === 'full' &&
                k.indexOf(this.ODATA_ANNOTATION_PREFIX) === -1 &&
                !k.startsWith(this.ODATA_FUNCTION_PREFIX)))
            .reduce((acc, e) => ({ ...acc, [e[0]]: e[1] }), {});
    },
    nextLink(annots) {
        return annots.has(this.ODATA_NEXTLINK)
            ? decodeURIComponent(annots.get(this.ODATA_NEXTLINK))
            : undefined;
    },
    readLink(annots) {
        return annots.has(this.ODATA_READLINK)
            ? decodeURIComponent(annots.get(this.ODATA_READLINK))
            : undefined;
    },
    mediaReadLink(annots) {
        return annots.has(this.ODATA_MEDIA_READLINK)
            ? decodeURIComponent(annots.get(this.ODATA_MEDIA_READLINK))
            : undefined;
    },
    editLink(annots) {
        return annots.has(this.ODATA_EDITLINK)
            ? decodeURIComponent(annots.get(this.ODATA_EDITLINK))
            : undefined;
    },
    mediaEditLink(annots) {
        return annots.has(this.ODATA_MEDIA_EDITLINK)
            ? decodeURIComponent(annots.get(this.ODATA_MEDIA_EDITLINK))
            : undefined;
    },
    deltaLink(annots) {
        return annots.has(this.ODATA_DELTALINK)
            ? decodeURIComponent(annots.get(this.ODATA_DELTALINK))
            : undefined;
    },
    mediaContentType(annots) {
        return annots.has(this.ODATA_MEDIA_CONTENTTYPE)
            ? decodeURIComponent(annots.get(this.ODATA_MEDIA_CONTENTTYPE))
            : undefined;
    },
};
const ODataHelper = {
    //#region Version 4.0
    [VERSION_4_0]: {
        ...ODataVersionBaseHelper,
        VALUE: 'value',
        ODATA_ANNOTATION_PREFIX: '@odata',
        ODATA_FUNCTION_PREFIX: '#',
        //odata.id: the ID of the entity
        ODATA_ID: '@odata.id',
        //odata.count: the total count of a collection of entities or collection of entity references, if requested.
        ODATA_COUNT: '@odata.count',
        //odata.context: the context URL for a collection, entity, primitive value, or service document.
        ODATA_CONTEXT: '@odata.context',
        //odata.etag: the ETag of the entity
        ODATA_ETAG: '@odata.etag',
        ODATA_METADATA_ETAG: '@odata.metadataEtag',
        //odata.type: the type of the containing {[key: string]: any} or targeted property if the type of the {[key: string]: any} or targeted property cannot be heuristically determined
        ODATA_TYPE: '@odata.type',
        //odata.nextLink: the next link of a collection with partial results
        ODATA_NEXTLINK: '@odata.nextLink',
        //odata.deltaLink: the delta link for obtaining changes to the result, if requested
        ODATA_DELTALINK: '@odata.deltaLink',
        //odata.readLink: the link used to read the entity, if the edit link cannot be used to read the entity
        ODATA_READLINK: '@odata.readLink',
        //odata.editLink: the link used to edit/update the entity, if the entity is updatable and the odata.id does not represent a URL that can be used to edit the entity
        ODATA_EDITLINK: '@odata.editLink',
        //odata.associationLink: the link used to describe the relationship between this entity and related entities
        ODATA_ASSOCIATIONLINK: '@odata.associationLink',
        //odata.navigationLink: the link used to retrieve the values of a navigation property
        ODATA_NAVIGATIONLINK: '@odata.navigationLink',
        //Media entities and stream properties may in addition contain the following annotations:
        //odata.mediaEtag: the ETag of the stream, as appropriate
        ODATA_MEDIA_ETAG: '@odata.mediaEtag',
        //odata.mediaContentType: the content type of the stream
        ODATA_MEDIA_CONTENTTYPE: '@odata.mediaContentType',
        //odata.mediaReadLink: the link used to read the stream
        ODATA_MEDIA_READLINK: '@odata.mediaReadLink',
        //odata.mediaEditLink: the link used to edit/update the stream
        ODATA_MEDIA_EDITLINK: '@odata.mediaEditLink',
        //http://nb-mdp-dev01:57970/$metadata#recursos/$entity
        //http://nb-mdp-dev01:57970/$metadata#categorias
        //http://nb-mdp-dev01:57970/$metadata#juzgados
        //http://nb-mdp-dev01:57970/$metadata#Collection(SIU.Recursos.RecursoEntry)
        //http://nb-mdp-dev01:57970/$metadata#categorias/$entity
        //http://nb-mdp-dev01:57970/$metadata#categorias(children(children(children(children(children(children(children(children(children(children()))))))))))/$entity
        //http://nb-mdp-dev01:57970/$metadata#recursos/SIU.Documentos.Documento/$entity
        //http://nb-mdp-dev01:57970/$metadata#SIU.Api.Infrastructure.Storage.Backend.SiuUrls
        context(annots) {
            let ctx = {};
            const str = annots instanceof Map
                ? annots.get(this.ODATA_CONTEXT)
                : annots[this.ODATA_CONTEXT];
            if (typeof str === 'string') {
                let index = str.indexOf('$metadata');
                ctx.serviceRootUrl = str.substring(0, index);
                index = str.indexOf('#');
                ctx.metadataUrl = str.substring(0, index);
                const parts = str.substring(index + 1).split('/');
                const col = COLLECTION.exec(parts[0]);
                if (col) {
                    ctx.type = col[1];
                }
                else if (parts[0].indexOf('.') !== -1) {
                    ctx.type = parts[0];
                }
                else {
                    const property = parts[0].match(PROPERTY);
                    const expand = parts[0].match(EXPAND);
                    ctx.entity = parts[1] === '$entity';
                    if (property) {
                        ctx.entitySet = property[1];
                        ctx.key = property[2];
                        ctx.property = parts[1];
                    }
                    else if (expand) {
                        ctx.entitySet = expand[1];
                        ctx.expand = expand[2];
                    }
                    else {
                        ctx.entitySet = parts[0];
                    }
                }
            }
            return ctx;
        },
        countParam() {
            return { [$COUNT]: 'true' };
        },
    },
    //#endregion
    //#region Version 3.0
    [VERSION_3_0]: {
        ...ODataVersionBaseHelper,
        ODATA_ANNOTATION_PREFIX: 'odata.',
        ODATA_FUNCTION_PREFIX: '',
        ODATA_ID: 'odata.id',
        ODATA_ETAG: 'odata.etag',
        ODATA_CONTEXT: 'odata.metadata',
        ODATA_NEXTLINK: 'odata.nextLink',
        ODATA_TYPE: 'odata.type',
        ODATA_COUNT: 'odata.count',
        VALUE: 'value',
        context(annots) {
            let ctx = {};
            const str = annots instanceof Map
                ? annots.get(this.ODATA_CONTEXT)
                : annots[this.ODATA_CONTEXT];
            if (typeof str === 'string') {
                let index = str.indexOf('$metadata');
                ctx.serviceRootUrl = str.substring(0, index);
                index = str.indexOf('#');
                ctx.metadataUrl = str.substring(0, index);
                const parts = str.substring(index + 1).split('/');
                ctx.entitySet = parts[0];
            }
            return ctx;
        },
        countParam() {
            return { [$INLINECOUNT]: 'allpages' };
        },
    },
    //#endregion
    //#region Version 2.0
    [VERSION_2_0]: {
        ...ODataVersionBaseHelper,
        ODATA_ID: 'id',
        ODATA_ETAG: 'etag',
        ODATA_ANNOTATION: '__metadata',
        ODATA_NEXTLINK: '__next',
        ODATA_COUNT: '__count',
        ODATA_DEFERRED: '__deferred',
        ODATA_TYPE: 'type',
        VALUE: 'results',
        annotations(value) {
            const annots = new Map();
            if (this.ODATA_ANNOTATION in value) {
                Object.entries(value[this.ODATA_ANNOTATION]).forEach(([key, value]) => annots.set(key, value));
            }
            return annots;
        },
        context(annots) {
            let ctx = {};
            return ctx;
        },
        attributes(value, metadata) {
            return value;
        },
        countParam() {
            return { [$INLINECOUNT]: 'allpages' };
        },
    },
    //#endregion
};

class ODataAnnotation {
    constructor(annot) {
        this.term = annot.term;
        Object.assign(this, annot);
    }
}
class ODataAnnotatable {
    constructor(config) {
        this.annotations = (config.annotations || []).map((annot) => new ODataAnnotation(annot));
    }
    /**
     * Find an annotation inside the annotatable.
     * @param predicate Function that returns true if the annotation match.
     * @returns The annotation that matches the predicate.
     */
    findAnnotation(predicate) {
        return this.annotations.find(predicate);
    }
    /**
     * Find an annotation inside the annotatable and return its value.
     * @param term The term of the annotation to find.
     * @returns The value of the annotation.
     */
    annotatedValue(term) {
        const reg = term instanceof RegExp ? term : new RegExp(`^${term}$`);
        const annot = this.findAnnotation((a) => reg.test(a.term));
        if (!annot) {
            return undefined;
        }
        return (annot.string ||
            annot.bool ||
            annot.int ||
            annot.permissions ||
            annot.properties);
    }
}

class ODataSchemaElement extends ODataAnnotatable {
    constructor(config, schema) {
        super(config);
        this.schema = schema;
        this.name = config.name;
    }
    get api() {
        return this.schema.api;
    }
    /**
     * Create a nicer looking title.
     * Titleize is meant for creating pretty output.
     * @param term The term of the annotation to find.
     * @returns The titleized string.
     */
    titleize(term) {
        return (term && this.annotatedValue(term)) || Strings.titleCase(this.name);
    }
    /**
     * Returns a full type of the structured type including the namespace/alias.
     * @param alias Use the alias of the namespace instead of the namespace.
     * @returns The string representation of the type.
     */
    type({ alias = false } = {}) {
        return `${alias ? this.schema.alias : this.schema.namespace}.${this.name}`;
    }
    /**
     * Returns a boolean indicating if the structured type is of the given type.
     * @param type String representation of the type
     * @returns True if the callable is type of the given type
     */
    isTypeOf(type) {
        var names = [`${this.schema.namespace}.${this.name}`];
        if (this.schema.alias)
            names.push(`${this.schema.alias}.${this.name}`);
        return names.indexOf(type) !== -1;
    }
    /**
     * Returns a boolean indicating if the structured type is a subtype of the given type.
     * @param type String representation of the type
     * @returns True if the callable is type of the given type
     */
    isSubtypeOf(schema) {
        if (this.isTypeOf(schema.type()))
            return true;
        return false;
    }
    /**
     * Returns a boolean indicating if the structured type is a supertype of the given type.
     * @param type String representation of the type
     * @returns True if the callable is type of the given type
     */
    isSupertypeOf(schema) {
        if (this.isTypeOf(schema.type()))
            return true;
        return false;
    }
}

//https://github.com/niklasvh/base64-arraybuffer
const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
// Use a lookup table to find the index.
const lookup = new Uint8Array(256);
for (var i = 0; i < chars.length; i++) {
    lookup[chars.charCodeAt(i)] = i;
}
const ArrayBuffers = {
    toArrayBuffer(v) {
        var bufferLength = v.length * 0.75, len = v.length, i, p = 0, encoded1, encoded2, encoded3, encoded4;
        if (v[v.length - 1] === '=') {
            bufferLength--;
            if (v[v.length - 2] === '=') {
                bufferLength--;
            }
        }
        var arraybuffer = new ArrayBuffer(bufferLength), bytes = new Uint8Array(arraybuffer);
        for (i = 0; i < len; i += 4) {
            encoded1 = lookup[v.charCodeAt(i)];
            encoded2 = lookup[v.charCodeAt(i + 1)];
            encoded3 = lookup[v.charCodeAt(i + 2)];
            encoded4 = lookup[v.charCodeAt(i + 3)];
            bytes[p++] = (encoded1 << 2) | (encoded2 >> 4);
            bytes[p++] = ((encoded2 & 15) << 4) | (encoded3 >> 2);
            bytes[p++] = ((encoded3 & 3) << 6) | (encoded4 & 63);
        }
        return arraybuffer;
    },
    toString(v) {
        var bytes = new Uint8Array(v), i, len = bytes.length, base64 = '';
        for (i = 0; i < len; i += 3) {
            base64 += chars[bytes[i] >> 2];
            base64 += chars[((bytes[i] & 3) << 4) | (bytes[i + 1] >> 4)];
            base64 += chars[((bytes[i + 1] & 15) << 2) | (bytes[i + 2] >> 6)];
            base64 += chars[bytes[i + 2] & 63];
        }
        if (len % 3 === 2) {
            base64 = base64.substring(0, base64.length - 1) + '=';
        }
        else if (len % 3 === 1) {
            base64 = base64.substring(0, base64.length - 2) + '==';
        }
        return base64;
    },
};

// Core EdmTypeParserBuilder
const EdmParser = (_d, _s, _e) => ({
    deserialize(value, options) {
        return Array.isArray(value)
            ? value.map((v) => _d(v, options))
            : _d(value, options);
    },
    serialize(value, options) {
        return Array.isArray(value)
            ? value.map((v) => _s(v, options))
            : _s(value, options);
    },
    encode(value, options) {
        return Array.isArray(value)
            ? value.map((v) => _e(v, options))
            : _e(value, options);
    },
});
const Identity = (v) => v;
const toNumber = (v) => Number(v);
const toString = (v) => v.toString();
const toBoolean = (v) => Boolean(v);
const toDate = (v) => new Date(v);
const EDM_PARSERS = {
    //Edm.Guid 16-byte (128-bit) unique identifier
    'Edm.Guid': EdmParser(Identity, Identity, (v) => raw(v)),
    //Edm.Int16 Signed 16-bit integer
    'Edm.Int16': EdmParser(toNumber, toNumber, toNumber),
    //Edm.String Sequence of UTF-8 characters
    'Edm.String': EdmParser(toString, toString, toString),
    //Edm.Boolean Binary-valued logic
    'Edm.Boolean': EdmParser(toBoolean, toBoolean, toBoolean),
    //Edm.Byte Unsigned 8-bit integer
    'Edm.Byte': EdmParser(toNumber, toNumber, toNumber),
    //Edm.SByte Signed 8-bit integer
    'Edm.SByte': EdmParser(toNumber, toNumber, toNumber),
    //Edm.Int32 Signed 16-bit integer
    'Edm.Int32': EdmParser(toNumber, toNumber, toNumber),
    //Edm.Int64 Signed 16-bit integer
    'Edm.Int64': EdmParser(toNumber, toNumber, toNumber),
    //Edm.Date Date without a time-zone offset
    'Edm.Date': EdmParser((v) => new Date(`${v}T00:00:00.000Z`), (v) => toDate(v).toISOString().substring(0, 10), (v) => raw(toDate(v).toISOString().substring(0, 10))),
    //Edm.TimeOfDay Clock time 00:00-23:59:59.999999999999
    'Edm.TimeOfDay': EdmParser((v) => new Date(`1970-01-01T${v}Z`), (v) => toDate(v).toISOString().substring(11, 23), (v) => raw(toDate(v).toISOString().substring(11, 23))),
    //Edm.DateTimeOffset Date and time with a time-zone offset, no leap seconds
    'Edm.DateTimeOffset': EdmParser(toDate, (v) => toDate(v).toISOString(), (v) => raw(toDate(v).toISOString())),
    //Edm.Duration Signed duration in days, hours, minutes, and (sub)seconds
    'Edm.Duration': EdmParser((v) => Durations.toDuration(v), (v) => Durations.toString(v), (v) => raw(Durations.toString(v))),
    //Edm.Decimal Numeric values with fixed precision and scale
    'Edm.Decimal': EdmParser((v, o) => {
        if (typeof v === 'string' && o.ieee754Compatible) {
            return parseFloat(v);
        }
        return v;
    }, (v, o) => {
        if (o.ieee754Compatible) {
            let vstr = v.toPrecision(o.field.precision);
            if (typeof o.field.scale === 'number') {
                vstr = parseFloat(vstr).toFixed(o.field.scale);
            }
            return vstr;
        }
        return v;
    }, (v, o) => {
        if (o.ieee754Compatible) {
            let vstr = v.toPrecision(o.field.precision);
            if (typeof o.field.scale === 'number') {
                vstr = parseFloat(vstr).toFixed(o.field.scale);
            }
            return raw(vstr);
        }
        return v;
    }),
    //Edm.Double IEEE 754 binary64 floating-point number (15-17 decimal digits)
    'Edm.Double': EdmParser((v) => (v === 'INF' ? Infinity : v), (v) => (v === Infinity ? 'INF' : v), (v) => raw((v === Infinity ? 'INF' : v.toString()))),
    //Edm.Single IEEE 754 binary32 floating-point number (6-9 decimal digits)
    'Edm.Single': EdmParser((v) => (v === 'INF' ? Infinity : v), (v) => (v === Infinity ? 'INF' : v), (v) => raw((v === Infinity ? 'INF' : v.toString()))),
    //Edm.Binary Binary data
    'Edm.Binary': EdmParser((v) => ArrayBuffers.toArrayBuffer(v), (v) => ArrayBuffers.toString(v), (v) => raw(ArrayBuffers.toString(v))),
};
/*
Edm.Stream Binary data stream
Edm.Geography Abstract base type for all Geography types
Edm.GeographyPoint A point in a round-earth coordinate system
Edm.GeographyLineString Line string in a round-earth coordinate system
Edm.GeographyPolygon Polygon in a round-earth coordinate system
Edm.GeographyMultiPoint Collection of points in a round-earth coordinate system
Edm.GeographyMultiLineString Collection of line strings in a round-earth coordinate system
Edm.GeographyMultiPolygon Collection of polygons in a round-earth coordinate system
Edm.GeographyCollection Collection of arbitrary Geography values
Edm.Geometry Abstract base type for all Geometry types
Edm.GeometryPoint Point in a flat-earth coordinate system
Edm.GeometryLineString Line string in a flat-earth coordinate system
Edm.GeometryPolygon Polygon in a flat-earth coordinate system
Edm.GeometryMultiPoint Collection of points in a flat-earth coordinate system
Edm.GeometryMultiLineString Collection of line strings in a flat-earth coordinate system
Edm.GeometryMultiPolygon Collection of polygons in a flat-earth coordinate system
Edm.GeometryCollection Collection of arbitrary Geometry values
*/

class ODataEnumTypeFieldParser extends ODataAnnotatable {
    constructor(name, field) {
        super(field);
        this.name = name;
        this.value = field.value;
    }
    titleize(term) {
        return (term && this.annotatedValue(term)) || this.name;
    }
}
class ODataEnumTypeParser extends ODataAnnotatable {
    constructor(config, namespace, alias) {
        super(config);
        this.name = config.name;
        this.namespace = namespace;
        this.alias = alias;
        this.flags = config.flags;
        this.members = config.members;
        this._fields = Object.entries(config.fields).map(([name, f]) => new ODataEnumTypeFieldParser(name, f));
    }
    configure({ options, parserForType, findOptionsForType, }) {
        this.parserOptions = options;
    }
    isTypeOf(type) {
        var names = [`${this.namespace}.${this.name}`];
        if (this.alias)
            names.push(`${this.alias}.${this.name}`);
        return names.indexOf(type) !== -1;
    }
    fields(value) {
        return [
            ...this._fields.filter((f) => value === undefined || Boolean(f.value & value)),
        ];
    }
    field(nameValue) {
        let field = this.fields().find((f) => f.name === nameValue || f.value === nameValue);
        //Throw error if not found
        if (field === undefined)
            throw new Error(`${this.name} has no field named ${String(name)}`);
        return field;
    }
    /**
     * Map the fields of the enum type.
     * @param mapper Function that maps the value to the new value
     * @returns The fields mapped by the mapper
     */
    mapFields(mapper) {
        return this.fields().map(mapper);
    }
    // Deserialize
    deserialize(value, options) {
        // string -> number
        const parserOptions = { ...this.parserOptions, ...options };
        if (this.flags) {
            return Enums.toValues(this.members, value).reduce((acc, v) => acc | v, 0);
        }
        else {
            return Enums.toValue(this.members, value);
        }
    }
    // Serialize
    serialize(value, options) {
        // Enum are string | number
        // string | number -> string
        const parserOptions = { ...this.parserOptions, ...options };
        if (this.flags) {
            let names = Enums.toNames(this.members, value);
            if (names.length === 0)
                names = [`${value}`];
            return !parserOptions?.stringAsEnum
                ? `${this.namespace}.${this.name}'${names.join(', ')}'`
                : names.join(', ');
        }
        else {
            let name = Enums.toName(this.members, value);
            if (name === undefined)
                name = `${value}`;
            return !parserOptions?.stringAsEnum
                ? `${this.namespace}.${this.name}'${name}'`
                : name;
        }
    }
    //Encode
    encode(value, options) {
        const parserOptions = { ...this.parserOptions, ...options };
        const serialized = this.serialize(value, parserOptions);
        if (serialized === undefined)
            return undefined;
        return parserOptions?.stringAsEnum
            ? raw(`'${serialized}'`)
            : raw(serialized);
    }
    // Json Schema
    toJsonSchema() {
        let property = {
            title: this.name,
            type: 'string',
        };
        property.enum = this._fields.map((f) => f.name);
        return property;
    }
    validate(member, { method, navigation = false, } = {}) {
        if (this.flags) {
            let members = Enums.toValues(this.members, member);
            return members.some((member) => !(member in this.members))
                ? ['mismatch']
                : undefined;
        }
        else {
            return !(member in this.members) ? ['mismatch'] : undefined;
        }
    }
    unpack(value) {
        return Enums.toValues(this.members, value);
    }
    pack(value) {
        return Enums.toValues(this.members, value).reduce((acc, v) => acc | v, 0);
    }
}

class ODataEntityTypeKey {
    constructor({ name, alias }) {
        this.name = name;
        this.alias = alias;
    }
}
class ODataReferential {
    constructor({ property, referencedProperty, }) {
        this.property = property;
        this.referencedProperty = referencedProperty;
    }
}
class ODataStructuredTypeFieldParser extends ODataAnnotatable {
    constructor(name, structuredType, field) {
        super(field);
        this.name = name;
        this.structuredType = structuredType;
        this.type = field.type;
        this.parser = NONE_PARSER;
        this.referentials = (field.referentials || []).map((referential) => new ODataReferential(referential));
        this.default = field.default;
        this.maxLength = field.maxLength;
        this.nullable = field.nullable !== undefined ? field.nullable : true;
        this.collection = Boolean(field.collection);
        this.navigation = Boolean(field.navigation);
        this.precision = field.precision;
        this.scale = field.scale;
    }
    validate(value, { method, navigation = false, } = {}) {
        let errors;
        if (this.collection && Array.isArray(value)) {
            errors = value.map((v) => this.validate(v, { method, navigation }));
        }
        else if ((this.isStructuredType() &&
            typeof value === 'object' &&
            value !== null) ||
            (this.navigation && value !== undefined)) {
            errors =
                this.structured().validate(value, { method, navigation }) ||
                    {};
        }
        else if (this.isEnumType() &&
            (typeof value === 'string' || typeof value === 'number')) {
            errors = this.enum().validate(value, { method, navigation });
        }
        else {
            // IsEdmType
            const computed = this.annotatedValue(COMPUTED);
            errors = [];
            if (!this.nullable &&
                (value === null || (value === undefined && method !== 'modify')) && // Is null or undefined without patch?
                !(computed && method === 'create') // Not (Is Computed field and create) ?
            ) {
                errors.push(`required`);
            }
            if (this.maxLength !== undefined &&
                typeof value === 'string' &&
                value.length > this.maxLength) {
                errors.push(`maxlength`);
            }
        }
        return !Types.isEmpty(errors) ? errors : undefined;
    }
    //#region Deserialize
    parse(parser, value, options) {
        const type = options !== undefined && Types.isPlainObject(value)
            ? ODataHelper[options?.version || DEFAULT_VERSION].type(value)
            : undefined;
        if (type !== undefined) {
            return parser
                .childParser((c) => c.isTypeOf(type))
                .deserialize(value, options);
        }
        return parser.deserialize(value, options);
    }
    deserialize(value, options) {
        const parserOptions = { ...this.parserOptions, ...options };
        if (this.parser instanceof ODataStructuredTypeParser) {
            const parser = this.parser;
            return Array.isArray(value)
                ? value.map((v) => this.parse(parser, v, parserOptions))
                : this.parse(parser, value, parserOptions);
        }
        return this.parser.deserialize(value, {
            field: this,
            ...parserOptions,
        });
    }
    //#endregion
    //#region Serialize
    toJson(parser, value, options) {
        const type = options !== undefined && Types.isPlainObject(value)
            ? ODataHelper[options?.version || DEFAULT_VERSION].type(value)
            : undefined;
        if (type !== undefined) {
            return parser
                .childParser((c) => c.isTypeOf(type))
                .serialize(value, options);
        }
        return parser.serialize(value, options);
    }
    serialize(value, options) {
        const parserOptions = { ...this.parserOptions, ...options };
        if (this.parser instanceof ODataStructuredTypeParser) {
            const parser = this.parser;
            return Array.isArray(value)
                ? value.map((v) => this.toJson(parser, v, parserOptions))
                : this.toJson(parser, value, parserOptions);
        }
        return this.parser.serialize(value, {
            field: this,
            ...parserOptions,
        });
    }
    //#endregion
    //#region Encode
    encode(value, options) {
        const parserOptions = { ...this.parserOptions, ...options };
        return this.parser.encode(value, {
            field: this,
            ...parserOptions,
        });
    }
    //#endregion
    configure({ options, parserForType, findOptionsForType, }) {
        this.parserOptions = options;
        this.parser = parserForType(this.type);
        if (this.default !== undefined) {
            this.default = this.deserialize(this.default, options);
        }
    }
    //#region Json Schema
    // https://json-schema.org/
    toJsonSchema(options = {}) {
        let schema = this.parser instanceof ODataStructuredTypeFieldParser ||
            this.parser instanceof ODataStructuredTypeParser ||
            this.parser instanceof ODataEnumTypeParser
            ? this.parser.toJsonSchema(options)
            : { title: this.name, type: 'object' };
        if ([
            'Edm.String',
            'Edm.Date',
            'Edm.TimeOfDay',
            'Edm.DateTimeOffset',
            'Edm.Guid',
            'Edm.Binary',
        ].indexOf(this.type) !== -1) {
            schema.type = 'string';
            if (this.type === 'Edm.Date')
                schema.format = 'date';
            else if (this.type === 'Edm.TimeOfDay')
                schema.format = 'time';
            else if (this.type === 'Edm.DateTimeOffset')
                schema.format = 'date-time';
            else if (this.type === 'Edm.Guid')
                schema.pattern =
                    '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$';
            else if (this.type === 'Edm.Binary')
                schema.contentEncoding = 'base64';
            else if (this.type === 'Edm.String' && this.maxLength)
                schema.maxLength = this.maxLength;
        }
        else if (['Edm.Int64', 'Edm.Int32', 'Edm.Int16', 'Edm.Byte', 'Edm.SByte'].indexOf(this.type) !== -1) {
            //TODO: Range
            schema.type = 'integer';
        }
        else if (['Edm.Decimal', 'Edm.Double'].indexOf(this.type) !== -1) {
            schema.type = 'number';
        }
        else if (['Edm.Boolean'].indexOf(this.type) !== -1) {
            schema.type = 'boolean';
        }
        if (this.default)
            schema.default = this.default;
        if (this.nullable)
            schema.type = [schema.type, 'null'];
        if (this.collection)
            schema = {
                type: 'array',
                items: schema,
                additionalItems: false,
            };
        return schema;
    }
    //#endregion
    isKey() {
        return (this.structuredType
            .keys({ include_parents: true })
            ?.find((k) => k.name === this.name) !== undefined);
    }
    hasReferentials() {
        return this.referentials.length !== 0;
    }
    isEdmType() {
        return this.type.startsWith('Edm.');
    }
    isEnumType() {
        return this.parser instanceof ODataEnumTypeParser;
    }
    enum() {
        if (!this.isEnumType())
            throw new Error('Field are not EnumType');
        return this.parser;
    }
    isStructuredType() {
        return this.parser instanceof ODataStructuredTypeParser;
    }
    structured() {
        if (!this.isStructuredType())
            throw new Error('Field are not StrucuturedType');
        return this.parser;
    }
    field(name) {
        if (this.isStructuredType())
            return this.parser.field(name);
        throw new Error(`The field ${this.name} is not related to a StructuredType`);
    }
}
class ODataStructuredTypeParser extends ODataAnnotatable {
    constructor(config, namespace, alias) {
        super(config);
        this.children = [];
        this.name = config.name;
        this.base = config.base;
        this.open = config.open || false;
        this.namespace = namespace;
        this.alias = alias;
        if (Array.isArray(config.keys))
            this._keys = config.keys.map((key) => new ODataEntityTypeKey(key));
        this._fields = Object.entries(config.fields).map(([name, config]) => new ODataStructuredTypeFieldParser(name, this, config));
    }
    /**
     * Create a nicer looking title.
     * Titleize is meant for creating pretty output.
     * @param term The term of the annotation to find.
     * @returns The titleized string.
     */
    titleize(term) {
        return (term && this.annotatedValue(term)) || Strings.titleCase(this.name);
    }
    isTypeOf(type) {
        var names = [`${this.namespace}.${this.name}`];
        if (this.alias)
            names.push(`${this.alias}.${this.name}`);
        return names.indexOf(type) !== -1;
    }
    isSubtypeOf(type) {
        if (this.isTypeOf(type))
            return true;
        if (this.parent)
            return this.parent.isSubtypeOf(type);
        return false;
    }
    findChildParser(predicate) {
        if (predicate(this))
            return this;
        let match;
        for (let ch of this.children) {
            match = ch.findChildParser(predicate);
            if (match !== undefined)
                break;
        }
        return match;
    }
    childParser(predicate) {
        return this.findChildParser(predicate) || NONE_PARSER;
    }
    // Deserialize
    deserialize(value, options) {
        const parserOptions = { ...this.parserOptions, ...options };
        const fields = this.fields({
            include_navigation: true,
            include_parents: true,
        }).filter((f) => f.name in value && value[f.name] !== undefined && value[f.name] !== null);
        return {
            ...value,
            ...fields.reduce((acc, f) => ({
                ...acc,
                [f.name]: f.deserialize(value[f.name], parserOptions),
            }), {}),
        };
    }
    // Serialize
    serialize(value, options) {
        const parserOptions = { ...this.parserOptions, ...options };
        const fields = this.fields({
            include_navigation: true,
            include_parents: true,
        }).filter((f) => f.name in value &&
            value[f.name] !== undefined &&
            value[f.name] !== null);
        return {
            ...value,
            ...fields.reduce((acc, f) => ({
                ...acc,
                [f.name]: f.serialize(value[f.name], parserOptions),
            }), {}),
        };
    }
    // Encode
    encode(value, options) {
        const parserOptions = { ...this.parserOptions, ...options };
        return raw(JSON.stringify(this.serialize(value, parserOptions)));
    }
    configure({ options, parserForType, findOptionsForType, }) {
        this.parserOptions = options;
        if (this.base) {
            const parent = parserForType(this.base);
            parent.children.push(this);
            this.parent = parent;
        }
        this._fields.forEach((f) => f.configure({ options, parserForType, findOptionsForType }));
    }
    /**
     * Returns all fields of the structured type.
     * @param include_navigation Include navigation properties in the result.
     * @param include_parents Include the parent types in the result.
     * @returns All fields of the structured type.
     */
    fields({ include_navigation, include_parents, }) {
        return [
            ...(include_parents && this.parent !== undefined
                ? this.parent.fields({ include_parents, include_navigation })
                : []),
            ...this._fields.filter((field) => include_navigation || !field.navigation),
        ];
    }
    /**
     * Returns the keys of the structured type.
     * @param include_parents Include the parent fields
     * @returns The keys of the structured type
     */
    keys({ include_parents, }) {
        return [
            ...(include_parents && this.parent !== undefined
                ? this.parent.keys({ include_parents })
                : []),
            ...(this._keys || []),
        ];
    }
    /**
     * Find the field parser for the given field name.
     * @param name Name of the field
     * @returns The field parser
     */
    field(name) {
        let field = this.fields({
            include_parents: true,
            include_navigation: true,
        }).find((field) => field.name === name);
        //Throw error if not found
        if (field === undefined)
            throw new Error(`${this.name} has no field named ${String(name)}`);
        return field;
    }
    /**
     * Picks the fields from attributes.
     * @param attrs
     * @param include_parents Include the parent fields
     * @param include_navigation Include the navigation fields
     * @param include_etag Include the etag field
     * @returns The picked fields
     */
    pick(attrs, { include_parents = true, include_navigation = false, include_etag = true, options, } = {}) {
        const parserOptions = options || this.parserOptions;
        const names = this.fields({ include_parents, include_navigation }).map((f) => f.name);
        return Object.keys(attrs)
            .filter((key) => names.indexOf(key) !== -1 ||
            (key ==
                ODataHelper[parserOptions?.version || DEFAULT_VERSION].ODATA_ETAG &&
                include_etag))
            .reduce((acc, key) => Object.assign(acc, { [key]: attrs[key] }), {});
    }
    resolveKey(value, { resolve = true, single = true, } = {}) {
        const keyTypes = this.keys({ include_parents: true });
        const key = new Map();
        for (var kt of keyTypes) {
            let v = value;
            let structured = this;
            let field;
            for (let name of kt.name.split('/')) {
                if (structured === undefined)
                    break;
                field = structured
                    .fields({ include_navigation: false, include_parents: true })
                    .find((f) => f.name === name);
                if (field !== undefined) {
                    v = Types.isPlainObject(v) ? v[field.name] : v;
                    structured = field.isStructuredType()
                        ? field.structured()
                        : undefined;
                }
            }
            if (field !== undefined && v !== undefined) {
                key.set(kt.alias || field.name, field.encode(v));
            }
        }
        if (key.size === 0)
            return undefined;
        return resolve
            ? Objects.resolveKey(key, { single })
            : Object.fromEntries(key);
    }
    defaults() {
        let fields = this.fields({
            include_navigation: false,
            include_parents: true,
        }).filter((f) => f.default !== undefined || f.isStructuredType());
        return {
            ...fields.reduce((acc, f) => {
                let value = f.isStructuredType()
                    ? f.structured().defaults()
                    : f.default;
                return Types.isEmpty(value) ? acc : { ...acc, [f.name]: value };
            }, {}),
        };
    }
    // Json Schema
    toJsonSchema(options = {}) {
        let schema = {
            $schema: 'http://json-schema.org/draft-07/schema#',
            $id: `${this.namespace}.${this.name}`,
            title: this.titleize(DESCRIPTION),
            description: this.annotatedValue(LONG_DESCRIPTION),
            type: 'object',
            properties: {},
            required: [],
        };
        const fields = this.fields({
            include_navigation: true,
            include_parents: true,
        }).filter((f) => (!f.navigation || (options.expand && f.name in options.expand)) &&
            (!options.select || options.select.indexOf(f.name) !== -1));
        schema.properties = Object.assign({}, schema.properties, fields
            .map((f) => {
            let expand = options.expand && f.name in options.expand
                ? options.expand[f.name]
                : undefined;
            let schema = f.toJsonSchema(expand);
            if (options.custom && f.name in options.custom)
                schema = options.custom[f.name](schema, f);
            return { [f.name]: schema };
        })
            .reduce((acc, v) => Object.assign(acc, v), {}));
        schema.required = [
            ...schema.required,
            ...fields
                .filter((f) => options.required && f.name in options.required
                ? options.required[f.name]
                : !f.nullable)
                .map((f) => f.name),
        ];
        return schema;
    }
    validate(attrs, { method, navigation = false, } = {}) {
        const errors = {};
        const fields = this.fields({
            include_navigation: true,
            include_parents: true,
        }).filter((f) => !f.navigation || navigation);
        for (var field of fields) {
            const value = attrs[field.name];
            const errs = field.validate(value, { method, navigation });
            if (errs !== undefined) {
                errors[field.name] = errs;
            }
        }
        return !Types.isEmpty(errors) ? errors : undefined;
    }
}

class ODataParameterParser {
    constructor(name, parameter) {
        this.name = name;
        this.type = parameter.type;
        this.parser = NONE_PARSER;
        this.nullable = parameter.nullable;
        this.collection = parameter.collection;
    }
    serialize(value, options) {
        const parserOptions = { ...this.parserOptions, ...options };
        return Array.isArray(value)
            ? value.map((v) => this.parser.serialize(v, parserOptions))
            : this.parser.serialize(value, parserOptions);
    }
    //Encode
    encode(value, options) {
        const parserOptions = { ...this.parserOptions, ...options };
        return Array.isArray(value)
            ? value.map((v) => this.parser.encode(v, parserOptions))
            : this.parser.encode(value, parserOptions);
    }
    configure({ options, parserForType, findOptionsForType, }) {
        this.parserOptions = options;
        this.parser = parserForType(this.type);
    }
    isEdmType() {
        return this.type.startsWith('Edm.');
    }
    isEnumType() {
        return this.parser instanceof ODataEnumTypeParser;
    }
    enum() {
        if (!this.isEnumType())
            throw new Error('Field are not EnumType');
        return this.parser;
    }
    isStructuredType() {
        return this.parser instanceof ODataStructuredTypeParser;
    }
    structured() {
        if (!this.isStructuredType())
            throw new Error('Field are not StrucuturedType');
        return this.parser;
    }
    field(name) {
        if (this.isStructuredType())
            return this.parser.field(name);
        throw new Error(`The field ${this.name} is not related to a StructuredType`);
    }
}
class ODataCallableParser {
    constructor(config, namespace, alias) {
        this.name = config.name;
        this.namespace = namespace;
        this.alias = alias;
        this.return = config.return;
        this.parser = NONE_PARSER;
        this.parameters = Object.entries(config.parameters || []).map(([name, p]) => new ODataParameterParser(name, p));
    }
    isTypeOf(type) {
        var names = [`${this.namespace}.${this.name}`];
        if (this.alias)
            names.push(`${this.alias}.${this.name}`);
        return names.indexOf(type) !== -1;
    }
    // Deserialize
    deserialize(value, options) {
        const parserOptions = { ...this.parserOptions, ...options };
        return this.parser.deserialize(value, parserOptions);
    }
    // Serialize
    serialize(params, options) {
        const parserOptions = { ...this.parserOptions, ...options };
        const parameters = this.parameters
            .filter((p) => p.name !== CALLABLE_BINDING_PARAMETER)
            .filter((p) => p.name in params && params[p.name] !== undefined);
        return parameters.reduce((acc, p) => ({
            ...acc,
            [p.name]: p.serialize(params[p.name], parserOptions),
        }), {});
    }
    //Encode
    encode(params, options) {
        const parserOptions = { ...this.parserOptions, ...options };
        const parameters = this.parameters
            .filter((p) => p.name !== CALLABLE_BINDING_PARAMETER)
            .filter((p) => p.name in params && params[p.name] !== undefined);
        return parameters.reduce((acc, p) => ({
            ...acc,
            [p.name]: p.encode(params[p.name], parserOptions),
        }), {});
    }
    configure({ options, parserForType, findOptionsForType, }) {
        this.parserOptions = options;
        if (this.return)
            this.parser = parserForType(this.return.type) || NONE_PARSER;
        this.parameters.forEach((p) => p.configure({ options, parserForType, findOptionsForType }));
    }
    binding() {
        return this.parameters.find((p) => p.name === CALLABLE_BINDING_PARAMETER);
    }
}

class ODataCallable extends ODataSchemaElement {
    constructor(config, schema) {
        super(config, schema);
        this.entitySetPath = config.entitySetPath;
        this.bound = config.bound;
        this.composable = config.composable;
        this.parser = new ODataCallableParser(config, schema.namespace, schema.alias);
    }
    path() {
        let path;
        if (this.entitySetPath)
            path = this.entitySetPath;
        else if (this.bound)
            path = `${this.schema.namespace}.${this.name}`;
        else
            path = this.parser.return
                ? this.api.findEntitySetForType(this.parser.return.type)?.name ||
                    this.name
                : this.name;
        return path;
    }
    configure({ options, parserForType, findOptionsForType, }) {
        this.parser.configure({
            options,
            parserForType,
            findOptionsForType,
        });
    }
    /**
     * Deseialize the given value from the callable.
     * @param value Value to deserialize
     * @param options Options for deserialization
     * @returns Deserialized value
     */
    deserialize(value, options) {
        return this.parser.deserialize(value, options);
    }
    /**
     * Serialize the given value for the callable.
     * @param value Value to serialize
     * @param options Options for serialization
     * @returns Serialized value
     */
    serialize(value, options) {
        return this.parser.serialize(value, options);
    }
    /**
     * Encode the given value for the callable.
     * @param value Value to encode
     * @param options Options for encoding
     * @returns Encoded value
     */
    encode(value, options) {
        return this.parser.encode(value, options);
    }
    /**
     * Returns the binding parameter of the callable.
     * @returns The binding parameter of the callable.
     */
    binding() {
        return this.parser.binding();
    }
}

class ODataEntitySet extends ODataSchemaElement {
    constructor(config, schema) {
        super(config, schema);
        this.entityType = config.entityType;
        this.service = config.service;
    }
}

class ODataEntityContainer extends ODataSchemaElement {
    constructor(config, schema) {
        super(config, schema);
        this.entitySets = (config.entitySets || []).map((config) => new ODataEntitySet(config, schema));
    }
}

class ODataEnumType extends ODataSchemaElement {
    constructor(config, schema) {
        super(config, schema);
        this.members = config.members;
        this.parser = new ODataEnumTypeParser(config, schema.namespace, schema.alias);
    }
    configure({ options, parserForType, findOptionsForType, }) {
        this.parser.configure({ options, parserForType, findOptionsForType });
    }
    /**
     * Returns the fields of the enum type.
     * @returns The fields of the enum type.
     */
    fields(value) {
        return this.parser.fields(value);
    }
    /**
     * Find a field by name or value.
     * @param enu The name or value of the field
     * @returns The field with the given name or value
     */
    field(enu) {
        return this.parser.field(enu);
    }
    /**
     * Map the fields of the enum type.
     * @param mapper Function that maps the value to the new value
     * @returns The fields mapped by the mapper
     */
    mapFields(mapper) {
        return this.parser.mapFields(mapper);
    }
    /**
     * Deseialize the given value from the enum type.
     * @param value Value to deserialize
     * @param options Options for deserialization
     * @returns Deserialized value
     */
    deserialize(value, options) {
        return this.parser.deserialize(value, options);
    }
    /**
     * Serialize the given value for the enum type.
     * @param value Value to serialize
     * @param options Options for serialization
     * @returns Serialized value
     */
    serialize(value, options) {
        return this.parser.serialize(value, options);
    }
    /**
     * Encode the given value for the enum type.
     * @param value Value to encode
     * @param options Options for encoding
     * @returns Encoded value
     */
    encode(value, options) {
        return this.parser.encode(value, options);
    }
    unpack(value) {
        return this.parser.unpack(value);
    }
    pack(value) {
        return this.parser.pack(value);
    }
}

class ODataStructuredType extends ODataSchemaElement {
    constructor(config, schema) {
        super(config, schema);
        this.children = [];
        this.base = config.base;
        this.open = config.open || false;
        this.parser = new ODataStructuredTypeParser(config, schema.namespace, schema.alias);
        this.model = config.model;
        this.collection = config.collection;
        if (this.model !== undefined) {
            const options = this.model.hasOwnProperty('options')
                ? this.model.options
                : { fields: {} };
            this.model.buildMeta({ options, schema: this });
        }
        if (this.collection !== undefined) {
            this.collection.model = this.model;
        }
    }
    configure({ options, parserForType, findOptionsForType, }) {
        if (this.base) {
            const parent = this.api.findStructuredTypeForType(this.base);
            parent.children.push(this);
            this.parent = parent;
        }
        this.parser.configure({
            options,
            parserForType,
            findOptionsForType,
        });
        if (this.model !== undefined && this.model.options !== null) {
            this.model.meta.configure({
                options,
                parserForType,
                findOptionsForType,
            });
        }
    }
    /**
     * Returns a boolean indicating if the structured type is a subtype of the given type.
     * @param type String representation of the type
     * @returns True if the callable is type of the given type
     */
    isSubtypeOf(schema) {
        return (super.isSubtypeOf(schema) ||
            (this.parent !== undefined && this.parent.isSubtypeOf(schema)));
    }
    /**
     * Returns a boolean indicating if the structured type is a supertype of the given type.
     * @param type String representation of the type
     * @returns True if the callable is type of the given type
     */
    isSupertypeOf(schema) {
        return (super.isSupertypeOf(schema) ||
            this.children.some((c) => c.isSupertypeOf(schema)));
    }
    /**
     * Returns a boolean indicating if the structured type has a simple key.
     * @returns True if the structured type has a simple key
     */
    isSimpleKey() {
        return this.keys().length === 1;
    }
    /**
     * Returns a boolean indicating if the structured type has a compound key.
     * @returns True if the structured type has a compound key.
     */
    isCompoundKey() {
        return this.keys().length > 1;
    }
    /**
     * Find the field parser for the given field name.
     * @param name Name of the field
     * @returns The field parser
     */
    field(name) {
        return this.parser.field(name);
    }
    /**
     * Find a parent schema of the structured type.
     * @param predicate Function for evaluate the schemas in the hierarchy.
     * @returns The schema that matches the predicate.
     */
    findParentSchema(predicate) {
        if (predicate(this))
            return this;
        if (this.parent === undefined)
            return undefined;
        return this.parent.findParentSchema(predicate);
    }
    /**
     * Find a parent schema of the structured type for the given field.
     * @param field Field that belongs to the structured type
     * @returns The schema of the field
     */
    findSchemaForField(field) {
        return this.findParentSchema((p) => p
            .fields({ include_parents: false, include_navigation: true })
            .find((f) => f === field) !== undefined);
    }
    /**
     * Picks the fields from attributes.
     * @param attrs
     * @param include_parents Include the parent fields
     * @param include_navigation Include the navigation fields
     * @param include_etag Include the etag field
     * @returns The picked fields
     */
    pick(attrs, { include_parents = true, include_navigation = false, include_etag = true, } = {}) {
        return this.parser.pick(attrs, {
            include_etag,
            include_navigation,
            include_parents,
            options: this.api.options,
        });
    }
    /**
     * Deseialize the given value from the structured type.
     * @param value Value to deserialize
     * @param options Options for deserialization
     * @returns Deserialized value
     */
    deserialize(value, options) {
        return this.parser.deserialize(value, options);
    }
    /**
     * Serialize the given value for the structured type.
     * @param value Value to serialize
     * @param options Options for serialization
     * @returns Serialized value
     */
    serialize(value, options) {
        return this.parser.serialize(value, options);
    }
    /**
     * Encode the given value for the structured type.
     * @param value Value to encode
     * @param options Options for encoding
     * @returns Encoded value
     */
    encode(value, options) {
        return this.parser.encode(value, options);
    }
    /**
     * Returns all fields of the structured type.
     * @param include_navigation Include navigation properties in the result.
     * @param include_parents Include the parent types in the result.
     * @returns All fields of the structured type.
     */
    fields({ include_navigation, include_parents, }) {
        return this.parser.fields({ include_navigation, include_parents });
    }
    /**
     * Returns the keys of the structured type.
     * @param include_parents Include the parent fields
     * @returns The keys of the structured type
     */
    keys({ include_parents = true, } = {}) {
        return this.parser.keys({ include_parents });
    }
    /**
     * Resolve the key of the structured type for the given value.
     * @param attrs Attributes of the value
     * @returns Resolved key
     */
    resolveKey(attrs) {
        return this.parser.resolveKey(attrs);
    }
    /**
     * Returns the defaults values for the structured type.
     * @returns Default values for the structured type
     */
    defaults() {
        return this.parser.defaults();
    }
    /**
     * Convert the structured type to json schema
     * @param options Options for json schema
     * @returns Json Schema
     */
    toJsonSchema(options = {}) {
        return this.parser.toJsonSchema(options);
    }
    /**
     * Validate the given value against the structured type.
     * @param attrs Attributes of the value
     * @param method Method to use for the process validation
     * @returns Object with the errors
     */
    validate(attrs, { method, navigation = false, } = {}) {
        return this.parser.validate(attrs, { method, navigation });
    }
}

class ODataSchema extends ODataAnnotatable {
    constructor(config, api) {
        super(config);
        this.api = api;
        this.namespace = config.namespace;
        this.alias = config.alias;
        this.enums = (config.enums || []).map((config) => new ODataEnumType(config, this));
        this.entities = (config.entities || []).map((config) => new ODataStructuredType(config, this));
        this.callables = OData.mergeCallableParameters(config.callables || []).map((config) => new ODataCallable(config, this));
        this.containers = (config.containers || []).map((config) => new ODataEntityContainer(config, this));
    }
    isNamespaceOf(type) {
        return (type.startsWith(this.namespace) ||
            (this.alias && type.startsWith(this.alias)));
    }
    get entitySets() {
        return this.containers.reduce((acc, container) => [...acc, ...container.entitySets], []);
    }
    //#region Find for Type
    findEnumTypeForType(type) {
        return this.enums.find((e) => e.isTypeOf(type));
    }
    findStructuredTypeForType(type) {
        return this.entities.find((e) => e.isTypeOf(type));
    }
    findCallableForType(type, bindingType) {
        const bindingStructuredType = bindingType !== undefined
            ? this.api.findStructuredTypeForType(bindingType)
            : undefined;
        return this.callables.find((c) => {
            const isCallableType = c.isTypeOf(type);
            const callableBindingType = c.binding()?.type;
            const callableBindingStructuredType = callableBindingType !== undefined
                ? this.api.findStructuredTypeForType(callableBindingType)
                : undefined;
            return (isCallableType &&
                (!bindingStructuredType ||
                    (callableBindingStructuredType &&
                        bindingStructuredType.isSubtypeOf(callableBindingStructuredType))));
        });
    }
    findEntitySetForType(type) {
        return this.entitySets.find((e) => e.isTypeOf(type));
    }
    //#endregion
    configure({ options, parserForType, findOptionsForType, }) {
        // Configure Enums
        this.enums.forEach((enu) => enu.configure({ options, parserForType, findOptionsForType }));
        // Configure Entities
        this.entities.forEach((config) => config.configure({ options, parserForType, findOptionsForType }));
        // Configure callables
        this.callables.forEach((callable) => callable.configure({ options, parserForType, findOptionsForType }));
    }
}

class ODataResource {
    constructor(api, { segments, query, schema, } = {}) {
        this.api = api;
        this.pathSegments = segments || new ODataPathSegments();
        this.queryOptions = query || new ODataQueryOptions();
        this.schema = schema;
    }
    /**
     * @returns string The type of the resource
     */
    type() {
        return this.pathSegments.last()?.type();
    }
    /**
     * @returns string The type of the return
     */
    returnType() {
        return this.pathSegments.last()?.type();
    }
    /**
     * @returns string All covered types of the resource
     */
    types() {
        return this.pathSegments.types();
    }
    /**
     * @returns boolean The resource has key ?
     */
    hasKey() {
        return Boolean(this.pathSegments.last({ key: true })?.hasKey());
    }
    hasEntityKey() {
        return Boolean(this.pathSegments.get(PathSegmentNames.entitySet)?.hasKey());
    }
    clearKey() {
        return this.pathSegments.last({ key: true })?.clearKey();
    }
    //#region Models
    asModel(entity, { annots, reset } = {}) {
        let resource = this;
        const type = annots?.type || this.returnType();
        if (type === undefined)
            throw Error('');
        const Model = this.api.modelForType(type);
        let entitySet = annots?.entitySet;
        if (entitySet !== undefined) {
            resource = this.api.entitySet(entitySet).entity(entity);
            resource.query((q) => q.apply(this.queryOptions.toQueryArguments()));
        }
        return new Model(entity, { resource, annots, reset });
    }
    asCollection(entities, { annots, reset, } = {}) {
        let resource = this;
        const type = annots?.type || this.returnType();
        if (type === undefined)
            throw Error('');
        const Collection = this.api.collectionForType(type);
        let path = annots?.entitySet;
        if (path !== undefined) {
            resource = this.api.entitySet(path);
            resource.query((q) => q.apply(this.queryOptions.toQueryArguments()));
        }
        return new Collection(entities, { resource, annots, reset });
    }
    //#endregion
    isSubtypeOf(other) {
        return (this.schema !== undefined &&
            other.schema !== undefined &&
            this.schema?.isSubtypeOf(other.schema));
    }
    isSupertypeOf(other) {
        return (this.schema !== undefined &&
            other.schema !== undefined &&
            this.schema?.isSupertypeOf(other.schema));
    }
    isEqualTo(other, test) {
        const [selfPath, selfParams] = this.pathAndParams();
        const [otherPath, otherParams] = other.pathAndParams();
        return test === 'path'
            ? otherPath === selfPath
            : test === 'params'
                ? Types.isEqual(selfParams, otherParams)
                : otherPath === selfPath && Types.isEqual(selfParams, otherParams);
    }
    pathAndParams(escape = false) {
        const [spath, sparams] = this.pathSegments.pathAndParams(escape);
        const [, qparams] = this.queryOptions.pathAndParams(escape);
        return [spath, { ...sparams, ...qparams }];
    }
    endpointUrl(params = true) {
        if (params) {
            return `${this.api.serviceRootUrl}${this}`;
        }
        else {
            let [path] = this.pathAndParams();
            return `${this.api.serviceRootUrl}${path}`;
        }
    }
    toString() {
        let [path, params] = this.pathAndParams();
        let queryString = Object.entries(params)
            .map((e) => `${e[0]}${VALUE_SEPARATOR}${e[1]}`)
            .join(PARAM_SEPARATOR);
        return queryString ? `${path}${QUERY_SEPARATOR}${queryString}` : path;
    }
    clone() {
        const Ctor = this.constructor;
        return new Ctor(this.api, {
            schema: this.schema,
            segments: this.cloneSegments(),
            query: this.cloneQuery(),
        });
    }
    __parser(value, options, type) {
        const dataType = options !== undefined && Types.isPlainObject(value)
            ? ODataHelper[options.version || DEFAULT_VERSION].type(value)
            : undefined;
        if (dataType !== undefined) {
            // Parser from data type
            return this.api.parserForType(dataType);
        }
        else if (this.schema !== undefined && 'parser' in this.schema) {
            // Parser from resource schema
            return this.schema.parser;
        }
        else if (type !== undefined) {
            // Parser from resource type
            return this.api.parserForType(type);
        }
        return undefined;
    }
    deserialize(value, options) {
        const resourceType = this.returnType();
        const _d = (value, options) => {
            const parser = this.__parser(value, options, resourceType);
            return parser !== undefined && 'deserialize' in parser
                ? parser.deserialize(value, options)
                : value;
        };
        return Array.isArray(value)
            ? value.map((v) => _d(v, options))
            : _d(value, options);
    }
    serialize(value, options) {
        const resourceType = this.type();
        const _s = (value, options) => {
            const parser = this.__parser(value, options, resourceType);
            return parser !== undefined && 'serialize' in parser
                ? parser.serialize(value, options)
                : value;
        };
        return Array.isArray(value)
            ? value.map((v) => _s(v, options))
            : _s(value, options);
    }
    encode(value, options) {
        const resourceType = this.type();
        const _e = (value, options) => {
            const parser = this.__parser(value, options, resourceType);
            return parser !== undefined && 'encode' in parser
                ? parser.encode(value, options)
                : value;
        };
        return Array.isArray(value)
            ? value.map((v) => _e(v, options))
            : _e(value, options);
    }
    toJSON() {
        return {
            segments: this.pathSegments.toJSON(),
            options: this.queryOptions.toJSON(),
        };
    }
    cloneSegments() {
        return this.pathSegments.clone();
    }
    //#region Query Options
    clearQuery() {
        this.queryOptions.clear();
    }
    cloneQuery() {
        return this.queryOptions.clone();
    }
    /**
     * Handle the path segments of the resource
     * Create an object handler for mutate the path segments of the resource
     * @param f Function context for handle the segments
     * @returns ODataActionResource
     */
    segment(f) {
        /*
        const type = this.type();
        const schema = type ? this.api.findStructuredTypeForType<T>(type) : undefined;
        */
        f(new ODataPathSegmentsHandler(this.pathSegments), this.schema instanceof ODataStructuredType ? this.schema : undefined);
        return this;
    }
    /**
     * Handle the query options of the resource
     * Create an object handler for mutate the query options of the resource
     * @param f Function context for handle the query options
     */
    query(f) {
        /*
        const type = this.returnType();
        const schema = type ? this.api.findStructuredTypeForType<T>(type) : undefined;
        */
        f(new ODataQueryOptionsHandler(this.queryOptions), this.schema instanceof ODataStructuredType ? this.schema : undefined);
        return this;
    }
    static resolveKey(value, schema) {
        if (isQueryCustomType(value)) {
            return value;
        }
        else if (Types.isPlainObject(value)) {
            return schema instanceof ODataStructuredType
                ? schema.resolveKey(value)
                : Objects.resolveKey(value);
        }
        return value;
    }
    resolveKey(value) {
        return ODataResource.resolveKey(value, this.schema);
    }
    //#endregion
    get(options = {}) {
        return this.api.request('GET', this, options);
    }
    post(body, options = {}) {
        return this.api.request('POST', this, { body, ...options });
    }
    put(body, options = {}) {
        return this.api.request('PUT', this, { body, ...options });
    }
    patch(body, options = {}) {
        return this.api.request('PATCH', this, { body, ...options });
    }
    delete(options = {}) {
        return this.api.request('DELETE', this, options);
    }
}

class ODataActionResource extends ODataResource {
    //#region Factory
    static factory(api, { path, schema, segments, query, }) {
        segments = segments || new ODataPathSegments();
        path = schema !== undefined ? schema.path() : path;
        if (path === undefined)
            throw new Error(`ODataActionResource: path is required`);
        const segment = segments.add(PathSegmentNames.action, path);
        if (schema !== undefined)
            segment.type(schema.type());
        return new ODataActionResource(api, {
            segments,
            query,
            schema,
        });
    }
    static fromResource(resource, path) {
        const baseType = resource.type();
        const actionSchema = resource.api.findCallableForType(path, baseType);
        const bindingType = actionSchema?.binding()?.type;
        const action = ODataActionResource.factory(resource.api, {
            path,
            schema: actionSchema,
            segments: resource.cloneSegments(),
        });
        // Switch entitySet to binding type if available
        if (bindingType !== undefined && bindingType !== baseType) {
            let entitySet = resource.api.findEntitySetForType(bindingType);
            if (entitySet !== undefined) {
                action.segment((s) => s.entitySet().path(entitySet.name));
            }
        }
        return action;
    }
    clone() {
        return super.clone();
    }
    //#endregion
    returnType() {
        return this.schema instanceof ODataCallable
            ? this.schema.parser.return?.type
            : undefined;
    }
    //#region Requests
    post(params, options) {
        return super.post(params, options);
    }
    call(params, options = {}) {
        return this.clone().post(params, options);
    }
    /**
     * Execute the action and return the result as a property
     * @param params Parameters for the action
     * @param options Options for the request
     * @returns Observable of the result of the action
     */
    callProperty(params, options = {}) {
        return this.call(params, { responseType: 'property', ...options }).pipe(map(({ property }) => property));
    }
    /**
     * Execute the action and return the result as a entity
     * @param params Parameters for the action
     * @param options Options for the request
     * @returns Observable of the result of the action
     */
    callEntity(params, options = {}) {
        return this.call(params, { responseType: 'entity', ...options }).pipe(map(({ entity }) => entity));
    }
    /**
     * Execute the action and return the result as a model
     * @param params Parameters for the action
     * @param options Options for the request
     * @returns Observable of the result of the action
     */
    callModel(params, options = {}) {
        return this.call(params, { responseType: 'entity', ...options }).pipe(map(({ entity, annots }) => entity ? this.asModel(entity, { annots, reset: true }) : null));
    }
    /**
     * Execute the action and return the result as a entities
     * @param params Parameters for the action
     * @param options Options for the request
     * @returns Observable of the result of the action
     */
    callEntities(params, options = {}) {
        return this.call(params, { responseType: 'entities', ...options }).pipe(map(({ entities }) => entities));
    }
    /**
     * Execute the action and return the result as a collection
     * @param params Parameters for the action
     * @param options Options for the request
     * @returns Observable of the result of the action
     */
    callCollection(params, options = {}) {
        return this.call(params, { responseType: 'entities', ...options }).pipe(map(({ entities, annots }) => entities
            ? this.asCollection(entities, { annots, reset: true })
            : null));
    }
}

const Arrays = {
    // Zip arrays
    // Example
    //   Arrays.zip([1, 2, 3, 4, 5, 6], ['a', 'b', 'c', 'd', 'e', 'f'])
    //   => [[1, 'a'], [2, 'b'], [3, 'c'], [4, 'd'], [5, 'e'], [6, 'f']]
    zip: (...arrays) => {
        return arrays[0].map((_, i) => arrays.map((array) => array[i]));
    },
};

class ODataBatchRequest extends Subject {
    constructor(request) {
        super();
        this.request = request;
    }
    toString() {
        //TODO: Relative or Absolute url ?
        let res = [
            `${this.request.method} ${this.request.pathWithParams} ${HTTP11}`,
        ];
        if (this.request.method === 'POST' ||
            this.request.method === 'PATCH' ||
            this.request.method === 'PUT') {
            res.push(`${CONTENT_TYPE}: ${APPLICATION_JSON}`);
        }
        if (this.request.headers instanceof HttpHeaders) {
            let headers = this.request.headers;
            res = [
                ...res,
                ...headers
                    .keys()
                    .map((key) => `${key}: ${(headers.getAll(key) || []).join(',')}`),
            ];
        }
        return res.join(NEWLINE);
    }
    onLoad(response) {
        if (response.ok) {
            this.next(response);
            this.complete();
        }
        else {
            // An unsuccessful request is delivered on the error channel.
            this.error(response);
        }
    }
    onError(response) {
        this.error(response);
    }
}
/**
 * OData Batch Resource
 * https://www.odata.org/getting-started/advanced-tutorial/#batch
 */
class ODataBatchResource extends ODataResource {
    constructor() {
        super(...arguments);
        // VARIABLES
        this._requests = [];
        this._responses = null;
    }
    requests() {
        return this._requests.map((r) => r.request);
    }
    responses() {
        return this._responses;
    }
    //#region Factory
    static factory(api) {
        let segments = new ODataPathSegments();
        segments.add(PathSegmentNames.batch, $BATCH);
        return new ODataBatchResource(api, { segments });
    }
    clone() {
        const batch = super.clone();
        batch._requests = [...this._requests];
        return batch;
    }
    //#endregion
    storeRequester() {
        const current = this.api.requester;
        // Switch to the batch requester
        this.api.requester = (req) => {
            if (req.api !== this.api)
                throw new Error('Batch Request are for the same api.');
            if (req.observe === 'events')
                throw new Error("Batch Request does not allows observe == 'events'.");
            this._requests.push(new ODataBatchRequest(req));
            return this._requests[this._requests.length - 1];
        };
        return current;
    }
    restoreRequester(handler) {
        this.api.requester = handler;
    }
    /**
     * Add to batch request
     * @param ctx The context for the request
     * @returns The result of execute the context
     */
    add(ctx) {
        // Store original requester
        var handler = this.storeRequester();
        // Execute the context
        const obs$ = ctx(this);
        // Restore original requester
        this.restoreRequester(handler);
        return obs$;
    }
    send(options) {
        if (this._requests.length == 0) {
            return of(null);
        }
        const bound = Strings.uniqueId(BATCH_PREFIX);
        const headers = Http.mergeHttpHeaders((options && options.headers) || {}, {
            [ODATA_VERSION]: VERSION_4_0,
            [CONTENT_TYPE]: MULTIPART_MIXED_BOUNDARY + bound,
            [ACCEPT]: MULTIPART_MIXED,
        });
        return this.api
            .request('POST', this, {
            body: ODataBatchResource.buildBody(bound, this._requests),
            responseType: 'text',
            observe: 'response',
            headers: headers,
            params: options ? options.params : undefined,
            withCredentials: options ? options.withCredentials : undefined,
        })
            .pipe(map$1((response) => {
            if (this._responses == null) {
                this._responses = [];
            }
            this._responses = [
                ...this._responses,
                ...ODataBatchResource.parseResponse(this._requests, response),
            ];
            Arrays.zip(this._requests, this._responses).forEach((tuple) => {
                if (!tuple[0].isStopped)
                    tuple[0].onLoad(tuple[1]);
            });
            return response;
        }));
    }
    /**
     * Execute the batch request
     * @param ctx The context for the request
     * @param options The options of the batch request
     * @returns The result of execute the context
     */
    exec(ctx, options) {
        let ctx$ = this.add(ctx);
        let send$ = this.send(options);
        firstValueFrom(send$);
        return ctx$;
        //return this.send(options).pipe(switchMap(() => ctx$));
    }
    body() {
        return ODataBatchResource.buildBody(Strings.uniqueId(BATCH_PREFIX), this._requests);
    }
    static buildBody(batchBoundary, requests) {
        let res = [];
        let changesetBoundary = null;
        let changesetId = 1;
        for (const batch of requests) {
            // if method is GET and there is a changeset boundary open then close it
            if (batch.request.method === 'GET' && changesetBoundary !== null) {
                res.push(`${BOUNDARY_PREFIX_SUFFIX}${changesetBoundary}${BOUNDARY_PREFIX_SUFFIX}`);
                changesetBoundary = null;
            }
            // if there is no changeset boundary open then open a batch boundary
            if (changesetBoundary === null) {
                res.push(`${BOUNDARY_PREFIX_SUFFIX}${batchBoundary}`);
            }
            // if method is not GET and there is no changeset boundary open then open a changeset boundary
            if (batch.request.method !== 'GET') {
                if (changesetBoundary === null) {
                    changesetBoundary = Strings.uniqueId(CHANGESET_PREFIX);
                    res.push(`${CONTENT_TYPE}: ${MULTIPART_MIXED_BOUNDARY}${changesetBoundary}`);
                    res.push(NEWLINE);
                }
                res.push(`${BOUNDARY_PREFIX_SUFFIX}${changesetBoundary}`);
            }
            res.push(`${CONTENT_TYPE}: ${APPLICATION_HTTP}`);
            res.push(`${CONTENT_TRANSFER_ENCODING}: ${BINARY}`);
            if (batch.request.method !== 'GET') {
                res.push(`${CONTENT_ID}: ${changesetId++}`);
            }
            res.push(NEWLINE);
            res.push(`${batch}`);
            if (batch.request.method === 'GET' || batch.request.method === 'DELETE') {
                res.push(NEWLINE);
            }
            else {
                res.push(`${NEWLINE}${JSON.stringify(batch.request.body)}`);
            }
        }
        if (res.length) {
            if (changesetBoundary !== null) {
                res.push(`${BOUNDARY_PREFIX_SUFFIX}${changesetBoundary}${BOUNDARY_PREFIX_SUFFIX}`);
                changesetBoundary = null;
            }
            res.push(`${BOUNDARY_PREFIX_SUFFIX}${batchBoundary}${BOUNDARY_PREFIX_SUFFIX}`);
        }
        return res.join(NEWLINE);
    }
    static parseResponse(requests, response) {
        let chunks = [];
        const contentType = response.headers.get(CONTENT_TYPE) || '';
        const batchBoundary = Http.boundaryDelimiter(contentType);
        const endLine = Http.boundaryEnd(batchBoundary);
        const lines = (response.body || '').split(NEWLINE_REGEXP);
        let changesetResponses = null;
        let contentId = null;
        let changesetBoundary = null;
        let changesetEndLine = null;
        let startIndex = null;
        for (let index = 0; index < lines.length; index++) {
            const line = lines[index];
            if (line.startsWith(CONTENT_TYPE)) {
                const contentTypeValue = Http.headerValue(line);
                if (contentTypeValue === MULTIPART_MIXED) {
                    changesetResponses = [];
                    contentId = null;
                    changesetBoundary = Http.boundaryDelimiter(line);
                    changesetEndLine = Http.boundaryEnd(changesetBoundary);
                    startIndex = null;
                }
                continue;
            }
            else if (changesetResponses !== null && line.startsWith(CONTENT_ID)) {
                contentId = Number(Http.headerValue(line));
            }
            else if (line.startsWith(HTTP11)) {
                startIndex = index;
            }
            else if (line === batchBoundary ||
                line === changesetBoundary ||
                line === endLine ||
                line === changesetEndLine) {
                if (!startIndex) {
                    continue;
                }
                const chunk = lines.slice(startIndex, index);
                if (changesetResponses !== null && contentId !== null) {
                    changesetResponses[contentId] = chunk;
                }
                else {
                    chunks.push(chunk);
                }
                if (line === batchBoundary || line === changesetBoundary) {
                    startIndex = index + 1;
                }
                else if (line === endLine || line === changesetEndLine) {
                    if (changesetResponses !== null) {
                        for (const response of changesetResponses) {
                            if (response) {
                                chunks.push(response);
                            }
                        }
                    }
                    changesetResponses = null;
                    changesetBoundary = null;
                    changesetEndLine = null;
                    startIndex = null;
                }
            }
        }
        return chunks.map((chunk, index) => {
            let request = requests[index].request;
            let { code, message } = Http.parseResponseStatus(chunk[0]);
            chunk = chunk.slice(1);
            let headers = new HttpHeaders();
            var index = 1;
            for (; index < chunk.length; index++) {
                const batchBodyLine = chunk[index];
                if (batchBodyLine === '') {
                    break;
                }
                const batchBodyLineParts = batchBodyLine.split(': ');
                headers = headers.append(batchBodyLineParts[0].trim(), batchBodyLineParts[1].trim());
            }
            let body = '';
            for (; index < chunk.length; index++) {
                body += chunk[index];
            }
            if (code === 0) {
                code = !!body ? 200 : 0;
            }
            let ok = code >= 200 && code < 300;
            if (request.responseType === 'json' && typeof body === 'string') {
                const originalBody = body;
                body = body.replace(XSSI_PREFIX, '');
                try {
                    body = body !== '' ? JSON.parse(body) : null;
                }
                catch (error) {
                    body = originalBody;
                    if (ok) {
                        ok = false;
                        body = { error, text: body };
                    }
                }
            }
            return ok
                ? new HttpResponse({
                    body,
                    headers,
                    status: code,
                    statusText: message,
                    url: request.urlWithParams,
                })
                : new HttpErrorResponse({
                    // The error in this case is the response body (error from the server).
                    error: body,
                    headers,
                    status: code,
                    statusText: message,
                    url: request.urlWithParams,
                });
        });
    }
}

class ODataCountResource extends ODataResource {
    //#region Factory
    static factory(api, { segments, query, }) {
        segments.add(PathSegmentNames.count, $COUNT).type('Edm.Int32');
        query?.keep(QueryOptionNames.filter, QueryOptionNames.search);
        return new ODataCountResource(api, { segments, query });
    }
    clone() {
        return super.clone();
    }
    //#endregion
    //#region Requests
    get(options) {
        return super.get({ responseType: 'value', ...options });
    }
    //#endregion
    //#region Shortcuts
    /**
     * Fetch the count of the set.
     * @param options Options for the request
     * @returns The count of the set
     */
    fetch(options) {
        return this.get(options);
    }
}

class ODataFunctionResource extends ODataResource {
    //#region Factory
    static factory(api, { path, schema, segments, query, }) {
        segments = segments || new ODataPathSegments();
        path = schema !== undefined ? schema.path() : path;
        if (path === undefined)
            throw new Error(`ODataActionResource: path is required`);
        const segment = segments.add(PathSegmentNames.function, path);
        if (schema !== undefined)
            segment.type(schema.type());
        return new ODataFunctionResource(api, {
            segments,
            query,
            schema,
        });
    }
    static fromResource(resource, path) {
        const baseType = resource.type();
        const schema = resource.api.findCallableForType(path, baseType);
        const bindingType = schema?.binding()?.type;
        const func = ODataFunctionResource.factory(resource.api, {
            path,
            schema,
            segments: resource.cloneSegments(),
        });
        // Switch entitySet to binding type if available
        if (bindingType !== undefined && bindingType !== baseType) {
            let entitySet = resource.api.findEntitySetForType(bindingType);
            if (entitySet !== undefined) {
                func.segment((s) => s.entitySet().path(entitySet.name));
            }
        }
        return func;
    }
    clone() {
        return super.clone();
    }
    //#endregion
    returnType() {
        return this.schema instanceof ODataCallable
            ? this.schema.parser.return?.type
            : undefined;
    }
    pathAndParams(escape = false) {
        let [path, params] = super.pathAndParams(escape);
        if (path.endsWith('()') &&
            this.api.options.nonParenthesisForEmptyParameterFunction) {
            path = path.substring(0, path.length - 2);
        }
        return [path, params];
    }
    parameters(params, { alias } = {}) {
        let parameters = params !== null ? this.encode(params) : null;
        if (alias && parameters !== null) {
            this.query((q) => {
                parameters = Object.entries(parameters).reduce((acc, [name, param]) => {
                    return Object.assign(acc, { [name]: q.alias(param, name) });
                }, {});
            });
        }
        return this.clone().segment((s) => s.function().parameters(parameters));
    }
    //#region Requests
    get(options) {
        return super.get(options);
    }
    call(params, { alias, ...options } = {}) {
        return this.parameters(params, { alias }).get(options);
    }
    /**
     * Execute the function with the given parameters and return the result as a property
     * @param params Parameters to be sent to the function
     * @param alias If true, the parameters will be send using aliases
     * @param options Options for the request
     * @returns Observable of the result of the function
     */
    callProperty(params, { alias, ...options } = {}) {
        return this.call(params, {
            responseType: 'property',
            alias,
            ...options,
        }).pipe(map(({ property }) => property));
    }
    /**
     * Execute the function with the given parameters and return the result as a entity
     * @param params Parameters to be sent to the function
     * @param alias If true, the parameters will be send using aliases
     * @param options Options for the request
     * @returns Observable of the result of the function
     */
    callEntity(params, { alias, ...options } = {}) {
        return this.call(params, {
            responseType: 'entity',
            alias,
            ...options,
        }).pipe(map(({ entity }) => entity));
    }
    /**
     * Execute the function with the given parameters and return the result as a model
     * @param params Parameters to be sent to the function
     * @param alias If true, the parameters will be send using aliases
     * @param options Options for the request
     * @returns Observable of the result of the function
     */
    callModel(params, { alias, ...options } = {}) {
        return this.call(params, {
            responseType: 'entity',
            alias,
            ...options,
        }).pipe(map(({ entity, annots }) => entity ? this.asModel(entity, { annots, reset: true }) : null));
    }
    /**
     * Execute the function with the given parameters and return the result as a entities
     * @param params Parameters to be sent to the function
     * @param alias If true, the parameters will be send using aliases
     * @param options Options for the request
     * @returns Observable of the result of the function
     */
    callEntities(params, { alias, ...options } = {}) {
        return this.call(params, {
            responseType: 'entities',
            alias,
            ...options,
        }).pipe(map(({ entities }) => entities));
    }
    /**
     * Execute the function with the given parameters and return the result as a collection
     * @param params Parameters to be sent to the function
     * @param alias If true, the parameters will be send using aliases
     * @param options Options for the request
     * @returns Observable of the result of the function
     */
    callCollection(params, { alias, ...options } = {}) {
        return this.call(params, {
            responseType: 'entities',
            alias,
            ...options,
        }).pipe(map(({ entities, annots }) => entities
            ? this.asCollection(entities, { annots, reset: true })
            : null));
    }
}

class ODataMediaResource extends ODataResource {
    //#region Factory
    static factory(api, { segments, query, }) {
        segments.add(PathSegmentNames.value, $VALUE);
        return new ODataMediaResource(api, { segments, query });
    }
    clone() {
        return super.clone();
    }
    //#endregion
    //#region Requests
    get(options) {
        return super.get(options);
    }
    put(data, options = {}) {
        return super.put(data, options);
    }
    fetch(options) {
        return this.get(options);
    }
    fetchArraybuffer(options = {}) {
        return this.fetch({ responseType: 'arraybuffer', ...options });
    }
    fetchBlob(options = {}) {
        return this.fetch({ responseType: 'blob', ...options });
    }
    upload(data, options = {}) {
        return this.put(data, options);
    }
    uploadArrayBuffer(data, contentType, options = {}) {
        options.headers = Http.mergeHttpHeaders(options.headers || {}, {
            'Content-Type': contentType,
        });
        return this.upload(data, options);
    }
    uploadBlob(data, options = {}) {
        return this.upload(data, options);
    }
}

class ODataValueResource extends ODataResource {
    //#region Factory
    static factory(api, { type, schema, segments, query, }) {
        const baseType = type;
        const bindingType = schema?.type();
        const segment = segments.add(PathSegmentNames.value, $VALUE);
        if (schema !== undefined)
            segment.type(schema.type());
        else if (type !== undefined)
            segment.type(type);
        query?.clear();
        const value = new ODataValueResource(api, { segments, query, schema });
        // Switch entitySet to binding type if available
        if (bindingType !== undefined && bindingType !== baseType) {
            let entitySet = api.findEntitySetForType(bindingType);
            if (entitySet !== undefined) {
                value.segment((s) => s.entitySet().path(entitySet.name));
            }
        }
        return value;
    }
    clone() {
        return super.clone();
    }
    //#endregion
    //#region Requests
    get(options = {}) {
        return super.get({ responseType: 'value', ...options });
    }
    //#endregion
    //#region Shortcuts
    /**
     * Fetch the value of the resource.
     * @param options OData options.
     * @returns Observable of the value.
     */
    fetch(options) {
        return this.get(options);
    }
}

class ODataPropertyResource extends ODataResource {
    //#region Factory
    static factory(api, { path, type, schema, segments, query, }) {
        const segment = segments.add(PathSegmentNames.property, path);
        if (schema !== undefined)
            segment.type(schema.type());
        else if (type !== undefined)
            segment.type(type);
        query?.clear();
        return new ODataPropertyResource(api, {
            segments,
            query,
            schema,
        });
    }
    static fromResource(resource, path) {
        const baseType = resource.type();
        let baseSchema = resource.schema;
        let fieldType;
        let fieldSchema;
        if (baseSchema !== undefined) {
            const field = baseSchema.field(path);
            fieldType = field?.type;
            fieldSchema =
                fieldType !== undefined
                    ? resource.api.findStructuredTypeForType(fieldType)
                    : undefined;
            baseSchema =
                field !== undefined
                    ? baseSchema.findSchemaForField(field)
                    : undefined;
        }
        const property = ODataPropertyResource.factory(resource.api, {
            path,
            type: fieldType,
            schema: fieldSchema,
            segments: resource.cloneSegments(),
            query: resource.cloneQuery(),
        });
        // Switch entitySet to binding type if available
        if (baseSchema !== undefined && baseSchema.type() !== baseType) {
            let entitySet = resource.api.findEntitySetForType(baseSchema.type());
            if (entitySet !== undefined) {
                property.segment((s) => s.entitySet().path(entitySet.name));
            }
        }
        return property;
    }
    clone() {
        return super.clone();
    }
    //#endregion
    key(value) {
        const property = this.clone();
        var key = this.resolveKey(value);
        if (key !== undefined)
            property.segment((s) => s.property().key(key));
        return property;
    }
    keys(values) {
        const property = this.clone();
        const types = this.pathSegments.types({ key: true });
        const keys = values.map((value, index) => ODataResource.resolveKey(value, this.api.findStructuredTypeForType(types[index])));
        property.segment((s) => s.keys(keys));
        return property;
    }
    value() {
        return ODataValueResource.factory(this.api, {
            type: this.returnType(),
            schema: this.schema,
            segments: this.cloneSegments(),
            query: this.cloneQuery(),
        });
    }
    /*
    navigationProperty<N>(path: string) {
      let schema: ODataStructuredType<N> | undefined;
      if (this.schema instanceof ODataStructuredType) {
        const field = this.schema.field<any>(path as keyof T);
        schema =
          field !== undefined
            ? this.schema.findSchemaForField<N>(field)
            : undefined;
      }
      return ODataNavigationPropertyResource.factory<N>(this.api, {
        path,
        schema,
        segments: this.cloneSegments(),
        query: this.cloneQuery<N>(),
      });
    }
    */
    property(path) {
        let type;
        let schema;
        if (this.schema instanceof ODataStructuredType) {
            const field = this.schema.field(path);
            type = field?.type;
            schema =
                field !== undefined
                    ? this.schema.findSchemaForField(field)
                    : undefined;
        }
        return ODataPropertyResource.factory(this.api, {
            path,
            type,
            schema,
            segments: this.cloneSegments(),
            query: this.cloneQuery(),
        });
    }
    //#region Requests
    get(options) {
        return super.get(options);
    }
    fetch(options = {}) {
        return this.get(options);
    }
    /**
     * Fetch the property value
     * @param options Options for the request
     * @returns The property value
     */
    fetchProperty(options = {}) {
        return this.fetch({ responseType: 'property', ...options }).pipe(map(({ property }) => property));
    }
    /**
     * Fetch the entity
     * @param options Options for the request
     * @returns The entity
     */
    fetchEntity(options = {}) {
        return this.fetch({ responseType: 'entity', ...options }).pipe(map(({ entity }) => entity));
    }
    /**
     * Fetch the entity and return as model
     * @param options Options for the request
     * @returns The model
     */
    fetchModel(options = {}) {
        return this.fetch({ responseType: 'entity', ...options }).pipe(map(({ entity, annots }) => entity ? this.asModel(entity, { annots, reset: true }) : null));
    }
    /**
     * Fetch the entities
     * @param options Options for the request
     * @returns The entities
     */
    fetchEntities(options = {}) {
        return this.fetch({ responseType: 'entities', ...options }).pipe(map(({ entities }) => entities));
    }
    /**
     * Fetch the entities and return as collection
     * @param options Options for the request
     * @returns The collection
     */
    fetchCollection(options = {}) {
        return this.fetch({ responseType: 'entities', ...options }).pipe(map(({ entities, annots }) => entities
            ? this.asCollection(entities, { annots, reset: true })
            : null));
    }
    fetchOne(options) {
        let res = this.clone();
        res.query((q) => q.top(1));
        return res.fetch({ responseType: 'entities', ...options }).pipe(map(({ entities, annots }) => ({ entity: entities !== null ? entities[0] || null : null, annots })));
    }
    fetchMany(top, options) {
        let res = this.clone();
        let fetch = (opts) => {
            if (opts) {
                res.query((q) => q.paging(opts));
            }
            return res.fetch({ responseType: 'entities', ...options });
        };
        return fetch({ top }).pipe(expand(({ annots }) => annots.skip || annots.skiptoken ? fetch(annots) : EMPTY), map(({ entities, annots }) => ({ entities: entities || [], annots })), reduce((acc, { entities, annots }) => ({
            entities: [...(acc.entities || []), ...(entities || [])],
            annots: acc.annots.union(annots)
        })));
    }
    /**
     * Fetch all entities
     * @param options Options for the request
     * @returns All entities
     */
    fetchAll(options = {}) {
        let res = this.clone();
        // Clean Paging
        res.query((q) => q.clearPaging());
        let fetch = (opts) => {
            if (opts) {
                res.query((q) => q.paging(opts));
            }
            return res.fetch({ responseType: 'entities', ...options });
        };
        return fetch().pipe(expand(({ annots }) => annots.skip || annots.skiptoken ? fetch(annots) : EMPTY), map(({ entities, annots }) => ({ entities: entities || [], annots })), reduce((acc, { entities, annots }) => ({
            entities: [...(acc.entities || []), ...(entities || [])],
            annots: acc.annots.union(annots)
        })));
    }
}

class ODataReferenceResource extends ODataResource {
    //#region Factory
    static factory(api, { segments, query, }) {
        segments.add(PathSegmentNames.reference, $REF);
        query?.clear();
        return new ODataReferenceResource(api, { segments, query });
    }
    clone() {
        return super.clone();
    }
    //#endregion
    //#region Requests
    post(target, options) {
        return super.post({ [ODATA_ID]: target.endpointUrl(false) }, options);
    }
    put(target, options) {
        return super.put({ [ODATA_ID]: target.endpointUrl(false) }, options);
    }
    delete({ etag, target, ...options } = {}) {
        if (target) {
            options.params = { [$ID]: target.endpointUrl(false) };
        }
        return super.delete({ etag, ...options });
    }
    //#endregion
    //#region Shortcuts for collections
    /**
     * Add the given target to the collection.
     * @param target The target resource
     * @param options Options for the request
     * @returns Observable of the response
     */
    add(target, options) {
        return this.post(target, options);
    }
    /**
     * Remove the given target from the collection.
     * @param target The target resource
     * @param options Options for the request
     * @returns Observable of the response
     */
    remove(target, options) {
        return this.delete({ target, ...options });
    }
    //#region
    //#region Shortcuts for single
    /**
     * Set the reference to the given target.
     * @param target The target resource
     * @param options Options for the request
     * @returns Observable of the response
     */
    set(target, options) {
        return this.put(target, options);
    }
    /**
     * Unset the reference to the given target.
     * @param options Options for the request.
     * @returns Observable of the response
     */
    unset(options) {
        return this.delete(options);
    }
    fetch(options = {}) {
        return this.get(options);
    }
    /**
     * Fetch the entity
     * @param options Options for the request
     * @returns The entity
     */
    fetchEntity(options = {}) {
        return this.fetch({ responseType: 'entity', ...options }).pipe(map(({ entity }) => entity));
    }
    /**
     * Fetch entities
     * @param options Options for the request
     * @returns The entities
     */
    fetchEntities(options = {}) {
        return this.fetch({ responseType: 'entities', ...options }).pipe(map(({ entities }) => entities));
    }
}

/**
 * OData Navigation Property Resource
 * https://www.odata.org/getting-started/advanced-tutorial/#containment
 * https://www.odata.org/getting-started/advanced-tutorial/#derived
 */
class ODataNavigationPropertyResource extends ODataResource {
    //#region Factory
    static factory(api, { path, type, schema, segments, query, }) {
        const segment = segments.add(PathSegmentNames.navigationProperty, path);
        if (schema !== undefined)
            segment.type(schema.type());
        else if (type !== undefined)
            segment.type(type);
        query?.keep(QueryOptionNames.format);
        return new ODataNavigationPropertyResource(api, {
            segments,
            query,
            schema,
        });
    }
    static fromResource(resource, path) {
        const baseType = resource.type();
        let baseSchema = resource.schema;
        let fieldType;
        let fieldSchema;
        if (baseSchema !== undefined) {
            const field = baseSchema.field(path);
            fieldType = field?.type;
            fieldSchema =
                fieldType !== undefined
                    ? resource.api.findStructuredTypeForType(fieldType)
                    : undefined;
            baseSchema =
                field !== undefined
                    ? baseSchema.findSchemaForField(field)
                    : undefined;
        }
        const navigation = ODataNavigationPropertyResource.factory(resource.api, {
            path,
            type: fieldType,
            schema: fieldSchema,
            segments: resource.cloneSegments(),
            query: resource.cloneQuery(),
        });
        // Switch entitySet to binding type if available
        if (baseSchema !== undefined && baseSchema.type() !== baseType) {
            let entitySet = resource.api.findEntitySetForType(baseSchema.type());
            if (entitySet !== undefined) {
                navigation.segment((s) => s.entitySet().path(entitySet.name));
            }
        }
        return navigation;
    }
    clone() {
        return super.clone();
    }
    //#endregion
    key(value) {
        const navigation = this.clone();
        var key = this.resolveKey(value);
        if (key !== undefined)
            navigation.segment((s) => s.navigationProperty().key(key));
        return navigation;
    }
    keys(values) {
        const navigation = this.clone();
        const types = this.pathSegments.types({ key: true });
        const keys = values.map((value, index) => ODataResource.resolveKey(value, this.api.findStructuredTypeForType(types[index])));
        navigation.segment((s) => s.keys(keys));
        return navigation;
    }
    media() {
        return ODataMediaResource.factory(this.api, {
            segments: this.cloneSegments(),
            query: this.cloneQuery(),
        });
    }
    reference() {
        return ODataReferenceResource.factory(this.api, {
            segments: this.cloneSegments(),
        });
    }
    navigationProperty(path) {
        return ODataNavigationPropertyResource.fromResource(this, path);
    }
    property(path) {
        return ODataPropertyResource.fromResource(this, path);
    }
    count() {
        return ODataCountResource.factory(this.api, {
            segments: this.cloneSegments(),
            query: this.cloneQuery(),
        });
    }
    cast(type) {
        const baseSchema = this.schema;
        const castSchema = this.api.findStructuredTypeForType(type);
        if (castSchema !== undefined &&
            baseSchema !== undefined &&
            !castSchema.isSubtypeOf(baseSchema))
            throw new Error(`Cannot cast to ${type}`);
        const segments = this.cloneSegments();
        segments.add(PathSegmentNames.type, type).type(type);
        return new ODataNavigationPropertyResource(this.api, {
            segments,
            schema: castSchema,
            query: this.cloneQuery(),
        });
    }
    //#region Requests
    post(attrs, options = {}) {
        return super.post(attrs, { responseType: 'entity', ...options });
    }
    put(attrs, options = {}) {
        return super.put(attrs, { responseType: 'entity', ...options });
    }
    patch(attrs, options = {}) {
        return super.patch(attrs, { responseType: 'entity', ...options });
    }
    delete(options = {}) {
        return super.delete({ responseType: 'entity', ...options });
    }
    get(options = {}) {
        return super.get(options);
    }
    //#endregion
    //#region Shortcuts
    /**
     * Create a new entity
     * @param attrs The entity attributes
     * @param options Options for the request
     * @returns The created entity with the annotations
     */
    create(attrs, options) {
        return this.post(attrs, options);
    }
    /**
     * Update an existing entity
     * @param attrs The entity attributes
     * @param options Options for the request
     * @returns The updated entity with the annotations
     */
    update(attrs, options) {
        return this.put(attrs, options);
    }
    /**
     * Modify an existing entity
     * @param attrs The entity attributes
     * @param options Options for the request
     * @returns The modified entity with the annotations
     */
    modify(attrs, options) {
        return this.patch(attrs, options);
    }
    /**
     * Delete an existing entity
     * @param options Options for the request
     * @returns An observable of the destroy
     */
    destroy(options) {
        return this.delete(options);
    }
    fetch(options = {}) {
        if (!this.hasEntityKey())
            return throwError(() => new Error('fetch: Navigation resource without entity key'));
        return this.get(options);
    }
    /**
     * Fetch the entity
     * @param options Options for the request
     * @returns The entity
     */
    fetchEntity(options = {}) {
        return this.fetch({ responseType: 'entity', ...options }).pipe(map(({ entity }) => entity));
    }
    /**
     * Fetch the entity and return as model
     * @param options Options for the request
     * @returns The model
     */
    fetchModel(options = {}) {
        return this.fetch({ responseType: 'entity', ...options }).pipe(map(({ entity, annots }) => entity ? this.asModel(entity, { annots, reset: true }) : null));
    }
    /**
     * Fetch entities
     * @param options Options for the request
     * @returns The entities
     */
    fetchEntities(options = {}) {
        return this.fetch({ responseType: 'entities', ...options }).pipe(map(({ entities }) => entities));
    }
    /**
     * Fetch entities and return as collection
     * @param options Options for the request
     * @returns The collection
     */
    fetchCollection(options = {}) {
        return this.fetch({ responseType: 'entities', ...options }).pipe(map(({ entities, annots }) => entities
            ? this.asCollection(entities, { annots, reset: true })
            : null));
    }
    fetchOne(options) {
        let res = this.clone();
        res.query((q) => q.top(1));
        return res.fetch({ responseType: 'entities', ...options }).pipe(map(({ entities, annots }) => ({ entity: entities !== null ? entities[0] || null : null, annots })));
    }
    fetchMany(top, options) {
        let res = this.clone();
        let fetch = (opts) => {
            if (opts) {
                res.query((q) => q.paging(opts));
            }
            return res.fetch({ responseType: 'entities', ...options });
        };
        return fetch({ top }).pipe(expand(({ annots }) => annots.skip || annots.skiptoken ? fetch(annots) : EMPTY), map(({ entities, annots }) => ({ entities: entities || [], annots })), reduce((acc, { entities, annots }) => ({
            entities: [...(acc.entities || []), ...(entities || [])],
            annots: acc.annots.union(annots)
        })));
    }
    /**
     * Fetch all entities
     * @param options Options for the request
     * @returns All entities
     */
    fetchAll(options = {}) {
        let res = this.clone();
        // Clean Paging
        res.query((q) => q.clearPaging());
        let fetch = (opts) => {
            if (opts) {
                res.query((q) => q.paging(opts));
            }
            return res.fetch({ responseType: 'entities', ...options });
        };
        return fetch().pipe(expand(({ annots }) => annots.skip || annots.skiptoken ? fetch(annots) : EMPTY), map(({ entities, annots }) => ({ entities: entities || [], annots })), reduce((acc, { entities, annots }) => ({
            entities: [...(acc.entities || []), ...(entities || [])],
            annots: acc.annots.union(annots)
        })));
    }
}

class ODataEntityResource extends ODataResource {
    //#region Factory
    static factory(api, { schema, segments, query, }) {
        query?.keep(QueryOptionNames.expand, QueryOptionNames.select, QueryOptionNames.format);
        return new ODataEntityResource(api, { segments, query, schema });
    }
    clone() {
        return super.clone();
    }
    //#endregion
    key(value) {
        const entity = this.clone();
        var key = this.resolveKey(value);
        if (key !== undefined)
            entity.segment((s) => s.entitySet().key(key));
        return entity;
    }
    keys(values) {
        const entity = this.clone();
        const types = this.pathSegments.types({ key: true });
        const keys = values.map((value, index) => ODataResource.resolveKey(value, this.api.findStructuredTypeForType(types[index])));
        entity.segment((s) => s.keys(keys));
        return entity;
    }
    media() {
        return ODataMediaResource.factory(this.api, {
            segments: this.cloneSegments(),
            query: this.cloneQuery(),
        });
    }
    navigationProperty(path) {
        return ODataNavigationPropertyResource.fromResource(this, path);
    }
    property(path) {
        return ODataPropertyResource.fromResource(this, path);
    }
    action(path) {
        return ODataActionResource.fromResource(this, path);
    }
    function(path) {
        return ODataFunctionResource.fromResource(this, path);
    }
    cast(type) {
        const baseSchema = this.schema;
        const castSchema = this.api.findStructuredTypeForType(type);
        if (castSchema !== undefined &&
            baseSchema !== undefined &&
            !castSchema.isSubtypeOf(baseSchema))
            throw new Error(`cast: Cannot cast to ${type}`);
        const segments = this.cloneSegments();
        segments.add(PathSegmentNames.type, type).type(type);
        return new ODataEntityResource(this.api, {
            segments,
            schema: castSchema,
            query: this.cloneQuery(),
        });
    }
    //#region Requests
    post(attrs, options = {}) {
        return super.post(attrs, { responseType: 'entity', ...options });
    }
    put(attrs, options = {}) {
        return super.put(attrs, { responseType: 'entity', ...options });
    }
    patch(attrs, options = {}) {
        return super.patch(attrs, { responseType: 'entity', ...options });
    }
    delete(options = {}) {
        return super.delete({ responseType: 'entity', ...options });
    }
    get(options = {}) {
        return super.get({ responseType: 'entity', ...options });
    }
    //#endregion
    //#region Shortcuts
    create(attrs, options) {
        return this.post(attrs, options);
    }
    update(attrs, options) {
        return this.put(attrs, options);
    }
    modify(attrs, options) {
        return this.patch(attrs, options);
    }
    destroy(options) {
        return this.delete(options);
    }
    fetch(options) {
        if (!this.hasKey())
            return throwError(() => new Error('fetch: Entity resource without key'));
        return this.get(options);
    }
    fetchEntity(options) {
        return this.fetch(options).pipe(map(({ entity }) => entity));
    }
    fetchModel(options) {
        return this.fetch(options).pipe(map(({ entity, annots }) => entity ? this.asModel(entity, { annots, reset: true }) : null));
    }
}

class ODataEntitySetResource extends ODataResource {
    //#region Factory
    static factory(api, { path, schema, query, }) {
        const segments = new ODataPathSegments();
        const segment = segments.add(PathSegmentNames.entitySet, path);
        if (schema !== undefined)
            segment.type(schema.type());
        return new ODataEntitySetResource(api, { segments, query, schema });
    }
    clone() {
        return super.clone();
    }
    //#endregion
    entity(key) {
        const entity = ODataEntityResource.factory(this.api, {
            schema: this.schema,
            segments: this.cloneSegments(),
            query: this.cloneQuery(),
        });
        if (key !== undefined) {
            return entity.key(key);
        }
        return entity;
    }
    action(path) {
        return ODataActionResource.fromResource(this, path);
    }
    function(path) {
        return ODataFunctionResource.fromResource(this, path);
    }
    count() {
        return ODataCountResource.factory(this.api, {
            segments: this.cloneSegments(),
            query: this.cloneQuery(),
        });
    }
    cast(type) {
        const baseSchema = this.schema;
        const castSchema = this.api.findStructuredTypeForType(type);
        if (castSchema !== undefined &&
            baseSchema !== undefined &&
            !castSchema.isSubtypeOf(baseSchema))
            throw new Error(`Cannot cast to ${type}`);
        const segments = this.cloneSegments();
        segments.add(PathSegmentNames.type, type).type(type);
        return new ODataEntitySetResource(this.api, {
            segments,
            schema: castSchema,
            query: this.cloneQuery(),
        });
    }
    //#region Requests
    post(attrs, options) {
        return super.post(attrs, { responseType: 'entity', ...options });
    }
    get(options = {}) {
        return super.get({ responseType: 'entities', ...options });
    }
    //#endregion
    //#region Shortcuts
    create(attrs, options) {
        return this.post(attrs, options);
    }
    fetch(options) {
        return this.get(options);
    }
    fetchOne(options) {
        let res = this.clone();
        res.query((q) => q.top(1));
        return res.fetch(options).pipe(map(({ entities, annots }) => ({ entity: entities !== null ? entities[0] || null : null, annots })));
    }
    fetchMany(top, options) {
        let res = this.clone();
        let fetch = (opts) => {
            if (opts) {
                res.query((q) => q.paging(opts));
            }
            return res.fetch(options);
        };
        return fetch({ top }).pipe(expand(({ annots }) => annots.skip || annots.skiptoken ? fetch(annots) : EMPTY), map(({ entities, annots }) => ({ entities: entities || [], annots })), reduce((acc, { entities, annots }) => ({
            entities: [...(acc.entities || []), ...(entities || [])],
            annots: acc.annots.union(annots)
        })));
    }
    fetchAll(options) {
        let res = this.clone();
        // Clean Paging
        res.query((q) => q.clearPaging());
        let fetch = (opts) => {
            if (opts) {
                res.query((q) => q.paging(opts));
            }
            return res.fetch(options);
        };
        return fetch().pipe(expand(({ annots }) => annots.skip || annots.skiptoken ? fetch(annots) : EMPTY), map(({ entities, annots }) => ({ entities: entities || [], annots })), reduce((acc, { entities, annots }) => ({
            entities: [...(acc.entities || []), ...(entities || [])],
            annots: acc.annots.union(annots)
        })));
    }
    //#endregion
    fetchEntities(options) {
        return this.fetch(options).pipe(map(({ entities }) => entities));
    }
    fetchCollection(options) {
        return this.fetch(options).pipe(map(({ entities, annots }) => entities
            ? this.asCollection(entities, { annots, reset: true })
            : null));
    }
}

class ODataAnnotations {
    constructor(helper, annotations = new Map(), context) {
        this.helper = helper;
        this.annotations = annotations;
        this.context = context;
    }
    attributes(data, metadata) {
        return this.helper.attributes(data, metadata);
    }
    update(data) {
        this.annotations = new Map([
            ...this.annotations,
            ...this.helper.annotations(data),
        ]);
    }
    get entitySet() {
        return this.context?.entitySet;
    }
    get type() {
        return this.helper.type(this.annotations) || this.context?.type;
    }
}
class ODataPropertyAnnotations extends ODataAnnotations {
    union(other) {
        return new ODataPropertyAnnotations(this.helper, new Map([...this.annotations, ...other.annotations]), Object.assign({}, this.context, other.context));
    }
    clone() {
        return new ODataPropertyAnnotations(this.helper, new Map(this.annotations), this.context);
    }
    data(data) {
        return this.helper.property(data);
    }
}
class ODataEntityAnnotations extends ODataAnnotations {
    union(other) {
        return new ODataEntityAnnotations(this.helper, new Map([...this.annotations, ...other.annotations]), Object.assign({}, this.context, other.context));
    }
    clone() {
        return new ODataEntityAnnotations(this.helper, new Map(this.annotations), this.context);
    }
    data(data) {
        return this.helper.entity(data);
    }
    get id() {
        return this.helper.id(this.annotations);
    }
    get etag() {
        return this.helper.etag(this.annotations);
    }
    get mediaEtag() {
        return this.helper.mediaEtag(this.annotations);
    }
    get metadataEtag() {
        return this.helper.metadataEtag(this.annotations);
    }
    get readLink() {
        return this.helper.readLink(this.annotations);
    }
    get editLink() {
        return this.helper.editLink(this.annotations);
    }
    get mediaReadLink() {
        return this.helper.mediaReadLink(this.annotations);
    }
    get mediaEditLink() {
        return this.helper.mediaEditLink(this.annotations);
    }
    get mediaContentType() {
        return this.helper.mediaContentType(this.annotations);
    }
    get properties() {
        if (this._properties === undefined) {
            this._properties = this.helper.properties(this.annotations);
        }
        return this._properties;
    }
    property(name, type) {
        const props = this.properties.get(name);
        return type === 'collection'
            ? new ODataEntitiesAnnotations(this.helper, props)
            : new ODataEntityAnnotations(this.helper, props);
    }
    get functions() {
        if (this._functions === undefined) {
            this._functions = this.helper.functions(this.annotations);
        }
        return this._functions;
    }
    function(name) {
        return this.functions[name];
    }
}
class ODataEntitiesAnnotations extends ODataAnnotations {
    union(other) {
        return new ODataEntitiesAnnotations(this.helper, new Map([...this.annotations, ...other.annotations]), Object.assign({}, this.context, other.context));
    }
    clone() {
        return new ODataEntitiesAnnotations(this.helper, new Map(this.annotations), this.context);
    }
    data(data) {
        return this.helper.entities(data);
    }
    get readLink() {
        return this.helper.readLink(this.annotations);
    }
    get count() {
        return this.helper.count(this.annotations);
    }
    get nextLink() {
        return this.helper.nextLink(this.annotations);
    }
    get deltaLink() {
        return this.helper.deltaLink(this.annotations);
    }
    get top() {
        let match = (this.nextLink || '').match(/[&?]{1}\$top=(\d+)/);
        return match !== null ? Number(match[1]) : undefined;
    }
    get skip() {
        let match = (this.nextLink || '').match(/[&?]{1}\$skip=(\d+)/);
        return match !== null ? Number(match[1]) : undefined;
    }
    get skiptoken() {
        let match = (this.nextLink || '').match(/[&?]{1}\$skiptoken=([\d\w\s']+)/);
        return match !== null ? match[1] : undefined;
    }
    get functions() {
        if (this._functions === undefined) {
            this._functions = this.helper.functions(this.annotations);
        }
        return this._functions;
    }
    function(name) {
        return this.functions[name];
    }
}

class ODataResponseOptions {
    constructor(config) {
        this.version = config.version || DEFAULT_VERSION;
    }
    get helper() {
        return ODataHelper[this.version];
    }
    clone() {
        return new ODataResponseOptions(this);
    }
    setFeatures(features) {
        features.split(';').forEach((o) => {
            let [k, v] = o.split('=');
            switch (k.trim()) {
                case 'odata.metadata':
                    this.metadata = v;
                    break;
                case 'odata.streaming':
                    this.streaming = v == 'true';
                    break;
                case 'IEEE754Compatible':
                    this.ieee754Compatible = v == 'true';
                    break;
            }
        });
    }
    setVersion(version) {
        const value = version.replace(/\;/g, '').trim();
        if ([VERSION_2_0, VERSION_3_0, VERSION_4_0].indexOf(value) !== -1)
            this.version = value;
    }
    setLocation(location) {
        // TODO: resolve location?
        this.location = location;
    }
    setCache(cacheControl) {
        cacheControl.split(',').forEach((directive) => {
            if (directive.startsWith(MAX_AGE)) {
                let maxAge = Number(directive.split('=')[1]);
                if (!Number.isNaN(maxAge))
                    this.maxAge = maxAge;
            }
            if (['public', 'private', 'no-cache', 'no-store'].indexOf(directive) !== -1) {
                this.cacheability = directive;
            }
        });
    }
}

/**
 * OData Response
 */
class ODataResponse extends HttpResponse {
    constructor(init) {
        super(init);
        this.api = init.api;
        this.resource = init.resource;
    }
    static fromHttpResponse(req, res) {
        return new ODataResponse({
            api: req.api,
            resource: req.resource,
            body: res.body,
            headers: res.headers,
            status: res.status,
            statusText: res.statusText,
            url: res.url || undefined,
        });
    }
    static fromJSON(req, json) {
        return new ODataResponse({
            api: req.api,
            resource: req.resource,
            body: json.body,
            headers: new HttpHeaders(json.headers),
            status: json.status,
            statusText: json.statusText,
            url: json.url || undefined,
        });
    }
    toJSON() {
        return {
            body: this.body,
            headers: this.headers
                .keys()
                .map((name) => ({ [name]: this.headers.getAll(name) || [] }))
                .reduce((acc, header) => Object.assign(acc, header), {}),
            status: this.status,
            statusText: this.statusText,
            url: this.url,
        };
    }
    get options() {
        if (this._options === undefined) {
            this._options = new ODataResponseOptions(this.api.options.parserOptions);
            const contentType = this.headers.get(CONTENT_TYPE);
            if (contentType && contentType.indexOf(APPLICATION_JSON) !== -1) {
                const features = contentType
                    .split(',')
                    .find((p) => p.startsWith(APPLICATION_JSON));
                this._options.setFeatures(features);
            }
            const header = Http.resolveHeaderKey(this.headers, ODATA_VERSION_HEADERS);
            if (header) {
                const version = (this.headers.get(header) || '').replace(/\;/g, '');
                this._options.setVersion(version);
            }
            const location = this.headers.get(LOCATION_HEADER);
            if (location) {
                this._options.setLocation(location);
            }
            const cacheControl = this.headers.get(CACHE_CONTROL);
            if (cacheControl) {
                this._options.setCache(cacheControl);
            }
        }
        return this._options;
    }
    get payload() {
        if (this._payload === undefined) {
            this._payload =
                this.body && this.options.version === '2.0'
                    ? this.body['d']
                    : this.body;
        }
        return this._payload;
    }
    get context() {
        if (this._context === undefined) {
            this._context = this.options.helper.context(Types.isPlainObject(this.payload) ? this.payload : {});
        }
        return this._context;
    }
    get annotations() {
        if (this._annotations === undefined) {
            const options = this.options;
            this._annotations = options.helper.annotations(this.payload || {});
            let key = Http.resolveHeaderKey(this.headers, ETAG_HEADERS);
            if (key) {
                const etag = this.headers.get(key);
                if (etag)
                    this._annotations.set(options.helper.ODATA_ETAG, etag);
            }
            key = Http.resolveHeaderKey(this.headers, ODATA_ENTITYID_HEADERS);
            if (key) {
                const entityId = this.headers.get(key);
                if (entityId)
                    this._annotations.set(options.helper.ODATA_ID, entityId);
            }
        }
        return this._annotations;
    }
    /**
     * Handle the response body as an entity
     * @returns
     */
    entity() {
        const options = this.options;
        const payload = this.payload;
        const annots = new ODataEntityAnnotations(options.helper, this.annotations, this.context);
        const data = payload ? annots.data(payload) : null;
        let entity = (data !== null && Types.isPlainObject(data)
            ? options.helper.attributes(data, this.api.options.stripMetadata)
            : data);
        if (entity !== null)
            entity = this.resource.deserialize(entity, options);
        return { entity, annots };
    }
    /**
     * Handle the response body as entities
     * @returns
     */
    entities() {
        const options = this.options;
        const payload = this.payload;
        const annots = new ODataEntitiesAnnotations(options.helper, this.annotations, this.context);
        let entities = payload ? annots.data(payload) : null;
        if (entities !== null)
            entities = this.resource.deserialize(entities, options);
        return { entities, annots };
    }
    /**
     * Handle the response body as a property
     * @returns
     */
    property() {
        const options = this.options;
        const payload = this.payload;
        const annots = new ODataPropertyAnnotations(options.helper, this.annotations, this.context);
        const data = payload ? annots.data(payload) : null;
        let property = (data !== null && Types.isPlainObject(data)
            ? options.helper.attributes(data, this.api.options.stripMetadata)
            : data);
        if (property !== null)
            property = this.resource.deserialize(property, options);
        return { property, annots };
    }
    /**
     * Handle the response body as a value
     * @returns
     */
    value() {
        const options = this.options;
        const payload = this.payload;
        let value = (payload !== null && Types.isPlainObject(payload)
            ? options.helper.attributes(payload, this.api.options.stripMetadata)
            : payload);
        if (value !== null)
            value = this.resource.deserialize(value, options);
        return value;
    }
}

class CsdlFunction {
    constructor(name, returnType, isBound, entitySetPath, isComposable, parameters) {
        this.name = name;
        this.returnType = returnType;
        this.isBound = isBound;
        this.entitySetPath = entitySetPath;
        this.isComposable = isComposable;
        this.parameters = parameters;
    }
}
class CsdlAction {
    constructor(name, returnType, isBound, entitySetPath, parameters) {
        this.name = name;
        this.returnType = returnType;
        this.isBound = isBound;
        this.entitySetPath = entitySetPath;
        this.parameters = parameters;
    }
}
class CsdlFunctionImport {
    constructor(name, functionName, entitySet, IncludeInServiceDocument) {
        this.name = name;
        this.functionName = functionName;
        this.entitySet = entitySet;
        this.IncludeInServiceDocument = IncludeInServiceDocument;
    }
}
class CsdlActionImport {
    constructor(name, action, entitySet) {
        this.name = name;
        this.action = action;
        this.entitySet = entitySet;
    }
}
class CsdlParameter {
    constructor(name, type, nullable, maxLength, precision, scale, srid) {
        this.name = name;
        this.type = type;
        this.nullable = nullable;
        this.maxLength = maxLength;
        this.precision = precision;
        this.scale = scale;
        this.srid = srid;
    }
}
class CsdlReturnType {
    constructor(type, nullable, maxLength, precision, scale, srid) {
        this.type = type;
        this.nullable = nullable;
        this.maxLength = maxLength;
        this.precision = precision;
        this.scale = scale;
        this.srid = srid;
    }
}

class CsdlAnnotable {
    constructor(annotationList) {
        this.annotationList = annotationList;
    }
}
class CsdlAnnotations extends CsdlAnnotable {
    constructor(target, annotationList, qualifier) {
        super(annotationList);
        this.target = target;
        this.qualifier = qualifier;
    }
}
class CsdlAnnotation {
    constructor(term, qualifier) {
        this.term = term;
        this.qualifier = qualifier;
    }
}
class CsdlTerm {
    constructor(name, type, baseTerm, defaultValue, appliesTo, nullable, maxLength, precision, scale, srid) {
        this.name = name;
        this.type = type;
        this.baseTerm = baseTerm;
        this.defaultValue = defaultValue;
        this.appliesTo = appliesTo;
        this.nullable = nullable;
        this.maxLength = maxLength;
        this.precision = precision;
        this.scale = scale;
        this.srid = srid;
    }
}

class CsdlStructuredType {
    constructor(name, properties, navigationProperties, baseType, openType, abstract) {
        this.name = name;
        this.properties = properties;
        this.navigationProperties = navigationProperties;
        this.baseType = baseType;
        this.openType = openType;
        this.abstract = abstract;
    }
}
class CsdlComplexType extends CsdlStructuredType {
    constructor(name, properties, navigationProperties, baseType, openType, abstract) {
        super(name, properties, navigationProperties, baseType, openType, abstract);
    }
}
class CsdlEntityType extends CsdlStructuredType {
    constructor(name, key, properties, navigationProperties, baseType, openType, abstract, hasStream) {
        super(name, properties, navigationProperties, baseType, openType, abstract);
        this.key = key;
        this.hasStream = hasStream;
    }
}
class CsdlKey {
    constructor(propertyRefs) {
        this.propertyRefs = propertyRefs;
    }
}
class CsdlPropertyRef {
    constructor(name, alias) {
        this.name = name;
        this.alias = alias;
    }
}

class CsdlEnumType {
    constructor(name, members, underlyingType, isFlags) {
        this.name = name;
        this.members = members;
        this.underlyingType = underlyingType;
        this.isFlags = isFlags;
    }
}
class CsdlEnumMember {
    constructor(name, value) {
        this.name = name;
        this.value = value;
    }
}

class CsdlReference extends CsdlAnnotable {
    constructor(uri, includes, includeAnnotations, annotationList) {
        super(annotationList);
        this.uri = uri;
        this.includes = includes;
        this.includeAnnotations = includeAnnotations;
    }
}
class CsdlInclude {
    constructor(namespace, alias) {
        this.namespace = namespace;
        this.alias = alias;
    }
}
class CsdlIncludeAnnotations {
    constructor(termNamespace, qualifier, targetNamespace) {
        this.termNamespace = termNamespace;
        this.qualifier = qualifier;
        this.targetNamespace = targetNamespace;
    }
}

class CsdlStructuralProperty {
    constructor(name, type, nullable) {
        this.name = name;
        this.type = type;
        this.nullable = nullable;
    }
}
class CsdlProperty extends CsdlStructuralProperty {
    constructor(name, type, nullable, maxLength, precision, scale, unicode, srid, defaultValue) {
        super(name, type, nullable);
        this.maxLength = maxLength;
        this.precision = precision;
        this.scale = scale;
        this.unicode = unicode;
        this.srid = srid;
        this.defaultValue = defaultValue;
    }
}
class CsdlNavigationProperty extends CsdlStructuralProperty {
    constructor(name, type, nullable, partner, containsTarget, referentialConstraints, onDelete) {
        super(name, type, nullable);
        this.partner = partner;
        this.containsTarget = containsTarget;
        this.referentialConstraints = referentialConstraints;
        this.onDelete = onDelete;
    }
}
class CsdlReferentialConstraint {
    constructor(property, referencedProperty) {
        this.property = property;
        this.referencedProperty = referencedProperty;
    }
}
class CsdlOnDelete {
    constructor(action) {
        this.action = action;
    }
}

class CsdlEntityContainer {
    constructor(name, extend, entitySets, singletons, functionImports, actionImports) {
        this.name = name;
        this.extend = extend;
        this.entitySets = entitySets;
        this.singletons = singletons;
        this.functionImports = functionImports;
        this.actionImports = actionImports;
    }
}

class CsdlEntitySet {
    constructor(name, entityType, navigationPropertyBinding, includeInServiceDocument) {
        this.name = name;
        this.entityType = entityType;
        this.navigationPropertyBinding = navigationPropertyBinding;
        this.includeInServiceDocument = includeInServiceDocument;
    }
}

class CsdlNavigationPropertyBinding {
    constructor(path, target) {
        this.path = path;
        this.target = target;
    }
}

class CsdlSchema extends CsdlAnnotable {
    constructor(namespace, alias, enumTypes, complexTypes, entityTypes, functions, actions, entityContainer, typeDefinitions, terms, annotationsList, annotationList) {
        super(annotationList);
        this.namespace = namespace;
        this.alias = alias;
        this.enumTypes = enumTypes;
        this.complexTypes = complexTypes;
        this.entityTypes = entityTypes;
        this.functions = functions;
        this.actions = actions;
        this.entityContainer = entityContainer;
        this.typeDefinitions = typeDefinitions;
        this.terms = terms;
        this.annotationsList = annotationsList;
    }
}

class CsdlSingleton {
    constructor(name, type, navigationPropertyBindings) {
        this.name = name;
        this.type = type;
        this.navigationPropertyBindings = navigationPropertyBindings;
    }
}

class CsdlTypeDefinition extends CsdlAnnotable {
    constructor(name, underlayingType, maxLength, precision, scale, unicode, srid, annotationList) {
        super(annotationList);
        this.name = name;
        this.underlayingType = underlayingType;
        this.maxLength = maxLength;
        this.precision = precision;
        this.scale = scale;
        this.unicode = unicode;
        this.srid = srid;
    }
}

var FieldType;
(function (FieldType) {
    FieldType[FieldType["ATTRIBUTE"] = 0] = "ATTRIBUTE";
    FieldType[FieldType["TAG"] = 1] = "TAG";
})(FieldType || (FieldType = {}));
class Field {
    constructor(name, fieldType) {
        this.name = name;
        this.fieldType = fieldType;
    }
}
class ODataMetadata {
    constructor(xml) {
        try {
            const parser = new DOMParser();
            const document = parser.parseFromString(xml, 'text/xml');
            this.checkVersion(document);
            this.version = this.getFieldValueByAttribute(new Field(ODataMetadata.ATTRIBUTE_VERSION, FieldType.ATTRIBUTE), document.documentElement.attributes);
            this.references = this.getObjects(document.documentElement, ODataMetadata.TAG_REFERENCE, [
                new Field(ODataMetadata.ATTRIBUTE_URI, FieldType.ATTRIBUTE),
                new Field(ODataMetadata.TAG_INCLUDE, FieldType.TAG),
                new Field(ODataMetadata.TAG_INCLUDE_ANNOTATIONS, FieldType.TAG),
                new Field(ODataMetadata.TAG_ANNOTATION, FieldType.TAG),
            ]);
            const dataServices = document.documentElement.getElementsByTagName(ODataMetadata.TAG_DATA_SERVICES)[0];
            this.schemas = this.getObjects(dataServices, ODataMetadata.TAG_SCHEMA, [
                new Field(ODataMetadata.ATTRIBUTE_NAMESPACE, FieldType.ATTRIBUTE),
                new Field(ODataMetadata.ATTRIBUTE_ALIAS, FieldType.ATTRIBUTE),
                new Field(ODataMetadata.TAG_ENUM_TYPE, FieldType.TAG),
                new Field(ODataMetadata.TAG_COMPLEX_TYPE, FieldType.TAG),
                new Field(ODataMetadata.TAG_ENTITY_TYPE, FieldType.TAG),
                new Field(ODataMetadata.TAG_FUNCTION, FieldType.TAG),
                new Field(ODataMetadata.TAG_ACTION, FieldType.TAG),
                new Field(ODataMetadata.TAG_ENTITY_CONTAINER, FieldType.TAG),
                new Field(ODataMetadata.TAG_TYPE_DEFINITION, FieldType.TAG),
                new Field(ODataMetadata.TAG_TERM, FieldType.TAG),
                new Field(ODataMetadata.TAG_ANNOTATIONS, FieldType.TAG),
                new Field(ODataMetadata.TAG_ANNOTATION, FieldType.TAG),
            ]);
        }
        catch (error) {
            throw new Error('Unable to parse metadata, ' + error);
        }
    }
    checkVersion(document) {
        // check version
        const attributes = document.documentElement.attributes;
        if (!attributes) {
            throw new Error('OData version is not specified in the metadata');
        }
        const attr = attributes.getNamedItem('Version');
        if (attr === null) {
            throw new Error('OData version is not specified in the metadata');
        }
        const odataVersion = attr?.nodeValue;
        if (odataVersion !== VERSION_4_0) {
            throw new Error('OData version "' + odataVersion + '" is not supported');
        }
    }
    getObjects(parentElement, tag, fieldNames) {
        let objects = [];
        const children = parentElement.children;
        for (let index = 0; index < children.length; index++) {
            const element = children.item(index);
            if (!element || element.nodeName !== tag) {
                continue;
            }
            const attributes = element.attributes;
            const fieldValues = this.getFieldValues(fieldNames, attributes, element);
            switch (tag) {
                case ODataMetadata.TAG_REFERENCE:
                    objects.push(new CsdlReference(fieldValues[0], fieldValues[1], fieldValues[2], fieldValues[3]));
                    break;
                case ODataMetadata.TAG_INCLUDE:
                    objects.push(new CsdlInclude(fieldValues[0], fieldValues[1]));
                    break;
                case ODataMetadata.TAG_INCLUDE_ANNOTATIONS:
                    objects.push(new CsdlIncludeAnnotations(fieldValues[0], fieldValues[1], fieldValues[2]));
                    break;
                case ODataMetadata.TAG_TERM:
                    objects.push(new CsdlTerm(fieldValues[0], fieldValues[1], fieldValues[2], fieldValues[3], fieldValues[4], fieldValues[5], fieldValues[6], fieldValues[7], fieldValues[8], fieldValues[9]));
                    break;
                case ODataMetadata.TAG_ANNOTATIONS:
                    objects.push(new CsdlAnnotations(fieldValues[0], fieldValues[1], fieldValues[2]));
                    break;
                case ODataMetadata.TAG_ANNOTATION:
                    objects.push(new CsdlAnnotation(fieldValues[0], fieldValues[1]));
                    break;
                case ODataMetadata.TAG_SCHEMA:
                    objects.push(new CsdlSchema(fieldValues[0], fieldValues[1], fieldValues[2], fieldValues[3], fieldValues[4], fieldValues[5], fieldValues[6], fieldValues[7], fieldValues[8], fieldValues[9], fieldValues[10], fieldValues[11]));
                    break;
                case ODataMetadata.TAG_ENUM_TYPE:
                    objects.push(new CsdlEnumType(fieldValues[0], fieldValues[1], fieldValues[2], fieldValues[3]));
                    break;
                case ODataMetadata.TAG_COMPLEX_TYPE:
                    objects.push(new CsdlComplexType(fieldValues[0], fieldValues[1], fieldValues[2], fieldValues[3], fieldValues[4], fieldValues[5]));
                    break;
                case ODataMetadata.TAG_ENTITY_TYPE:
                    objects.push(new CsdlEntityType(fieldValues[0], fieldValues[1], fieldValues[2], fieldValues[3], fieldValues[4], fieldValues[5], fieldValues[6], fieldValues[7]));
                    break;
                case ODataMetadata.TAG_FUNCTION:
                    objects.push(new CsdlFunction(fieldValues[0], fieldValues[1], fieldValues[2], fieldValues[3], fieldValues[4], fieldValues[5]));
                    break;
                case ODataMetadata.TAG_MEMBER:
                    objects.push(new CsdlEnumMember(fieldValues[0], fieldValues[1]));
                    break;
                case ODataMetadata.TAG_PROPERTY:
                    objects.push(new CsdlProperty(fieldValues[0], fieldValues[1], fieldValues[2], fieldValues[3], fieldValues[4], fieldValues[5], fieldValues[6], fieldValues[7], fieldValues[8]));
                    break;
                case ODataMetadata.TAG_PROPERTY_REF:
                    objects.push(new CsdlPropertyRef(fieldValues[0], fieldValues[1]));
                    break;
                case ODataMetadata.TAG_NAVIGATION_PROPERTY:
                    objects.push(new CsdlNavigationProperty(fieldValues[0], fieldValues[1], fieldValues[2], fieldValues[3], fieldValues[4], fieldValues[5], fieldValues[6]));
                    break;
                case ODataMetadata.TAG_REFERENTIAL_CONSTRAINT:
                    objects.push(new CsdlReferentialConstraint(fieldValues[0], fieldValues[1]));
                    break;
                case ODataMetadata.TAG_PARAMETER:
                    objects.push(new CsdlParameter(fieldValues[0], fieldValues[1], fieldValues[2], fieldValues[3], fieldValues[4], fieldValues[5], fieldValues[6]));
                    break;
                case ODataMetadata.TAG_ACTION:
                    objects.push(new CsdlAction(fieldValues[0], fieldValues[1], fieldValues[2], fieldValues[3], fieldValues[4]));
                    break;
                case ODataMetadata.TAG_ENTITY_SET:
                    objects.push(new CsdlEntitySet(fieldValues[0], fieldValues[1], fieldValues[2], fieldValues[3]));
                    break;
                case ODataMetadata.TAG_SINGLETON:
                    objects.push(new CsdlSingleton(fieldValues[0], fieldValues[1], fieldValues[2]));
                    break;
                case ODataMetadata.TAG_FUNCTION_IMPORT:
                    objects.push(new CsdlFunctionImport(fieldValues[0], fieldValues[1], fieldValues[2], fieldValues[3]));
                    break;
                case ODataMetadata.TAG_ACTION_IMPORT:
                    objects.push(new CsdlActionImport(fieldValues[0], fieldValues[1], fieldValues[2]));
                    break;
                case ODataMetadata.TAG_NAVIGATION_PROPERTY_BINDING:
                    objects.push(new CsdlNavigationPropertyBinding(fieldValues[0], fieldValues[1]));
                    break;
                case ODataMetadata.TAG_TYPE_DEFINITION:
                    objects.push(new CsdlTypeDefinition(fieldValues[0], fieldValues[1], fieldValues[2], fieldValues[3], fieldValues[4], fieldValues[5], fieldValues[6], fieldValues[7]));
                    break;
                default:
                    throw new Error('Unknwon tag:' + tag);
            }
        }
        return objects;
    }
    getObject(parentElement, tag, fieldNames) {
        let object;
        const children = parentElement.children;
        let element;
        for (let index = 0; index < children.length; index++) {
            if (children && children.item(index)?.nodeName === tag) {
                if (element !== undefined && element !== null) {
                    throw new Error('Expected one ' + tag);
                }
                element = children.item(index);
            }
        }
        if (element !== undefined && element !== null) {
            const attributes = element.attributes;
            const fieldValues = this.getFieldValues(fieldNames, attributes, element);
            switch (tag) {
                case ODataMetadata.TAG_KEY:
                    object = new CsdlKey(fieldValues[0]);
                    break;
                case ODataMetadata.TAG_RETURN_TYPE:
                    object = new CsdlReturnType(fieldValues[0], fieldValues[1], fieldValues[2], fieldValues[3], fieldValues[4], fieldValues[5]);
                    break;
                case ODataMetadata.TAG_ENTITY_CONTAINER:
                    object = new CsdlEntityContainer(fieldValues[0], fieldValues[1], fieldValues[2], fieldValues[3], fieldValues[4], fieldValues[5]);
                    break;
                case ODataMetadata.TAG_ON_DELETE:
                    object = new CsdlOnDelete(fieldValues[0]);
                    break;
                default:
                    throw new Error('Unknwon tag:' + tag);
            }
        }
        return object;
    }
    getFieldValues(fields, attributes, element) {
        const fieldValues = [];
        for (const field of fields) {
            if (field.fieldType === FieldType.TAG) {
                fieldValues.push(this.getFieldValueByTag(field, element));
            }
            else if (field.fieldType === FieldType.ATTRIBUTE) {
                fieldValues.push(this.getFieldValueByAttribute(field, attributes));
            }
            else {
                throw new Error('Unknown field type: ' + field.fieldType);
            }
        }
        return fieldValues;
    }
    getFieldValueByAttribute(field, attributes) {
        switch (field.name) {
            case ODataMetadata.ATTRIBUTE_VERSION:
            case ODataMetadata.ATTRIBUTE_URI:
            case ODataMetadata.ATTRIBUTE_NAMESPACE:
            case ODataMetadata.ATTRIBUTE_ALIAS:
            case ODataMetadata.ATTRIBUTE_TERM_NAMESPACE:
            case ODataMetadata.ATTRIBUTE_TERM:
            case ODataMetadata.ATTRIBUTE_QUALIFIER:
            case ODataMetadata.ATTRIBUTE_TARGET_NAMESPACE:
            case ODataMetadata.ATTRIBUTE_NAME:
            case ODataMetadata.ATTRIBUTE_TYPE:
            case ODataMetadata.ATTRIBUTE_SRID:
            case ODataMetadata.ATTRIBUTE_DEFAULT_VALUE:
            case ODataMetadata.ATTRIBUTE_PARTNER:
            case ODataMetadata.ATTRIBUTE_PROPERTY:
            case ODataMetadata.ATTRIBUTE_REFERENCED_PROPERTY:
            case ODataMetadata.ATTRIBUTE_BASE_TYPE:
            case ODataMetadata.ATTRIBUTE_ENTITY_SET_PATH:
            case ODataMetadata.ATTRIBUTE_ENTITY_TYPE:
            case ODataMetadata.ATTRIBUTE_PATH:
            case ODataMetadata.ATTRIBUTE_TARGET:
            case ODataMetadata.ATTRIBUTE_FUNCTION:
            case ODataMetadata.ATTRIBUTE_ACTION:
            case ODataMetadata.ATTRIBUTE_ENTITY_SET:
            case ODataMetadata.ATTRIBUTE_UNDERLYING_TYPE:
            case ODataMetadata.ATTRIBUTE_EXTENDS:
            case ODataMetadata.ATTRIBUTE_BASE_TERM:
            case ODataMetadata.ATTRIBUTE_APPLIES_TO:
                return this.getAttributeValue(attributes, field.name);
            case ODataMetadata.ATTRIBUTE_NULLABLE:
            case ODataMetadata.ATTRIBUTE_UNICODE:
            case ODataMetadata.ATTRIBUTE_OPEN_TYPE:
            case ODataMetadata.ATTRIBUTE_HAS_STREAM:
            case ODataMetadata.ATTRIBUTE_IS_BOUND:
            case ODataMetadata.ATTRIBUTE_IS_COMPOSABLE:
            case ODataMetadata.ATTRIBUTE_CONTAINS_TARGET:
            case ODataMetadata.ATTRIBUTE_INCLUDE_IN_SERVICE_DOCUMENT:
            case ODataMetadata.ATTRIBUTE_ABSTRACT:
            case ODataMetadata.ATTRIBUTE_IS_FLAGS:
                return this.propertyValueToBoolean(this.getAttributeValue(attributes, field.name));
            case ODataMetadata.ATTRIBUTE_VALUE:
            case ODataMetadata.ATTRIBUTE_MAX_LENGTH:
            case ODataMetadata.ATTRIBUTE_PRECISION:
            case ODataMetadata.ATTRIBUTE_SCALE:
                return this.propertyValueToNumber(this.getAttributeValue(attributes, field.name));
            default:
                throw new Error('Unknwon attribute:' + field.name);
        }
    }
    getFieldValueByTag(field, element) {
        switch (field.name) {
            case ODataMetadata.TAG_INCLUDE:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAMESPACE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_ALIAS, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_INCLUDE_ANNOTATIONS:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_TERM_NAMESPACE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_QUALIFIER, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_TARGET_NAMESPACE, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_TERM:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAME, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_TYPE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_BASE_TERM, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_DEFAULT_VALUE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_APPLIES_TO, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_NULLABLE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_MAX_LENGTH, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_PRECISION, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_SCALE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_SRID, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_ANNOTATIONS:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_TARGET, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.TAG_ANNOTATION, FieldType.TAG),
                    new Field(ODataMetadata.ATTRIBUTE_QUALIFIER, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_ANNOTATION:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_TERM, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_QUALIFIER, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_ENUM_TYPE:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAME, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.TAG_MEMBER, FieldType.TAG),
                    new Field(ODataMetadata.ATTRIBUTE_UNDERLYING_TYPE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_IS_FLAGS, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_COMPLEX_TYPE:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAME, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.TAG_PROPERTY, FieldType.TAG),
                    new Field(ODataMetadata.TAG_NAVIGATION_PROPERTY, FieldType.TAG),
                    new Field(ODataMetadata.ATTRIBUTE_BASE_TYPE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_OPEN_TYPE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_ABSTRACT, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_ENTITY_TYPE:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAME, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.TAG_KEY, FieldType.TAG),
                    new Field(ODataMetadata.TAG_PROPERTY, FieldType.TAG),
                    new Field(ODataMetadata.TAG_NAVIGATION_PROPERTY, FieldType.TAG),
                    new Field(ODataMetadata.ATTRIBUTE_BASE_TYPE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_OPEN_TYPE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_HAS_STREAM, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_ABSTRACT, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_FUNCTION:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAME, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.TAG_RETURN_TYPE, FieldType.TAG),
                    new Field(ODataMetadata.ATTRIBUTE_IS_BOUND, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_ENTITY_SET_PATH, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_IS_COMPOSABLE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.TAG_PARAMETER, FieldType.TAG),
                ]);
            case ODataMetadata.TAG_MEMBER:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAME, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_VALUE, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_PROPERTY:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAME, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_TYPE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_NULLABLE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_MAX_LENGTH, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_PRECISION, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_SCALE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_UNICODE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_SRID, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_DEFAULT_VALUE, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_KEY:
                return this.getObject(element, field.name, [
                    new Field(ODataMetadata.TAG_PROPERTY_REF, FieldType.TAG),
                ]);
            case ODataMetadata.TAG_PROPERTY_REF:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAME, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_ALIAS, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_NAVIGATION_PROPERTY:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAME, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_TYPE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_NULLABLE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_PARTNER, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_CONTAINS_TARGET, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.TAG_REFERENTIAL_CONSTRAINT, FieldType.TAG),
                    new Field(ODataMetadata.TAG_ON_DELETE, FieldType.TAG),
                ]);
            case ODataMetadata.TAG_REFERENTIAL_CONSTRAINT:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_PROPERTY, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_REFERENCED_PROPERTY, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_ON_DELETE:
                return this.getObject(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_ACTION, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_PARAMETER:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAME, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_TYPE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_NULLABLE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_MAX_LENGTH, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_PRECISION, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_SCALE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_SRID, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_RETURN_TYPE:
                return this.getObject(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_TYPE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_NULLABLE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_MAX_LENGTH, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_PRECISION, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_SCALE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_SRID, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_ACTION:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAME, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.TAG_RETURN_TYPE, FieldType.TAG),
                    new Field(ODataMetadata.ATTRIBUTE_IS_BOUND, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_ENTITY_SET_PATH, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.TAG_PARAMETER, FieldType.TAG),
                ]);
            case ODataMetadata.TAG_ENTITY_CONTAINER:
                return this.getObject(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAME, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_EXTENDS, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.TAG_ENTITY_SET, FieldType.TAG),
                    new Field(ODataMetadata.TAG_SINGLETON, FieldType.TAG),
                    new Field(ODataMetadata.TAG_FUNCTION_IMPORT, FieldType.TAG),
                    new Field(ODataMetadata.TAG_ACTION_IMPORT, FieldType.TAG),
                ]);
            case ODataMetadata.TAG_ENTITY_SET:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAME, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_ENTITY_TYPE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.TAG_NAVIGATION_PROPERTY_BINDING, FieldType.TAG),
                    new Field(ODataMetadata.ATTRIBUTE_INCLUDE_IN_SERVICE_DOCUMENT, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_SINGLETON:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAME, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_TYPE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.TAG_NAVIGATION_PROPERTY_BINDING, FieldType.TAG),
                ]);
            case ODataMetadata.TAG_FUNCTION_IMPORT:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAME, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_FUNCTION, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_ENTITY_SET, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_INCLUDE_IN_SERVICE_DOCUMENT, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_ACTION_IMPORT:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAME, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_ACTION, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_ENTITY_SET, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_NAVIGATION_PROPERTY_BINDING:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_PATH, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_TARGET, FieldType.ATTRIBUTE),
                ]);
            case ODataMetadata.TAG_TYPE_DEFINITION:
                return this.getObjects(element, field.name, [
                    new Field(ODataMetadata.ATTRIBUTE_NAME, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_UNDERLYING_TYPE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_MAX_LENGTH, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_PRECISION, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_SCALE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_UNICODE, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.ATTRIBUTE_SRID, FieldType.ATTRIBUTE),
                    new Field(ODataMetadata.TAG_ANNOTATION, FieldType.TAG),
                ]);
            default:
                throw new Error('Unknwon tag:' + field.name);
        }
    }
    getAttributeValue(attributes, attributeName) {
        const attribute = attributes.getNamedItem(attributeName);
        return attribute !== null && attribute.nodeValue
            ? attribute.nodeValue
            : undefined;
    }
    propertyValueToNumber(attributeValue) {
        return attributeValue !== undefined ? Number(attributeValue) : undefined;
    }
    propertyValueToBoolean(attributeValue) {
        return attributeValue !== undefined ? attributeValue === 'true' : false;
    }
}
// TAGS
ODataMetadata.TAG_EDMX = 'edmx:Edmx';
ODataMetadata.TAG_DATA_SERVICES = 'edmx:DataServices';
ODataMetadata.TAG_REFERENCE = 'edmx:Reference';
ODataMetadata.TAG_INCLUDE = 'edmx:Include';
ODataMetadata.TAG_INCLUDE_ANNOTATIONS = 'edmx:IncludeAnnotations';
ODataMetadata.TAG_TERM = 'Term';
ODataMetadata.TAG_ANNOTATIONS = 'Annotations';
ODataMetadata.TAG_ANNOTATION = 'Annotation';
ODataMetadata.TAG_SCHEMA = 'Schema';
ODataMetadata.TAG_ENUM_TYPE = 'EnumType';
ODataMetadata.TAG_MEMBER = 'Member';
ODataMetadata.TAG_COMPLEX_TYPE = 'ComplexType';
ODataMetadata.TAG_ENTITY_TYPE = 'EntityType';
ODataMetadata.TAG_PROPERTY = 'Property';
ODataMetadata.TAG_KEY = 'Key';
ODataMetadata.TAG_PROPERTY_REF = 'PropertyRef';
ODataMetadata.TAG_NAVIGATION_PROPERTY = 'NavigationProperty';
ODataMetadata.TAG_REFERENTIAL_CONSTRAINT = 'ReferentialConstraint';
ODataMetadata.TAG_ON_DELETE = 'OnDelete';
ODataMetadata.TAG_FUNCTION = 'Function';
ODataMetadata.TAG_RETURN_TYPE = 'ReturnType';
ODataMetadata.TAG_PARAMETER = 'Parameter';
ODataMetadata.TAG_ACTION = 'Action';
ODataMetadata.TAG_ENTITY_CONTAINER = 'EntityContainer';
ODataMetadata.TAG_ENTITY_SET = 'EntitySet';
ODataMetadata.TAG_SINGLETON = 'Singleton';
ODataMetadata.TAG_FUNCTION_IMPORT = 'FunctionImport';
ODataMetadata.TAG_ACTION_IMPORT = 'ActionImport';
ODataMetadata.TAG_NAVIGATION_PROPERTY_BINDING = 'NavigationPropertyBinding';
ODataMetadata.TAG_TYPE_DEFINITION = 'TypeDefinition';
// ATTRIBUTES
ODataMetadata.ATTRIBUTE_VERSION = 'Version';
ODataMetadata.ATTRIBUTE_URI = 'Uri';
ODataMetadata.ATTRIBUTE_ALIAS = 'Alias';
ODataMetadata.ATTRIBUTE_NAMESPACE = 'Namespace';
ODataMetadata.ATTRIBUTE_TERM_NAMESPACE = 'TermNamespace';
ODataMetadata.ATTRIBUTE_QUALIFIER = 'Qualifier';
ODataMetadata.ATTRIBUTE_TARGET_NAMESPACE = 'TargetNamespace';
ODataMetadata.ATTRIBUTE_TERM = 'Term';
ODataMetadata.ATTRIBUTE_NAME = 'Name';
ODataMetadata.ATTRIBUTE_VALUE = 'Value';
ODataMetadata.ATTRIBUTE_BASE_TYPE = 'BaseType';
ODataMetadata.ATTRIBUTE_OPEN_TYPE = 'OpenType';
ODataMetadata.ATTRIBUTE_TYPE = 'Type';
ODataMetadata.ATTRIBUTE_NULLABLE = 'Nullable';
ODataMetadata.ATTRIBUTE_MAX_LENGTH = 'MaxLength';
ODataMetadata.ATTRIBUTE_PRECISION = 'Precision';
ODataMetadata.ATTRIBUTE_SCALE = 'Scale';
ODataMetadata.ATTRIBUTE_UNICODE = 'Unicode';
ODataMetadata.ATTRIBUTE_SRID = 'SRID';
ODataMetadata.ATTRIBUTE_DEFAULT_VALUE = 'DefaultValue';
ODataMetadata.ATTRIBUTE_PARTNER = 'Partner';
ODataMetadata.ATTRIBUTE_PROPERTY = 'Property';
ODataMetadata.ATTRIBUTE_REFERENCED_PROPERTY = 'ReferencedProperty';
ODataMetadata.ATTRIBUTE_HAS_STREAM = 'HasStream';
ODataMetadata.ATTRIBUTE_CONTAINS_TARGET = 'ContainsTarget';
ODataMetadata.ATTRIBUTE_IS_BOUND = 'IsBound';
ODataMetadata.ATTRIBUTE_ENTITY_SET_PATH = 'EntitySetPath';
ODataMetadata.ATTRIBUTE_IS_COMPOSABLE = 'IsComposable';
ODataMetadata.ATTRIBUTE_ENTITY_TYPE = 'EntityType';
ODataMetadata.ATTRIBUTE_PATH = 'Path';
ODataMetadata.ATTRIBUTE_TARGET = 'Target';
ODataMetadata.ATTRIBUTE_FUNCTION = 'Function';
ODataMetadata.ATTRIBUTE_ACTION = 'Action';
ODataMetadata.ATTRIBUTE_ENTITY_SET = 'EntitySet';
ODataMetadata.ATTRIBUTE_INCLUDE_IN_SERVICE_DOCUMENT = 'IncludeInServiceDocument';
ODataMetadata.ATTRIBUTE_ABSTRACT = 'Abstract';
ODataMetadata.ATTRIBUTE_UNDERLYING_TYPE = 'UnderlyingType';
ODataMetadata.ATTRIBUTE_IS_FLAGS = 'IsFlags';
ODataMetadata.ATTRIBUTE_EXTENDS = 'Extends';
ODataMetadata.ATTRIBUTE_BASE_TERM = 'BaseTerm';
ODataMetadata.ATTRIBUTE_APPLIES_TO = 'AppliesTo';

class ODataMetadataResource extends ODataResource {
    constructor(api, segments) {
        super(api, { segments });
    }
    //#region Factory
    static factory(api) {
        let segments = new ODataPathSegments();
        segments.add(PathSegmentNames.metadata, $METADATA);
        return new ODataMetadataResource(api, segments);
    }
    clone() {
        return super.clone();
    }
    //#endregion
    //#region Requests
    get(options) {
        return super.get({ responseType: 'text', ...options });
    }
    //#endregion
    //#region Shortcuts
    fetch(options) {
        return this.get(options).pipe(map((body) => new ODataMetadata(body)));
    }
}

class ODataSingletonResource extends ODataResource {
    //#region Factory
    static factory(api, { path, schema, query, }) {
        const segments = new ODataPathSegments();
        const segment = segments.add(PathSegmentNames.singleton, path);
        if (schema !== undefined)
            segment.type(schema.type());
        return new ODataSingletonResource(api, { segments, query, schema });
    }
    clone() {
        return super.clone();
    }
    //#endregion
    key(value) {
        const singleton = this.clone();
        var key = this.resolveKey(value);
        if (key !== undefined)
            singleton.segment((s) => s.singleton().key(key));
        return singleton;
    }
    keys(values) {
        const singleton = this.clone();
        const types = this.pathSegments.types({ key: true });
        const keys = values.map((value, index) => ODataResource.resolveKey(value, this.api.findStructuredTypeForType(types[index])));
        singleton.segment((s) => s.keys(keys));
        return singleton;
    }
    navigationProperty(path) {
        return ODataNavigationPropertyResource.fromResource(this, path);
    }
    property(path) {
        return ODataPropertyResource.fromResource(this, path);
    }
    action(path) {
        return ODataActionResource.fromResource(this, path);
    }
    function(path) {
        return ODataFunctionResource.fromResource(this, path);
    }
    //#region Requests
    post(attrs, options = {}) {
        return super.post(attrs, { responseType: 'entity', ...options });
    }
    put(attrs, options = {}) {
        return super.put(attrs, { responseType: 'entity', ...options });
    }
    patch(attrs, options = {}) {
        return super.patch(attrs, { responseType: 'entity', ...options });
    }
    delete(options = {}) {
        return super.delete({ responseType: 'entity', ...options });
    }
    get(options = {}) {
        return super.get({ responseType: 'entity', ...options });
    }
    //#endregion
    //#region Shortcuts
    /**
     * Creates a new entity.
     * @param attrs The entity attributes to create.
     * @param options The options for the request.
     * @returns The created entity with the annotations.
     */
    create(attrs, options) {
        return this.post(attrs, options);
    }
    /**
     * Updates an existing entity.
     * @param attrs The entity attributes to update.
     * @param options The options for the request.
     * @returns The updated entity with the annotations.
     */
    update(attrs, options) {
        return this.put(attrs, options);
    }
    /**
     * Modifies an existing entity.
     * @param attrs The entity attributes to modify.
     * @param options The options for the request.
     * @returns The modified entity with the annotations.
     */
    modify(attrs, options) {
        return this.patch(attrs, options);
    }
    /**
     * Delete an existing entity.
     * @param options The options for the request.
     * @returns Observable of the deleted entity.
     */
    destroy(options) {
        return this.delete(options);
    }
    /**
     * Fetch an existing entity.
     * @param options The options for the request.
     * @returns Observable of the entity with the annotations.
     */
    fetch(options) {
        return this.get(options);
    }
    /**
     * Fetch an existing entity.
     * @param options The options for the request.
     * @returns Observable of the entity.
     */
    fetchEntity(options) {
        return this.fetch(options).pipe(map(({ entity }) => entity));
    }
    /**
     * Fetch an existing entity and return a model.
     * @param options The options for the request.
     * @returns Observable of the entity.
     */
    fetchModel(options) {
        return this.fetch(options).pipe(map(({ entity, annots }) => entity ? this.asModel(entity, { annots, reset: true }) : null));
    }
}

class ODataInStorageCache extends ODataCache {
    constructor({ name, storage = sessionStorage, timeout, }) {
        super({ timeout });
        this.name = name;
        this.storage = storage;
        this.restore();
        window.addEventListener('beforeunload', () => this.store());
    }
    /**
     * Store the cache in the storage
     */
    store() {
        this.storage.setItem(this.name, JSON.stringify(Array.from(this.entries.entries())));
    }
    /**
     * Restore the cache from the storage
     */
    restore() {
        this.entries = new Map(JSON.parse(this.storage.getItem(this.name) || '[]'));
    }
    /**
     * Flush the cache and clean the storage
     */
    flush() {
        super.flush();
        this.store();
    }
    /**
     * Store the response in the cache
     * @param req The request with the resource to store the response
     * @param res The response to store in the cache
     */
    putResponse(req, res) {
        const scope = this.scope(req);
        const tags = this.tags(res);
        this.put(req.cacheKey, res.toJSON(), {
            timeout: res.options.maxAge,
            scope,
            tags,
        });
    }
    /**
     * Restore the response from the cache
     * @param req The request with the resource to get the response
     * @returns The response from the cache
     */
    getResponse(req) {
        const scope = this.scope(req);
        const data = this.get(req.cacheKey, { scope });
        return data !== undefined ? ODataResponse.fromJSON(req, data) : undefined;
    }
}

class ODataModelEvent {
    constructor(name, { model, collection, previous, value, field, options, } = {}) {
        this.bubbling = true;
        this.name = name;
        this.model = model;
        this.collection = collection;
        this.previous = previous;
        this.value = value;
        this.options = options;
        this.chain = [
            [
                (this.collection || this.model),
                field || null,
            ],
        ];
    }
    stopPropagation() {
        this.bubbling = false;
    }
    push(model, field) {
        let event = new ODataModelEvent(this.name, {
            model: this.model,
            collection: this.collection,
            previous: this.previous,
            value: this.value,
            options: this.options,
        });
        event.chain = [...this.chain];
        event.chain.splice(0, 0, [model, field]);
        return event;
    }
    visited(model) {
        return (this.chain.some((c) => c[0] === model) &&
            this.chain[this.chain.length - 1][0] !== model);
    }
    get path() {
        return this.chain
            .map(([, field], index) => typeof field === 'number'
            ? `[${field}]`
            : field instanceof ODataModelField
                ? index === 0
                    ? field.name
                    : `.${field.name}`
                : '')
            .join('');
    }
    //Identifies the current model for the event
    get currentModel() {
        const link = this.chain.find((c) => ODataModelOptions.isModel(c[0]));
        return link !== undefined ? link[0] : undefined;
    }
    //Identifies the current collection for the event
    get currentCollection() {
        const link = this.chain.find((c) => ODataModelOptions.isCollection(c[0]));
        return link !== undefined
            ? link[0]
            : undefined;
    }
}
const BUBBLING = [
    'change',
    'reset',
    'update',
    'destroy',
    'add',
    'remove',
];
const INCLUDE_SHALLOW = {
    include_concurrency: true,
    include_computed: true,
    include_key: true,
};
const INCLUDE_DEEP = {
    include_navigation: true,
    include_non_field: true,
    ...INCLUDE_SHALLOW,
};
var ODataModelState;
(function (ODataModelState) {
    ODataModelState[ODataModelState["Added"] = 0] = "Added";
    ODataModelState[ODataModelState["Removed"] = 1] = "Removed";
    ODataModelState[ODataModelState["Changed"] = 2] = "Changed";
    ODataModelState[ODataModelState["Unchanged"] = 3] = "Unchanged";
})(ODataModelState || (ODataModelState = {}));
function Model({ cid = CID_FIELD_NAME } = {}) {
    return (constructor) => {
        const Klass = constructor;
        if (!Klass.hasOwnProperty('options'))
            Klass.options = { fields: {} };
        Klass.options.cid = cid;
        return constructor;
    };
}
function ModelField({ name, ...options } = {}) {
    return (target, key) => {
        const Klass = target.constructor;
        if (!Klass.hasOwnProperty('options'))
            Klass.options = { fields: {} };
        options.field = name || key;
        Klass.options.fields[key] = options;
    };
}
class ODataModelField {
    constructor(options, { name, field, parser, ...opts }) {
        this.options = options;
        this.name = name;
        this.field = field;
        this.parser = parser;
        this.default = opts.default || parser.default;
        this.required = Boolean(opts.required || !parser.nullable);
        this.concurrency = Boolean(opts.concurrency);
        this.maxLength = opts.maxLength || parser.maxLength;
        this.minLength = opts.minLength;
        this.min = opts.min;
        this.max = opts.max;
        this.pattern = opts.pattern;
    }
    get api() {
        return this.options.api;
    }
    get type() {
        return this.parser.type;
    }
    get navigation() {
        return Boolean(this.parser.navigation);
    }
    get collection() {
        return Boolean(this.parser.collection);
    }
    annotatedValue(term) {
        return this.parser.annotatedValue(term);
    }
    configure({ findOptionsForType, concurrency, options, }) {
        this.meta = findOptionsForType(this.parser.type);
        this.parserOptions = options;
        if (concurrency)
            this.concurrency = concurrency;
        if (this.default !== undefined)
            this.default = this.deserialize(this.default, options);
    }
    isKey() {
        return this.parser.isKey();
    }
    hasReferentials() {
        return this.parser.hasReferentials();
    }
    get referentials() {
        return this.parser.referentials;
    }
    isStructuredType() {
        return this.parser.isStructuredType();
    }
    structured() {
        let structuredType = this.api.findStructuredTypeForType(this.parser.type);
        //Throw error if not found
        if (!structuredType)
            throw new Error(`Could not find structured type for ${this.parser.type}`);
        return structuredType;
    }
    isEnumType() {
        return this.parser.isEnumType();
    }
    enum() {
        let enumType = this.api.findEnumTypeForType(this.parser.type);
        //Throw error if not found
        if (!enumType)
            throw new Error(`Could not find enum type for ${this.parser.type}`);
        return enumType;
    }
    validate(value, { method, navigation = false, } = {}) {
        /*
        if ('isValid' in value && typeof value.isValid === 'function') {
          return !value.isValid({ method, navigation }) ? value._errors : undefined;
        } else if ('models' in value && typeof value.models === 'function') {
          */
        if (ODataModelOptions.isModel(value)) {
            return !value.isValid({ method, navigation }) ? value._errors : undefined;
        }
        else if (ODataModelOptions.isCollection(value)) {
            return value
                .models()
                .some((m) => !m.isValid({ method, navigation }))
                ? value.models().map((m) => m._errors)
                : undefined;
        }
        else {
            const computed = this.annotatedValue(COMPUTED);
            let errors = this.parser?.validate(value, { method, navigation }) || [];
            if (this.required &&
                (value === null || (value === undefined && method !== 'modify')) && // Is null or undefined without patch?
                !(computed && method === 'create') // Not (Is Computed field and create) ?
            ) {
                errors['push'](`required`);
            }
            if (this.maxLength !== undefined &&
                typeof value === 'string' &&
                value.length > this.maxLength) {
                errors['push'](`maxlength`);
            }
            if (this.minLength !== undefined &&
                typeof value === 'string' &&
                value.length < this.minLength) {
                errors['push'](`minlength`);
            }
            if (this.min !== undefined &&
                typeof value === 'number' &&
                value < this.min) {
                errors['push'](`min`);
            }
            if (this.max !== undefined &&
                typeof value === 'number' &&
                value > this.max) {
                errors['push'](`max`);
            }
            if (this.pattern !== undefined &&
                typeof value === 'string' &&
                !this.pattern.test(value)) {
                errors['push'](`pattern`);
            }
            return !Types.isEmpty(errors) ? errors : undefined;
        }
    }
    defaults() {
        return this.isStructuredType() && this.meta !== undefined
            ? this.meta.defaults()
            : this.default;
    }
    deserialize(value, options) {
        const parserOptions = options || this.parserOptions;
        return this.parser.deserialize(value, parserOptions);
    }
    serialize(value, options) {
        const parserOptions = options || this.parserOptions;
        return this.parser.serialize(value, parserOptions);
    }
    encode(value, options) {
        const parserOptions = options || this.parserOptions;
        return this.parser.encode(value, parserOptions);
    }
    resourceFactory(base) {
        if (!(base instanceof ODataEntityResource ||
            base instanceof ODataNavigationPropertyResource ||
            base instanceof ODataPropertyResource))
            throw new Error("Can't build resource for non compatible base type");
        return this.navigation
            ? base.navigationProperty(this.parser.name)
            : base.property(this.parser.name);
    }
    annotationsFactory(base) {
        return this.parser.collection ?
            base.property(this.parser.name, 'collection') :
            base.property(this.parser.name, 'single');
    }
    schemaFactory(base) {
        return this.api.findStructuredTypeForType(this.parser.type);
    }
    modelFactory({ parent, value, reset, }) {
        // Model
        const annots = this.annotationsFactory(parent.annots());
        let Model = this.api.modelForType(this.parser.type);
        if (Model === undefined)
            throw Error(`No Model type for ${this.name}`);
        if (value !== undefined) {
            annots.update(value);
        }
        if (annots?.type !== undefined && Model.meta !== null) {
            let schema = Model.meta.findChildOptions((o) => o.isTypeOf(annots.type))?.schema;
            if (schema !== undefined && schema.model !== undefined)
                // Change to child model
                Model = schema.model;
        }
        return new Model((value || {}), {
            annots,
            reset,
            parent: [parent, this],
        });
    }
    collectionFactory({ parent, value, reset, }) {
        // Collection Factory
        const annots = this.annotationsFactory(parent.annots());
        const Collection = this.api.collectionForType(this.parser.type);
        if (Collection === undefined)
            throw Error(`No Collection type for ${this.name}`);
        return new Collection((value || []), {
            annots: annots,
            parent: [parent, this],
            reset,
        });
    }
}
class ODataModelOptions {
    constructor({ options, schema, }) {
        this.children = [];
        this.name = schema.name;
        this.base = schema.base;
        this.open = schema.open;
        this.schema = schema;
        this.cid = options?.cid || CID_FIELD_NAME;
        this._fields = Object.entries(options.fields).map(([name, options]) => {
            const { field, ...opts } = options;
            if (field === undefined || name === undefined)
                throw new Error('Model Properties need name and field');
            const parser = this.schema.field(field);
            if (parser === undefined)
                throw new Error(`No parser for ${field} with name = ${name}`);
            return new ODataModelField(this, { name, field, parser, ...opts });
        });
    }
    get api() {
        return this.schema.api;
    }
    type({ alias = false } = {}) {
        return this.schema.type({ alias });
    }
    isTypeOf(type) {
        return this.schema.isTypeOf(type);
    }
    isModelFor(entity) {
        // Resolve By Type
        let type = this.api.options.helper.type(entity);
        if (type && this.isTypeOf(type))
            return true;
        // Resolve By fields
        let keys = Object.keys(entity);
        let names = this.fields({
            include_navigation: true,
            include_parents: true,
        }).map((f) => f.name);
        return keys.every((key) => names.includes(key));
    }
    findChildOptions(predicate) {
        if (predicate(this))
            return this;
        let match;
        for (let ch of this.children) {
            match = ch.findChildOptions(predicate);
            if (match !== undefined)
                break;
        }
        return match;
    }
    configure({ options, parserForType, findOptionsForType, }) {
        if (this.base) {
            const parent = findOptionsForType(this.base);
            parent.children.push(this);
            this.parent = parent;
        }
        this.entitySet = this.api.findEntitySetForEntityType(this.type());
        let concurrencyFields = [];
        if (this.entitySet !== undefined) {
            concurrencyFields =
                this.entitySet.annotatedValue(OPTIMISTIC_CONCURRENCY) || [];
        }
        this._fields.forEach((field) => {
            let concurrency = concurrencyFields.indexOf(field.field) !== -1;
            field.configure({
                findOptionsForType,
                concurrency,
                options,
            });
        });
    }
    fields({ include_navigation, include_parents, }) {
        return [
            ...(include_parents && this.parent !== undefined
                ? this.parent.fields({ include_navigation, include_parents })
                : []),
            ...this._fields.filter((field) => include_navigation || !field.navigation),
        ];
    }
    field(name) {
        let field = this.fields({
            include_parents: true,
            include_navigation: true,
        }).find((modelField) => modelField.name === name || modelField.field === name);
        //Throw error if not found
        if (field === undefined)
            throw new Error(`No field with name ${name}`);
        return field;
    }
    attach(self, resource) {
        if (self._resource !== null &&
            resource.type() !== self._resource.type() &&
            !self._resource.isSubtypeOf(resource))
            throw new Error(`Can't attach ${resource.type()} to ${self._resource.type()}`);
        const current = self._resource;
        if (current === null || !current.isEqualTo(resource)) {
            self._resource = resource;
            self.events$.emit(new ODataModelEvent('attach', {
                model: self,
                previous: current,
                value: resource,
            }));
        }
    }
    //# region Resource
    static chain(child) {
        const chain = [];
        let tuple = [child, null];
        while (tuple !== null) {
            const parent = tuple;
            if (chain.some((p) => p[0] === parent[0]))
                break;
            chain.splice(0, 0, parent);
            tuple = tuple[0]._parent;
        }
        return chain;
    }
    static resource(child) {
        let resource = null;
        let prevField = null;
        for (let [model, field] of ODataModelOptions.chain(child)) {
            resource = resource || model._resource;
            if (resource === null)
                break;
            if (ODataModelOptions.isModel(model) &&
                (prevField === null || prevField.collection)) {
                // Resolve key
                let modelKey = model.key({
                    field_mapping: true,
                });
                if (modelKey !== undefined)
                    resource =
                        resource instanceof ODataEntitySetResource
                            ? resource.entity(modelKey)
                            : resource.key(modelKey);
            }
            prevField = field;
            if (field === null) {
                // Apply the query from model to new resource
                const query = model._resource?.cloneQuery().toQueryArguments();
                if (query !== undefined)
                    resource.query((q) => q.apply(query));
                continue;
            }
            resource = field.resourceFactory(resource);
        }
        if (resource === null)
            throw new Error(`resource: Can't build resource for ${child}`);
        return resource;
    }
    collectionResourceFactory(query) {
        if (this.entitySet === undefined)
            return undefined;
        return ODataEntitySetResource.factory(this.api, {
            path: this.entitySet.name,
            schema: this.schema,
            query,
        });
    }
    modelResourceFactory(query) {
        const resource = this.collectionResourceFactory(query);
        if (resource instanceof ODataEntitySetResource)
            return resource.entity();
        return resource;
    }
    entityResource(self) {
        let resource = this.modelResourceFactory(self._resource !== null ? self._resource.cloneQuery() : undefined);
        if (resource === undefined)
            throw new Error(`entityResource: Can't build resource for ${self}`);
        const key = self.key({ field_mapping: true });
        if (key !== undefined)
            return resource.key(key);
        return resource;
    }
    //#endregion
    bind(self, { parent, resource, annots, } = {}) {
        // Parent
        if (parent !== undefined) {
            self._parent = parent;
        }
        // Resource
        if (self._parent === null && resource === undefined)
            resource = this.modelResourceFactory();
        if (resource !== undefined) {
            this.attach(self, resource);
        }
        // Annotations
        self._annotations =
            annots || new ODataEntityAnnotations(ODataHelper[DEFAULT_VERSION]);
        const fields = this.fields({
            include_navigation: true,
            include_parents: true,
        });
        for (let field of fields) {
            Object.defineProperty(self, field.name, {
                configurable: true,
                get: () => this._get(self, field),
                set: (value) => this._set(self, field, value),
            });
        }
    }
    query(self, resource, func) {
        resource.query(func);
        this.attach(self, resource);
        return self;
    }
    resolveKey(value, { field_mapping = false, resolve = true, single = true, } = {}) {
        const keyTypes = this.schema.keys({ include_parents: true });
        const key = new Map();
        for (let kt of keyTypes) {
            let v = value;
            let options = this;
            let field;
            for (let name of kt.name.split('/')) {
                if (options === undefined)
                    break;
                field = options
                    .fields({ include_navigation: false, include_parents: true })
                    .find((field) => field.field === name);
                if (field !== undefined) {
                    v =
                        Types.isPlainObject(v) || ODataModelOptions.isModel(v)
                            ? v[field.name]
                            : v;
                    options = field.meta;
                }
            }
            if (field === undefined)
                return undefined;
            let name = field_mapping ? field.field : field.name;
            if (kt.alias !== undefined)
                name = kt.alias;
            key.set(name, v);
        }
        if (key.size === 0)
            return undefined;
        return resolve
            ? Objects.resolveKey(key, { single })
            : Object.fromEntries(key);
    }
    resolveReferential(value, field, { field_mapping = false, resolve = true, single = false, } = {}) {
        const referential = new Map();
        for (let ref of field.referentials) {
            let from = this.fields({
                include_navigation: false,
                include_parents: true,
            }).find((p) => p.field === ref.referencedProperty);
            let to = field.options
                .fields({ include_navigation: false, include_parents: true })
                .find((field) => field.field === ref.property);
            if (from !== undefined && to !== undefined) {
                let name = field_mapping ? to.field : to.name;
                referential.set(name, value && value[from.name]);
            }
        }
        if (referential.size === 0)
            return undefined;
        if (referential.size === 1 && Array.from(referential.values())[0] === null)
            return null;
        return resolve
            ? Objects.resolveKey(referential, { single })
            : Object.fromEntries(referential);
    }
    resolveReferenced(value, field, { field_mapping = false, resolve = true, single = false, } = {}) {
        const referenced = new Map();
        for (let ref of field.referentials) {
            let from = this.fields({
                include_navigation: false,
                include_parents: true,
            }).find((field) => field.field === ref.property);
            let to = field.meta
                .fields({ include_navigation: false, include_parents: true })
                .find((field) => field.field === ref.referencedProperty);
            if (from !== undefined && to !== undefined) {
                let name = field_mapping ? to.field : to.name;
                referenced.set(name, value && value[from.name]);
            }
        }
        if (referenced.size === 0)
            return undefined;
        if (referenced.size === 1 && Array.from(referenced.values())[0] === null)
            return null;
        return resolve
            ? Objects.resolveKey(referenced, { single })
            : Object.fromEntries(referenced);
    }
    validate(self, { method, navigation = false, } = {}) {
        const errors = this.fields({
            include_parents: true,
            include_navigation: navigation,
        }).reduce((acc, field) => {
            let value = self[field.name];
            let errs = field.validate(value, { method });
            return errs !== undefined
                ? Object.assign(acc, { [field.name]: errs })
                : acc;
        }, {});
        return !Types.isEmpty(errors) ? errors : undefined;
    }
    defaults() {
        const defs = this.fields({
            include_navigation: false,
            include_parents: true,
        }).reduce((acc, field) => {
            let value = field.defaults();
            return value !== undefined
                ? Object.assign(acc, { [field.name]: value })
                : acc;
        }, {});
        return !Types.isEmpty(defs) ? defs : undefined;
    }
    hasChanged(self, { include_navigation = false } = {}) {
        return (self._changes.size != 0 ||
            [...self._relations.values()]
                .filter(({ field }) => !field.navigation || include_navigation)
                .some(({ field, state, model }) => (!include_navigation || field.navigation) &&
                (state === ODataModelState.Changed ||
                    (model != undefined && model.hasChanged({ include_navigation })))));
    }
    asEntity(self, func) {
        // Build new resource
        const query = self._resource?.cloneQuery();
        let resource = this.modelResourceFactory(query);
        if (resource === undefined)
            throw new Error('Model does not have associated Entity endpoint');
        // Store parent and resource
        const store = { parent: self._parent, resource: self._resource };
        // Replace parent and resource
        self._parent = null;
        self._resource = resource;
        // Execute function
        const result = func(self);
        if (result instanceof Observable) {
            return result.pipe(finalize(() => {
                // Restore parent and resource
                self._parent = store.parent;
                self._resource = store.resource;
            }));
        }
        else {
            // Restore parent and resource
            self._parent = store.parent;
            self._resource = store.resource;
            return result;
        }
    }
    toEntity(self, { client_id = false, include_navigation = false, include_concurrency = false, include_computed = false, include_key = true, include_non_field = false, changes_only = false, field_mapping = false, chain = [], } = {}) {
        let attrs = self.attributes({
            changes_only,
            field_mapping,
            include_concurrency,
            include_computed,
            include_non_field,
        });
        let relations = [...self._relations.entries()]
            .filter(
        // Chain
        ([, { model }]) => chain.every((c) => c !== model))
            .filter(
        // Changes only
        ([, { model, state }]) => !changes_only ||
            (changes_only &&
                (state === ODataModelState.Changed ||
                    (model != undefined && model.hasChanged({ include_navigation })))))
            .filter(([, { field, model }]) => 
        // Navigation
        (include_navigation && field.navigation && model !== null) ||
            !field.navigation)
            .map(([k, { model, field, state }]) => {
            let changesOnly = changes_only &&
                state !== ODataModelState.Changed &&
                !!field.navigation;
            let includeKey = include_key && !!field.navigation;
            if (ODataModelOptions.isModel(model)) {
                return [
                    k,
                    model.toEntity({
                        client_id,
                        include_navigation,
                        include_concurrency,
                        include_non_field,
                        field_mapping,
                        changes_only: changesOnly,
                        include_key: includeKey,
                        chain: [self, ...chain],
                    }),
                ];
            }
            else if (ODataModelOptions.isCollection(model)) {
                return [
                    k,
                    model.toEntities({
                        client_id,
                        include_navigation,
                        include_concurrency,
                        include_non_field,
                        field_mapping,
                        changes_only: changesOnly,
                        include_key: includeKey,
                        chain: [self, ...chain],
                    }),
                ];
            }
            return [k, model];
        })
            .reduce((acc, [k, v]) => {
            const name = field_mapping
                ? this.fields({
                    include_navigation: false,
                    include_parents: true,
                }).find((field) => field.name === k)?.field ||
                    k
                : k;
            return Object.assign(acc, { [name]: v });
        }, {});
        // Create entity
        let entity = { ...attrs, ...relations };
        // Add client_id
        if (client_id) {
            entity[this.cid] = self[this.cid];
        }
        // Add key
        if (include_key) {
            entity = {
                ...entity,
                ...this.resolveKey(self, { field_mapping, resolve: false }),
            };
        }
        // Add type
        if (self._parent !== null &&
            ((ODataModelOptions.isModel(self._parent[0]) &&
                self._parent[1].meta !== self._meta) ||
                (ODataModelOptions.isCollection(self._parent[0]) &&
                    self._parent[0]._model
                        .meta !== self._meta))) {
            entity[this.api.options.helper.ODATA_TYPE] = `#${this.schema.type()}`;
        }
        return entity;
    }
    attributes(self, { changes_only = false, include_concurrency = false, include_computed = false, include_non_field = false, field_mapping = false, } = {}) {
        // Attributes by fields (attributes for the model type)
        const fieldAttrs = this.fields({
            include_navigation: false,
            include_parents: true,
        }).reduce((acc, f) => {
            const isChanged = self._changes.has(f.name);
            const name = field_mapping ? f.field : f.name;
            const computed = f.annotatedValue(COMPUTED);
            const value = isChanged
                ? self._changes.get(f.name)
                : self._attributes.get(f.name);
            if (f.concurrency && include_concurrency) {
                return Object.assign(acc, { [name]: value });
            }
            else if (computed && include_computed) {
                return Object.assign(acc, { [name]: value });
            }
            else if (changes_only && isChanged) {
                return Object.assign(acc, { [name]: value });
            }
            else if (!changes_only && !f.concurrency && !computed) {
                return Object.assign(acc, { [name]: value });
            }
            else {
                return acc;
            }
        }, {});
        if (!include_non_field)
            return fieldAttrs;
        const names = Object.keys(fieldAttrs);
        // Attributes from object (attributes for object)
        const nonFieldAttrs = Object.entries(self)
            .filter(([k]) => names.indexOf(k) === -1 && !k.startsWith('_') && !k.endsWith('$'))
            .reduce((acc, [k, v]) => Object.assign(acc, { [k]: v }), {});
        return { ...fieldAttrs, ...nonFieldAttrs };
    }
    reset(self, { name, silent = false } = {}) {
        let changes = [];
        if (name !== undefined) {
            // Reset value
            const value = self._attributes.get(name);
            const change = self._changes.get(name);
            if (value !== change)
                changes = [name];
            self._relations.delete(name);
            self._changes.delete(name);
        }
        else {
            // reset all
            changes = [...self._changes.keys()];
            self._relations.clear();
            self._changes.clear();
        }
        if (!silent && changes.length > 0) {
            self.events$.emit(new ODataModelEvent('reset', {
                model: self,
                options: { changes },
            }));
        }
    }
    assign(self, entity, { reset = false, reparent = false, silent = false, } = {}) {
        self._reset = reset;
        self._reparent = reparent;
        self._silent = silent;
        const changes = [];
        // Apply entity
        const model = self;
        Object.entries(entity)
            .filter(([, value]) => value !== undefined) // Filter undefined
            .forEach(([key, value]) => {
            const field = this.fields({
                include_parents: true,
                include_navigation: true,
            }).find((field) => (self._reset && field.field === key) || field.name === key);
            if (field !== undefined) {
                // Delegated to private setter
                if (this._set(self, field, value)) {
                    changes.push(field.name);
                }
            }
            else {
                // Basic assignment
                const current = model[key];
                model[key] = value;
                if (current !== value)
                    changes.push(key);
            }
        });
        if ((!self._silent && changes.length > 0) || self._reset) {
            self.events$.emit(new ODataModelEvent(self._reset ? 'reset' : 'update', {
                model: self,
                options: { changes },
            }));
        }
        self._reset = false;
        self._reparent = false;
        self._silent = false;
    }
    static isModel(obj) {
        return Types.rawType(obj) === 'Model';
    }
    static isCollection(obj) {
        return Types.rawType(obj) === 'Collection';
    }
    updateCollection(self, field, collection, value) {
        collection._annotations = field.annotationsFactory(self.annots());
        collection.assign(value, {
            reset: self._reset,
            reparent: self._reparent,
            silent: self._silent,
        });
        return collection.hasChanged();
    }
    updateModel(self, field, model, value) {
        model._annotations = field.annotationsFactory(self.annots());
        model.assign(value, {
            reset: self._reset,
            reparent: self._reparent,
            silent: self._silent,
        });
        return model.hasChanged();
    }
    _get(self, field) {
        if (field.isStructuredType()) {
            const relation = self._relations.get(field.name);
            if (field.navigation &&
                (relation?.model === null || ODataModelOptions.isModel(relation?.model))) {
                // Check for reference
                const referenced = this.resolveReferenced(self, field);
                if (relation?.model !== null &&
                    referenced !== null &&
                    referenced !== undefined) {
                    relation.model.assign(referenced, {
                        silent: true,
                    });
                }
                else if (relation?.model !== null && referenced === null) {
                    this._unlink(self, relation);
                    // New value is null
                    relation.model = null;
                }
                else if (relation?.model === null && referenced !== null) {
                    // New value is undefined
                    relation.model = undefined;
                }
            }
            return relation?.model;
        }
        else {
            return this.attributes(self, {
                include_concurrency: true,
                include_computed: true,
            })[field.name];
        }
    }
    _setStructured(self, field, value) {
        let changed = false;
        // Ensures that the relation exists
        if (!self._relations.has(field.name)) {
            self._relations.set(field.name, {
                state: ODataModelState.Unchanged,
                field: field,
            });
        }
        const relation = self._relations.get(field.name);
        const current = relation.model;
        if (value === null) {
            // Unlink old relation
            this._unlink(self, relation);
            // New value is null
            relation.model = value;
            changed = current !== value;
        }
        else if (ODataModelOptions.isCollection(current)) {
            // Current is collection
            let currentCollection = current;
            if (ODataModelOptions.isCollection(value)) {
                // New value is collection
                let newCollection = value;
                if (currentCollection.equals(newCollection)) {
                    changed = false;
                }
                else {
                    // Unlink old collection
                    this._unlink(self, relation);
                    relation.model = newCollection;
                    // Link new collection
                    this._link(self, relation);
                    changed = true;
                }
            }
            else if (Types.isArray(value)) {
                // New value is array
                changed = this.updateCollection(self, field, currentCollection, value);
            }
        }
        else if (ODataModelOptions.isModel(current)) {
            // Current is model
            let currentModel = current;
            if (ODataModelOptions.isModel(value)) {
                // New value is model
                let newModel = value;
                if (currentModel.equals(newModel)) {
                    changed = false;
                }
                else {
                    // Unlink old model
                    this._unlink(self, relation);
                    relation.model = newModel;
                    // Link new model
                    this._link(self, relation);
                    changed = true;
                }
            }
            else if (Types.isPlainObject(value)) {
                changed = this.updateModel(self, field, currentModel, value);
            }
        }
        else {
            // Current is null or undefined
            // create new model/collection for given value
            relation.model =
                ODataModelOptions.isCollection(value) ||
                    ODataModelOptions.isModel(value)
                    ? value
                    : field.collection
                        ? field.collectionFactory({
                            parent: self,
                            value: value,
                            reset: self._reset,
                        })
                        : field.modelFactory({
                            parent: self,
                            value: value,
                            reset: self._reset,
                        });
            // Link new model/collection
            this._link(self, relation);
            changed = true;
        }
        // Resolve referentials
        if (!ODataModelOptions.isCollection(relation.model)) {
            let ref = field.meta?.resolveReferential(relation.model, field);
            if (ref !== null && ref !== undefined) {
                Object.assign(self, ref);
            }
        }
        // Update state and emit event
        relation.state =
            self._reset || !changed
                ? ODataModelState.Unchanged
                : ODataModelState.Changed;
        if (!self._silent && changed) {
            self.events$.emit(new ODataModelEvent('change', {
                field: field,
                model: self,
                value: relation.model,
                previous: current,
            }));
        }
        return changed;
    }
    _setValue(self, field, value, key) {
        let changed = false;
        const attrs = this.attributes(self, {
            include_concurrency: true,
            include_computed: true,
        });
        const name = field.name;
        const currentValue = attrs[name];
        changed = !Types.isEqual(currentValue, value);
        if (self._reset) {
            self._changes.delete(name);
            self._attributes.set(name, value);
        }
        else if (Types.isEqual(value, self._attributes.get(name))) {
            self._changes.delete(name);
        }
        else if (changed) {
            self._changes.set(name, value);
        }
        if (!self._silent && changed) {
            self.events$.emit(new ODataModelEvent('change', {
                field: field,
                model: self,
                value,
                previous: currentValue,
                options: { key },
            }));
        }
        return changed;
    }
    _set(self, field, value) {
        return field.isStructuredType()
            ? this._setStructured(self, field, value)
            : this._setValue(self, field, value, field.isKey());
    }
    _unlink(self, relation) {
        if (relation.subscription !== undefined) {
            relation.subscription.unsubscribe();
            relation.subscription = undefined;
        }
        if (relation.model != null) {
            relation.model._parent = null;
        }
    }
    _link(self, relation) {
        if (relation.subscription) {
            throw new Error('Subscription already exists');
        }
        if (relation.model == null) {
            throw new Error('Subscription model is null');
        }
        if (self._reparent)
            relation.model._parent = [self, relation.field];
        relation.subscription = relation.model.events$.subscribe((event) => {
            if (BUBBLING.indexOf(event.name) !== -1 &&
                event.bubbling &&
                !event.visited(self)) {
                if (event.model === relation.model) {
                    if (event.name === 'change' &&
                        relation.field.navigation &&
                        event.options?.key) {
                        let ref = relation.model.referential(relation.field);
                        if (ref !== null && ref !== undefined) {
                            Object.assign(self, ref);
                        }
                    }
                }
                self.events$.emit(event.push(self, relation.field));
            }
        });
    }
}

class ODataCollection {
    constructor(entities = [], { parent, resource, annots, model, reset = false, } = {}) {
        this._parent = null;
        this._resource = null;
        this._entries = [];
        //Events
        this.events$ = new EventEmitter();
        this._sortBy = null;
        const Klass = this.constructor;
        if (model === undefined && Klass.model !== null)
            model = Klass.model;
        if (model === undefined)
            throw new Error('Collection: Collection need model');
        this._model = model;
        // Parent
        if (parent !== undefined) {
            this._parent = parent;
        }
        // Resource
        if (this._parent === null && resource === undefined)
            resource = this._model.meta.collectionResourceFactory();
        if (resource !== undefined) {
            this.attach(resource);
        }
        // Annotations
        this._annotations =
            annots ||
                new ODataEntitiesAnnotations(ODataHelper[resource?.api?.options.version || DEFAULT_VERSION]);
        entities = entities || [];
        this.assign(entities, { reset });
    }
    models() {
        return this._entries
            .filter((e) => e.state !== ODataModelState.Removed)
            .map((e) => e.model);
    }
    get length() {
        return this.models().length;
    }
    isParentOf(child) {
        return (child !== this &&
            ODataModelOptions.chain(child).some((p) => p[0] === this));
    }
    resource() {
        return ODataModelOptions.resource(this);
    }
    attach(resource) {
        if (this._resource !== null &&
            this._resource.type() !== resource.type() &&
            !this._resource.isSubtypeOf(resource))
            throw new Error(`attach: Can't reattach ${this._resource.type()} to ${resource.type()}`);
        this._entries.forEach(({ model }) => {
            const mr = this._model.meta.modelResourceFactory(resource.cloneQuery());
            model.attach(mr);
        });
        const current = this._resource;
        if (current === null || !current.isEqualTo(resource)) {
            this._resource = resource;
            this.events$.emit(new ODataModelEvent('attach', {
                collection: this,
                previous: current,
                value: resource,
            }));
        }
    }
    asEntitySet(func) {
        // Build new resource
        const query = this._resource?.cloneQuery();
        let resource = this._model.meta.collectionResourceFactory(query);
        if (resource === undefined)
            throw new Error('asEntitySet: Collection does not have associated EntitySet endpoint');
        // Store parent and resource
        const store = { parent: this._parent, resource: this._resource };
        // Replace parent and resource
        this._parent = null;
        this._resource = resource;
        // Execute
        const result = func(this);
        if (result instanceof Observable) {
            return result.pipe(finalize(() => {
                // Restore
                this._parent = store.parent;
                this._resource = store.resource;
            }));
        }
        else {
            // Restore
            this._parent = store.parent;
            this._resource = store.resource;
            return result;
        }
    }
    annots() {
        return this._annotations;
    }
    modelFactory(data, { reset = false } = {}) {
        let Model = this._model;
        const annots = new ODataEntityAnnotations(this._annotations.helper);
        annots.update(data);
        if (annots?.type !== undefined && Model.meta !== null) {
            let schema = Model.meta.findChildOptions((o) => o.isTypeOf(annots.type))?.schema;
            if (schema !== undefined && schema.model !== undefined)
                // Change to child model
                Model = schema.model;
        }
        return new Model(data, {
            annots,
            reset,
            parent: [this, null],
        });
    }
    toEntities({ client_id = false, include_navigation = false, include_concurrency = false, include_computed = false, include_key = true, include_non_field = false, changes_only = false, field_mapping = false, chain = [], } = {}) {
        return this._entries
            .filter(({ model, state }) => state !== ODataModelState.Removed && chain.every((c) => c !== model))
            .map(({ model, state }) => {
            var changesOnly = changes_only && state !== ODataModelState.Added;
            return model.toEntity({
                client_id,
                include_navigation,
                include_concurrency,
                include_computed,
                include_key,
                include_non_field,
                field_mapping,
                changes_only: changesOnly,
                chain: [this, ...chain],
            });
        });
    }
    toJSON() {
        return this.toEntities();
    }
    hasChanged({ include_navigation } = {}) {
        return (this._entries.some((e) => e.state !== ODataModelState.Unchanged) ||
            this.models().some((m) => m.hasChanged({ include_navigation })));
    }
    clone() {
        let Ctor = this.constructor;
        return new Ctor(this.toEntities(INCLUDE_SHALLOW), {
            resource: this.resource(),
            annots: this.annots(),
        });
    }
    _request(obs$) {
        this.events$.emit(new ODataModelEvent('request', { collection: this, options: { observable: obs$ } }));
        return obs$.pipe(map(({ entities, annots }) => {
            this._annotations = annots;
            this.assign(entities || [], { reset: true });
            this.events$.emit(new ODataModelEvent('sync', { collection: this, options: { entities, annots } }));
            return this;
        }));
    }
    fetch({ withCount, ...options } = {}) {
        const resource = this.resource();
        const obs$ = resource instanceof ODataEntitySetResource
            ? resource.fetch({ withCount, ...options })
            : resource.fetch({
                responseType: 'entities',
                withCount,
                ...options,
            });
        return this._request(obs$);
    }
    fetchMany(top, { withCount, ...options } = {}) {
        const resource = this.resource();
        const obs$ = resource.fetchMany(top, { withCount, ...options });
        return this._request(obs$);
    }
    fetchAll({ withCount, ...options } = {}) {
        const resource = this.resource();
        const obs$ = resource.fetchAll({ withCount, ...options });
        return this._request(obs$);
    }
    /**
     * Save all models in the collection
     * @param relModel The model is relationship
     * @param method The method to use
     * @param options HttpOptions
     */
    save({ relModel = false, method, ...options } = {}) {
        const resource = this.resource();
        if (resource instanceof ODataPropertyResource)
            return throwError(() => new Error('save: Resource is ODataPropertyResource'));
        let toDestroyEntity = [];
        let toRemoveReference = [];
        let toDestroyContained = [];
        let toCreateEntity = [];
        let toAddReference = [];
        let toCreateContained = [];
        let toUpdateEntity = [];
        let toUpdateContained = [];
        this._entries.forEach(({ model, state }) => {
            if (state === ODataModelState.Removed) {
                if (relModel) {
                    toDestroyEntity.push(model);
                }
                else if (!model.isNew()) {
                    toRemoveReference.push(model);
                }
                else {
                    toDestroyContained.push(model);
                }
            }
            else if (state === ODataModelState.Added) {
                if (relModel) {
                    toCreateEntity.push(model);
                }
                else if (!model.isNew()) {
                    toAddReference.push(model);
                }
                else {
                    toCreateContained.push(model);
                }
            }
            else if (model.hasChanged()) {
                toUpdateEntity.push(model);
            }
        });
        if (toDestroyEntity.length > 0 ||
            toRemoveReference.length > 0 ||
            toDestroyContained.length > 0 ||
            toCreateEntity.length > 0 ||
            toAddReference.length > 0 ||
            toCreateContained.length > 0 ||
            toUpdateEntity.length > 0 ||
            toUpdateContained.length > 0) {
            const obs$ = forkJoin([
                ...toDestroyEntity.map((m) => m.asEntity((e) => e.destroy(options))),
                ...toRemoveReference.map((m) => this.removeReference(m, options)),
                ...toDestroyContained.map((m) => m.destroy(options)),
                ...toCreateEntity.map((m) => m.asEntity((e) => e.save({ method: 'create', ...options }))),
                ...toAddReference.map((m) => this.addReference(m, options)),
                ...toCreateContained.map((m) => m.save({ method: 'create', ...options })),
                ...toUpdateEntity.map((m) => m.asEntity((e) => e.save({ method, ...options }))),
                ...toUpdateContained.map((m) => m.save({ method, ...options })),
            ]);
            this.events$.emit(new ODataModelEvent('request', {
                collection: this,
                options: { observable: obs$ },
            }));
            return obs$.pipe(map(() => {
                this._entries = this._entries
                    .filter((entry) => entry.state !== ODataModelState.Removed)
                    .map((entry) => ({ ...entry, state: ODataModelState.Unchanged }));
                this.events$.emit(new ODataModelEvent('sync', { collection: this }));
                return this;
            }));
        }
        return of(this);
    }
    addReference(model, options) {
        const resource = this.resource();
        if (!model.isNew() && resource instanceof ODataNavigationPropertyResource) {
            return resource
                .reference()
                .add(model._meta.entityResource(model), options)
                .pipe(map(() => model));
        }
        return of(model);
    }
    _addModel(model, { silent = false, reset = false, reparent = false, merge = false, position = -1, } = {}) {
        const key = model.key();
        let entry = this._findEntry({
            model,
            key,
            cid: model[this._model.meta.cid],
        });
        if (entry !== undefined && entry.state !== ODataModelState.Removed) {
            if (merge) {
                entry.model.assign(model.toEntity());
            }
            return entry.model;
        }
        if (entry !== undefined && entry.state === ODataModelState.Removed) {
            const index = this._entries.indexOf(entry);
            this._entries.splice(index, 1);
        }
        // Create Entry
        entry = {
            state: reset ? ODataModelState.Unchanged : ODataModelState.Added,
            model,
            key: model.key(),
        };
        // Set Parent
        if (reparent)
            model._parent = [this, null];
        // Subscribe
        this._link(entry);
        // Now add
        if (position >= 0)
            this._entries.splice(position, 0, entry);
        else
            this._entries.push(entry);
        if (!silent) {
            model.events$.emit(new ODataModelEvent('add', { model, collection: this }));
        }
        return entry.model;
    }
    addModel(model, { silent = false, reset = false, reparent = false, merge = false, position = -1, } = {}) {
        if (position < 0)
            position = this._bisect(model);
        const added = this._addModel(model, {
            silent,
            reset,
            merge,
            position,
            reparent,
        });
        if (!silent && added !== undefined) {
            this.events$.emit(new ODataModelEvent('update', {
                collection: this,
                options: { added: [added], removed: [], merged: [] },
            }));
        }
        return added;
    }
    add(model, { silent = false, reparent = false, server = true, merge = false, position = -1, } = {}) {
        if (server) {
            return this.addReference(model).pipe(map((model) => {
                return this.addModel(model, {
                    silent,
                    position,
                    reparent,
                    merge,
                    reset: true,
                });
            }));
        }
        else {
            return of(this.addModel(model, { silent, position, merge, reparent }));
        }
    }
    removeReference(model, options) {
        let resource = this.resource();
        if (!model.isNew() && resource instanceof ODataNavigationPropertyResource) {
            let target = this._model.meta.api.options.deleteRefBy === 'id'
                ? model._meta.entityResource(model)
                : undefined;
            if (this._model.meta.api.options.deleteRefBy === 'path') {
                resource = resource.key(model.key());
            }
            return resource
                .reference()
                .remove(target, options)
                .pipe(map(() => model));
        }
        return of(model);
    }
    _removeModel(model, { silent = false, reset = false, } = {}) {
        const key = model.key();
        let entry = this._findEntry({
            model,
            key,
            cid: model[this._model.meta.cid],
        });
        if (entry === undefined || entry.state === ODataModelState.Removed) {
            return model;
        }
        // Emit Event
        if (!silent)
            model.events$.emit(new ODataModelEvent('remove', { model, collection: this }));
        // Now remove
        const index = this._entries.indexOf(entry);
        this._entries.splice(index, 1);
        if (!(reset || entry.state === ODataModelState.Added)) {
            // Move to end of array and mark as removed
            entry.state = ODataModelState.Removed;
            this._entries.push(entry);
        }
        this._unlink(entry);
        return entry.model;
    }
    removeModel(model, { silent = false, reset = false, } = {}) {
        const removed = this._removeModel(model, { silent, reset });
        if (!silent && removed !== undefined) {
            this.events$.emit(new ODataModelEvent('update', {
                collection: this,
                options: { added: [], removed: [removed], merged: [] },
            }));
        }
        return removed;
    }
    remove(model, { silent = false, server = true, } = {}) {
        if (server) {
            return this.removeReference(model).pipe(map((model) => this.removeModel(model, { silent, reset: true })));
        }
        else {
            return of(this.removeModel(model, { silent }));
        }
    }
    create(attrs = {}, { silent = false, server = true, } = {}) {
        const model = this.modelFactory(attrs);
        return (model.isValid() && server ? model.save() : of(model)).pipe(switchMap((model) => this.add(model, { silent, server })), map(() => model));
    }
    set(path, value) {
        const Model = this._model;
        const pathArray = (Types.isArray(path) ? path : path.match(/([^[.\]])+/g));
        if (pathArray.length === 0)
            return undefined;
        if (pathArray.length > 1) {
            const model = this._entries[Number(pathArray[0])].model;
            return model.set(pathArray.slice(1), value);
        }
        if (pathArray.length === 1 && ODataModelOptions.isModel(value)) {
            let toAdd = [];
            let toChange = [];
            let toRemove = [];
            let index = Number(pathArray[0]);
            const model = this.models()[index];
            const entry = this._findEntry({ model });
            if (entry !== undefined) {
                //TODO: Remove/Add or Merge?
                // Merge
                entry.model.assign(value.toEntity({ client_id: true, ...INCLUDE_DEEP }));
                if (entry.model.hasChanged())
                    toChange.push(model);
            }
            else {
                // Add
                this._addModel(value, { reparent: true });
                toAdd.push(model);
            }
            this.events$.emit(new ODataModelEvent('update', {
                collection: this,
                options: { added: toAdd, removed: toRemove, changed: toChange },
            }));
            return value;
        }
    }
    get(path) {
        const Model = this._model;
        const pathArray = (Types.isArray(path) ? path : `${path}`.match(/([^[.\]])+/g));
        if (pathArray.length === 0)
            return undefined;
        const value = this.models()[Number(pathArray[0])];
        if (pathArray.length > 1 && ODataModelOptions.isModel(value)) {
            return value.get(pathArray.slice(1));
        }
        return value;
    }
    reset({ path, silent = false, } = {}) {
        let toAdd = [];
        let toChange = [];
        let toRemove = [];
        if (path !== undefined) {
            // Reset by path
            const pathArray = (Types.isArray(path) ? path : `${path}`.match(/([^[.\]])+/g));
            const index = Number(pathArray[0]);
            if (!Number.isNaN(index)) {
                const model = this.models()[index];
                if (ODataModelOptions.isModel(model)) {
                    const entry = this._findEntry({ model });
                    if (entry.state === ODataModelState.Changed ||
                        (entry.state === ODataModelState.Unchanged &&
                            entry.model.hasChanged())) {
                        toChange = [entry];
                    }
                    path = pathArray.slice(1);
                }
            }
        }
        else {
            // Reset all
            toAdd = this._entries.filter((e) => e.state === ODataModelState.Removed);
            toChange = this._entries.filter((e) => e.state === ODataModelState.Changed ||
                (e.state === ODataModelState.Unchanged && e.model.hasChanged()));
            toRemove = this._entries.filter((e) => e.state === ODataModelState.Added);
        }
        toRemove.forEach((entry) => {
            this._removeModel(entry.model, { silent });
        });
        toAdd.forEach((entry) => {
            this._addModel(entry.model, { silent });
        });
        toChange.forEach((entry) => {
            entry.model.reset({ path, silent });
            entry.state = ODataModelState.Unchanged;
        });
        if (!silent &&
            (toAdd.length > 0 || toRemove.length > 0 || toChange.length > 0)) {
            this.events$.emit(new ODataModelEvent('reset', {
                collection: this,
                options: {
                    added: toAdd.map((e) => e.model),
                    removed: toRemove.map((e) => e.model),
                    changed: toChange.map((e) => e.model),
                },
            }));
        }
    }
    clear({ silent = false } = {}) {
        let toRemove = this.models();
        toRemove.forEach((m) => {
            this._removeModel(m, { silent });
        });
        this._entries = [];
        if (!silent) {
            this.events$.emit(new ODataModelEvent('update', {
                collection: this,
                options: { removed: toRemove },
            }));
        }
    }
    assign(objects, { reset = false, reparent = false, silent = false, } = {}) {
        const Model = this._model;
        let toAdd = [];
        let toChange = [];
        let toRemove = [];
        let toSort = [];
        let modelMap = [];
        objects.forEach((obj, index) => {
            const isModel = ODataModelOptions.isModel(obj);
            const key = Model !== null && Model.meta ? Model.meta.resolveKey(obj) : undefined;
            const cid = Model.meta.cid in obj ? obj[Model.meta.cid] : undefined;
            // Try find entry
            const entry = isModel
                ? this._findEntry({ model: obj }) // By Model
                : this._findEntry({ cid, key }); // By Cid or Key
            let model;
            if (entry !== undefined) {
                // Merge
                model = entry.model;
                if (model !== obj) {
                    // Get entity from model
                    if (isModel) {
                        const entity = obj.toEntity({
                            client_id: true,
                            ...INCLUDE_DEEP,
                        });
                        model.assign(entity, { reset, silent });
                    }
                    else {
                        const helper = this._annotations.helper;
                        const annots = new ODataEntityAnnotations(helper, helper.annotations(obj));
                        const entity = annots.attributes(obj, 'full');
                        model._annotations = annots;
                        model.assign(entity, { reset, silent });
                    }
                    // Model Change?
                    if (model.hasChanged())
                        toChange.push(model);
                }
                // Has Sort or Index Change?
                if (toSort.length > 0 || index !== this.models().indexOf(model)) {
                    toSort.push([model, index]);
                }
            }
            else {
                // Add
                model = isModel
                    ? obj
                    : this.modelFactory(obj, {
                        reset,
                    });
                toAdd.push([model, index]);
            }
            modelMap.push(model[Model.meta.cid]);
        });
        this._entries
            .filter((e) => modelMap.indexOf(e.model[Model.meta.cid]) === -1)
            .forEach((entry) => {
            toRemove.push(entry.model);
        });
        toRemove.forEach((m) => {
            this._removeModel(m, { silent, reset });
        });
        toAdd.forEach((m) => {
            this._addModel(m[0], { silent, reset, reparent, position: m[1] });
        });
        toSort.forEach((m) => {
            this._removeModel(m[0], { silent: true, reset });
            this._addModel(m[0], { silent: true, reset, position: m[1], reparent });
        });
        if ((!silent &&
            (toAdd.length > 0 ||
                toRemove.length > 0 ||
                toChange.length > 0 ||
                toSort.length > 0)) ||
            reset) {
            this._sortBy = null;
            this.events$.emit(new ODataModelEvent(reset ? 'reset' : 'update', {
                collection: this,
                options: {
                    added: toAdd,
                    removed: toRemove,
                    changed: toChange,
                    sorted: toSort,
                },
            }));
        }
    }
    query(func) {
        const resource = this.resource();
        resource.query(func);
        this.attach(resource);
        return this;
    }
    callFunction(name, params, responseType, { ...options } = {}) {
        const resource = this.resource();
        if (resource instanceof ODataEntitySetResource) {
            const func = resource.function(name);
            func.query((q) => q.apply(options));
            switch (responseType) {
                case 'property':
                    return func.callProperty(params, options);
                case 'model':
                    return func.callModel(params, options);
                case 'collection':
                    return func.callCollection(params, options);
                default:
                    return func.call(params, { responseType, ...options });
            }
        }
        return throwError(() => new Error(`callFunction: Can't function without ODataEntitySetResource`));
    }
    callAction(name, params, responseType, { ...options } = {}) {
        const resource = this.resource();
        if (!(resource instanceof ODataEntitySetResource)) {
            return throwError(() => new Error(`callAction: Can't action without ODataEntitySetResource`));
        }
        const action = resource.action(name);
        action.query((q) => q.apply(options));
        switch (responseType) {
            case 'property':
                return action.callProperty(params, options);
            case 'model':
                return action.callModel(params, options);
            case 'collection':
                return action.callCollection(params, options);
            default:
                return action.call(params, { responseType, ...options });
        }
    }
    _unlink(entry) {
        if (entry.subscription) {
            entry.subscription.unsubscribe();
            entry.subscription = undefined;
        }
    }
    _link(entry) {
        if (entry.subscription) {
            throw new Error('Collection: Subscription already exists');
        }
        entry.subscription = entry.model.events$.subscribe((event) => {
            if (BUBBLING.indexOf(event.name) !== -1 &&
                event.bubbling &&
                !event.visited(this)) {
                if (event.model === entry.model) {
                    if (event.name === 'destroy') {
                        this.removeModel(entry.model, { reset: true });
                    }
                    else if (event.name === 'change' && event.options?.key) {
                        entry.key = entry.model.key();
                    }
                }
                const index = this.models().indexOf(entry.model);
                this.events$.emit(event.push(this, index));
            }
        });
    }
    _findEntry({ model, cid, key, } = {}) {
        return this._entries.find((entry) => {
            const byModel = model !== undefined && entry.model.equals(model);
            const byCid = cid !== undefined && entry.model[this._model.meta.cid] === cid;
            const byKey = key !== undefined &&
                entry.key !== undefined &&
                Types.isEqual(entry.key, key);
            return byModel || byCid || byKey;
        });
    }
    // Collection functions
    equals(other) {
        return this === other;
    }
    get [Symbol.toStringTag]() {
        return 'Collection';
    }
    [Symbol.iterator]() {
        let pointer = 0;
        let models = this.models();
        return {
            next() {
                return {
                    done: pointer === models.length,
                    value: models[pointer++],
                };
            },
        };
    }
    filter(predicate, thisArg) {
        return this.models().filter(predicate);
    }
    map(callbackfn, thisArg) {
        return this.models().map(callbackfn, thisArg);
    }
    find(predicate, thisArg) {
        return this.models().find(predicate);
    }
    reduce(callbackfn, initialValue) {
        return this.models().reduce(callbackfn, initialValue);
    }
    first() {
        return this.models()[0];
    }
    last() {
        const models = this.models();
        return models[models.length - 1];
    }
    next(model) {
        const index = this.indexOf(model);
        if (index === -1 || index === this.length - 1) {
            return undefined;
        }
        return this.get(index + 1);
    }
    prev(model) {
        const index = this.indexOf(model);
        if (index <= 0) {
            return undefined;
        }
        return this.get(index - 1);
    }
    every(predicate) {
        return this.models().every(predicate);
    }
    some(predicate) {
        return this.models().some(predicate);
    }
    contains(model) {
        return this.some((m) => m.equals(model));
    }
    indexOf(model) {
        const models = this.models();
        const m = models.find((m) => m.equals(model));
        return m === undefined ? -1 : models.indexOf(m);
    }
    isEmpty() {
        // Local length and if exists remote length
        return (this.length === 0 &&
            this.annots().count !== undefined &&
            this.annots().count === 0);
    }
    //#region Sort
    _bisect(model) {
        let index = -1;
        if (this._sortBy !== null) {
            for (index = 0; index < this._entries.length; index++) {
                if (this._compare(model, this._entries[index], this._sortBy, 0) < 0) {
                    return index;
                }
            }
        }
        return index;
    }
    _compare(e1, e2, by, index) {
        let m1 = ODataModelOptions.isModel(e1)
            ? e1
            : e1.model;
        let m2 = ODataModelOptions.isModel(e2)
            ? e2
            : e2.model;
        let value1 = m1.get(by[index].field);
        let value2 = m2.get(by[index].field);
        let result = 0;
        if (value1 == null && value2 != null)
            result = -1;
        else if (value1 != null && value2 == null)
            result = 1;
        else if (value1 == null && value2 == null)
            result = 0;
        else if (typeof value1 == 'string' || value1 instanceof String) {
            if (value1.localeCompare && value1 != value2) {
                return (by[index].order || 1) * value1.localeCompare(value2);
            }
        }
        else {
            result = value1 < value2 ? -1 : 1;
        }
        if (value1 == value2) {
            return by.length - 1 > index ? this._compare(e1, e2, by, index + 1) : 0;
        }
        return (by[index].order || 1) * result;
    }
    isSorted() {
        return this._sortBy !== null;
    }
    sort(by, { silent } = {}) {
        this._sortBy = by;
        this._entries = this._entries.sort((e1, e2) => this._compare(e1, e2, by, 0));
        if (!silent) {
            this.events$.emit(new ODataModelEvent('update', {
                collection: this,
            }));
        }
    }
}
ODataCollection.model = null;

// @dynamic
class ODataModel {
    constructor(data = {}, { parent, resource, annots, reset = false, } = {}) {
        // Parent
        this._parent = null;
        this._attributes = new Map();
        this._changes = new Map();
        this._relations = new Map();
        this._resource = null;
        this._annotations = null;
        this._reset = false;
        this._reparent = false;
        this._silent = false;
        // Events
        this.events$ = new EventEmitter();
        const Klass = this.constructor;
        if (Klass.meta === undefined)
            throw new Error(`ODataModel: Can't create model without metadata`);
        this._meta = Klass.meta;
        this._meta.bind(this, { parent, resource, annots });
        // Client Id
        this[this._meta.cid] =
            data[this._meta.cid] ||
                Strings.uniqueId(`${Klass.meta.schema.name.toLowerCase()}-`);
        let attrs = this.annots().attributes(data, 'full');
        let defaults = this.defaults();
        this.assign(Objects.merge(defaults, attrs), { reset });
    }
    static buildMeta({ options, schema, }) {
        if (options === undefined) {
            let fields = schema
                .fields({ include_navigation: true, include_parents: true })
                .reduce((acc, f) => {
                let name = f.name;
                // Prevent collision with reserved keywords
                while (RESERVED_FIELD_NAMES.includes(name)) {
                    name = name + '_';
                }
                return Object.assign(acc, {
                    [name]: {
                        field: f.name,
                        default: f.default,
                        required: !f.nullable,
                    },
                });
            }, {});
            options = { fields };
        }
        this.meta = new ODataModelOptions({ options, schema });
    }
    //#region Resources
    resource() {
        return ODataModelOptions.resource(this);
    }
    navigationProperty(name) {
        const field = this._meta.field(name);
        if (field === undefined || !field.navigation)
            throw Error(`navigationProperty: Can't find navigation property ${name}`);
        const resource = this.resource();
        if (!(resource instanceof ODataEntityResource) || !resource.hasKey())
            throw Error("navigationProperty: Can't get navigation without ODataEntityResource with key");
        return field.resourceFactory(resource);
    }
    property(name) {
        const field = this._meta.field(name);
        if (field === undefined || field.navigation)
            throw Error(`property: Can't find property ${name}`);
        const resource = this.resource();
        if (!(resource instanceof ODataEntityResource) || !resource.hasKey())
            throw Error("property: Can't get property without ODataEntityResource with key");
        return field.resourceFactory(resource);
    }
    attach(resource) {
        return this._meta.attach(this, resource);
    }
    //#endregion
    schema() {
        return this._meta.schema;
    }
    annots() {
        return this._annotations ?? new ODataEntityAnnotations(ODataHelper[DEFAULT_VERSION]);
    }
    key({ field_mapping = false, resolve = true, } = {}) {
        return this._meta.resolveKey(this, { field_mapping, resolve });
    }
    isNew() {
        return this.key() === undefined;
    }
    isParentOf(child) {
        return (child !== this &&
            ODataModelOptions.chain(child).some((p) => p[0] === this));
    }
    referential(field, { field_mapping = false, resolve = true, } = {}) {
        return this._meta.resolveReferential(this, field, {
            field_mapping,
            resolve,
        });
    }
    referenced(field, { field_mapping = false, resolve = true, } = {}) {
        return this._meta.resolveReferenced(this, field, {
            field_mapping,
            resolve,
        });
    }
    validate({ method, navigation = false, } = {}) {
        return this._meta.validate(this, { method, navigation });
    }
    isValid({ method, navigation = false, } = {}) {
        this._errors = this.validate({ method, navigation });
        if (this._errors !== undefined)
            this.events$.emit(new ODataModelEvent('invalid', {
                model: this,
                value: this._errors,
                options: { method },
            }));
        return this._errors === undefined;
    }
    defaults() {
        return this._meta.defaults() || {};
    }
    toEntity({ client_id = false, include_navigation = false, include_concurrency = false, include_computed = false, include_key = true, include_non_field = false, changes_only = false, field_mapping = false, chain = [], } = {}) {
        return this._meta.toEntity(this, {
            client_id,
            include_navigation,
            include_concurrency,
            include_computed,
            include_key,
            include_non_field,
            changes_only,
            field_mapping,
            chain,
        });
    }
    toJSON() {
        return this.toEntity();
    }
    attributes({ changes_only = false, include_concurrency = false, include_computed = false, include_non_field = false, field_mapping = false, } = {}) {
        return this._meta.attributes(this, {
            changes_only,
            include_concurrency,
            include_computed,
            include_non_field,
            field_mapping,
        });
    }
    set(path, value) {
        const pathArray = (Types.isArray(path) ? path : path.match(/([^[.\]])+/g));
        if (pathArray.length === 0)
            return undefined;
        if (pathArray.length > 1) {
            const model = this[pathArray[0]];
            return model.set(pathArray.slice(1), value);
        }
        if (pathArray.length === 1) {
            return (this[pathArray[0]] = value);
        }
    }
    get(path) {
        const pathArray = (Types.isArray(path) ? path : path.match(/([^[.\]])+/g));
        if (pathArray.length === 0)
            return undefined;
        const value = this[pathArray[0]];
        if (pathArray.length > 1 &&
            (value instanceof ODataModel || value instanceof ODataCollection)) {
            return value.get(pathArray.slice(1));
        }
        return value;
    }
    reset({ path, silent = false, } = {}) {
        const pathArray = (path === undefined
            ? []
            : Types.isArray(path)
                ? path
                : path.match(/([^[.\]])+/g));
        const name = pathArray[0];
        const value = name !== undefined ? this[name] : undefined;
        if (ODataModelOptions.isModel(value) ||
            ODataModelOptions.isCollection(value)) {
            value.reset({ path: pathArray.slice(1), silent });
        }
        else {
            this._meta.reset(this, { name: pathArray[0], silent });
        }
    }
    clear({ silent = false } = {}) {
        this._attributes.clear();
        this._changes.clear();
        this._relations.clear();
        if (!silent) {
            this.events$.emit(new ODataModelEvent('update', {
                model: this,
            }));
        }
    }
    assign(entity, { reset = false, reparent = false, silent = false, } = {}) {
        return this._meta.assign(this, entity, { reset, silent, reparent });
    }
    clone() {
        let Ctor = this.constructor;
        return new Ctor(this.toEntity(INCLUDE_SHALLOW), {
            resource: this.resource(),
            annots: this.annots(),
        });
    }
    _request(obs$) {
        this.events$.emit(new ODataModelEvent('request', {
            model: this,
            options: { observable: obs$ },
        }));
        return obs$.pipe(map(({ entity, annots }) => {
            this._annotations = annots;
            this.assign(annots.attributes(entity || {}, 'full'), {
                reset: true,
            });
            this.events$.emit(new ODataModelEvent('sync', {
                model: this,
                options: { entity, annots },
            }));
            return this;
        }));
    }
    fetch({ ...options } = {}) {
        let resource = this.resource();
        if (resource === undefined)
            return throwError(() => new Error('fetch: Resource is undefined'));
        let obs$;
        if (resource instanceof ODataEntityResource) {
            obs$ = resource.fetch(options);
        }
        else if (resource instanceof ODataNavigationPropertyResource) {
            obs$ = resource.fetch({ responseType: 'entity', ...options });
        }
        else {
            obs$ = resource.fetch({
                responseType: 'entity',
                ...options,
            });
        }
        return this._request(obs$);
    }
    save({ method, navigation = false, validate = true, ...options } = {}) {
        let resource = this.resource();
        if (resource === undefined)
            return throwError(() => new Error('save: Resource is undefined'));
        if (!(resource instanceof ODataEntityResource ||
            resource instanceof ODataNavigationPropertyResource))
            return throwError(() => new Error('save: Resource type ODataEntityResource/ODataNavigationPropertyResource needed'));
        // Resolve method and resource key
        if (method === undefined && this.schema().isCompoundKey())
            return throwError(() => new Error('save: Composite key require a specific method, use create/update/modify'));
        method = method || (!resource.hasKey() ? 'create' : 'update');
        if (resource instanceof ODataEntityResource &&
            (method === 'update' || method === 'modify') &&
            !resource.hasKey())
            return throwError(() => new Error('save: Update/Patch require entity key'));
        if (resource instanceof ODataNavigationPropertyResource ||
            method === 'create')
            resource.clearKey();
        if (validate && !this.isValid({ method, navigation })) {
            return throwError(() => new Error('save: Validation errors'));
        }
        const _entity = this.toEntity({
            changes_only: method === 'modify',
            field_mapping: true,
            include_concurrency: true,
            include_navigation: navigation,
        });
        return this._request((method === 'create'
            ? resource.create(_entity, options)
            : method === 'modify'
                ? resource.modify(_entity, { etag: this.annots().etag, ...options })
                : resource.update(_entity, { etag: this.annots().etag, ...options })).pipe(map(({ entity, annots }) => ({ entity: entity || _entity, annots }))));
    }
    destroy({ ...options } = {}) {
        let resource = this.resource();
        if (resource === undefined)
            return throwError(() => new Error('destroy: Resource is undefined'));
        if (!(resource instanceof ODataEntityResource ||
            resource instanceof ODataNavigationPropertyResource))
            return throwError(() => new Error('destroy: Resource type ODataEntityResource/ODataNavigationPropertyResource needed'));
        if (!resource.hasKey())
            return throwError(() => new Error("destroy: Can't destroy model without key"));
        const _entity = this.toEntity({ field_mapping: true });
        const obs$ = resource
            .destroy({ etag: this.annots().etag, ...options })
            .pipe(map(({ entity, annots }) => ({ entity: entity || _entity, annots })));
        return this._request(obs$).pipe(tap(() => this.events$.emit(new ODataModelEvent('destroy', { model: this }))));
    }
    /**
     * Create an execution context for change the internal query of a resource
     * @param func Function to execute
     */
    query(func) {
        return this._meta.query(this, this.resource(), func);
    }
    /**
     * Perform a check on the internal state of the model and return true if the model is changed.
     * @param include_navigation Check in navigation properties
     * @returns true if the model has changed, false otherwise
     */
    hasChanged({ include_navigation = false, } = {}) {
        return this._meta.hasChanged(this, { include_navigation });
    }
    /**
     * Create an execution context for a given function, where the model is bound to its entity endpoint
     * @param ctx Context function
     * @returns The result of the context
     */
    asEntity(ctx) {
        return this._meta.asEntity(this, ctx);
    }
    callFunction(name, params, responseType, { ...options } = {}) {
        const resource = this.resource();
        if (!(resource instanceof ODataEntityResource) || !resource.hasKey())
            return throwError(() => new Error("callFunction: Can't call function without ODataEntityResource with key"));
        const func = resource.function(name).query((q) => q.apply(options));
        switch (responseType) {
            case 'property':
                return func.callProperty(params, options);
            case 'model':
                return func.callModel(params, options);
            case 'collection':
                return func.callCollection(params, options);
            default:
                return func.call(params, { responseType, ...options });
        }
    }
    callAction(name, params, responseType, { ...options } = {}) {
        const resource = this.resource();
        if (!(resource instanceof ODataEntityResource) || !resource.hasKey())
            return throwError(() => new Error("callAction: Can't call action without ODataEntityResource with key"));
        const action = resource.action(name).query((q) => q.apply(options));
        switch (responseType) {
            case 'property':
                return action.callProperty(params, options);
            case 'model':
                return action.callModel(params, options);
            case 'collection':
                return action.callCollection(params, options);
            default:
                return action.call(params, { responseType, ...options });
        }
    }
    // Cast
    cast(type) {
        const resource = this.resource();
        if (!(resource instanceof ODataEntityResource))
            throw new Error(`cast: Can't cast to derived model without ODataEntityResource`);
        return resource
            .cast(type)
            .asModel(this.toEntity(INCLUDE_DEEP), { annots: this.annots() });
    }
    fetchNavigationProperty(name, responseType, { ...options } = {}) {
        const nav = this.navigationProperty(name);
        nav.query((q) => q.apply(options));
        switch (responseType) {
            case 'model':
                return nav.fetchModel(options);
            case 'collection':
                return nav.fetchCollection(options);
        }
    }
    // Get Value
    getValue(name, options) {
        const field = this._meta.field(name);
        if (field === undefined || field.navigation)
            throw Error(`getValue: Can't find property ${name}`);
        let value = this[name];
        if (value === undefined) {
            const prop = field.resourceFactory(this.resource());
            return field.collection
                ? prop
                    .fetchCollection(options)
                    .pipe(tap((c) => this.assign({ [name]: c }, { silent: true })))
                : field.isStructuredType()
                    ? prop
                        .fetchModel(options)
                        .pipe(tap((c) => this.assign({ [name]: c }, { silent: true })))
                    : prop
                        .fetchProperty(options)
                        .pipe(tap((c) => this.assign({ [name]: c }, { silent: true })));
        }
        return of(value);
    }
    // Set Reference
    setReference(name, model, options) {
        const reference = this.navigationProperty(name).reference();
        const etag = this.annots().etag;
        let obs$ = NEVER;
        if (model instanceof ODataModel) {
            obs$ = reference.set(model._meta.entityResource(model), { etag, ...options });
        }
        else if (model instanceof ODataCollection) {
            obs$ = forkJoin(model
                .models()
                .map((m) => reference.add(m._meta.entityResource(m), options)));
        }
        else if (model === null) {
            obs$ = reference.unset({ etag, ...options });
        }
        this.events$.emit(new ODataModelEvent('request', {
            model: this,
            options: { observable: obs$ },
        }));
        return obs$.pipe(map((model) => {
            this.assign({ [name]: model });
            this.events$.emit(new ODataModelEvent('sync', { model: this }));
            return this;
        }));
    }
    getReference(name) {
        const field = this._meta.field(name);
        if (field === undefined || !field.navigation)
            throw Error(`getReference: Can't find navigation property ${name}`);
        let model = this[name];
        if (model === null)
            return null;
        if (model === undefined) {
            if (field.collection) {
                model = field.collectionFactory({ parent: this });
            }
            else {
                const value = this.referenced(field);
                model =
                    value === null ? null : field.modelFactory({ parent: this, value });
            }
            this[name] = model;
        }
        return model;
    }
    // Model functions
    equals(other) {
        const thisKey = this.key();
        const otherKey = other.key();
        return (this === other ||
            (typeof this === typeof other &&
                (this[this._meta.cid] === other[this._meta.cid] ||
                    (thisKey !== undefined &&
                        otherKey !== undefined &&
                        Types.isEqual(thisKey, otherKey)))));
    }
    get [Symbol.toStringTag]() {
        return 'Model';
    }
    collection() {
        return this._parent !== null &&
            ODataModelOptions.isCollection(this._parent[0])
            ? this._parent[0]
            : undefined;
    }
    next() {
        return this.collection()?.next(this);
    }
    prev() {
        return this.collection()?.prev(this);
    }
}
const RESERVED_FIELD_NAMES = Object.getOwnPropertyNames(ODataModel.prototype);

class ODataApiOptions {
    constructor(config) {
        this.etag = { ifMatch: true, ifNoneMatch: false };
        this.version = config.version || DEFAULT_VERSION;
        this.stringAsEnum = config.stringAsEnum || false;
        this.params = config.params || {};
        this.headers = config.headers || {};
        this.withCredentials = config.withCredentials;
        this.stripMetadata = config.stripMetadata || DEFAULT_STRIP_METADATA;
        this.fetchPolicy = config.fetchPolicy || DEFAULT_FETCH_POLICY;
        this.bodyQueryOptions = config.bodyQueryOptions || [];
        this.accept = config.accept;
        Object.assign(this.etag, config.etag || {});
        this.prefer = config.prefer;
        this.deleteRefBy = config.deleteRefBy || 'path';
        this.nonParenthesisForEmptyParameterFunction =
            config.nonParenthesisForEmptyParameterFunction || false;
    }
    get parserOptions() {
        return {
            version: this.version,
            stringAsEnum: this.stringAsEnum,
            deleteRefBy: this.deleteRefBy,
            nonParenthesisForEmptyParameterFunction: this.nonParenthesisForEmptyParameterFunction,
            ...this.accept,
        };
    }
    get helper() {
        return ODataHelper[this.version];
    }
}

/**
 * Api abstraction for consuming OData services.
 */
class ODataApi {
    constructor(config) {
        //# region Find by Type
        // Memoize
        this.memo = {
            forType: {
                enum: new Map(),
                structured: new Map(),
                callable: new Map(),
                entitySet: new Map(),
                parser: new Map(),
                options: new Map(),
            },
            byName: {
                enum: new Map(),
                structured: new Map(),
                callable: new Map(),
                entitySet: new Map(),
            },
        };
        this.serviceRootUrl = config.serviceRootUrl;
        if (this.serviceRootUrl.indexOf('?') != -1)
            throw new Error("The 'serviceRootUrl' should not contain query string. Please use 'params' to add extra parameters");
        if (!this.serviceRootUrl.endsWith('/'))
            this.serviceRootUrl += '/';
        this.metadataUrl = `${this.serviceRootUrl}$metadata`;
        this.name = config.name;
        this.version = config.version || DEFAULT_VERSION;
        this.default = config.default || false;
        this.creation = config.creation || new Date();
        this.options = new ODataApiOptions(Object.assign({ version: this.version }, config.options || {}));
        this.cache = config.cache || new ODataInMemoryCache();
        this.errorHandler = config.errorHandler;
        this.parsers = new Map(Object.entries(config.parsers || EDM_PARSERS));
        this.schemas = (config.schemas || []).map((schema) => new ODataSchema(schema, this));
    }
    configure(settings = {}) {
        this.requester = settings.requester;
        this.schemas.forEach((schema) => {
            schema.configure({
                options: this.options.parserOptions,
                parserForType: (type) => this.parserForType(type),
                findOptionsForType: (type) => this.findOptionsForType(type),
            });
        });
    }
    fromJSON(json) {
        const segments = new ODataPathSegments(json.segments);
        const query = new ODataQueryOptions(json.options);
        switch (segments.last()?.name) {
            case PathSegmentNames.entitySet:
                if (segments.last()?.hasKey()) {
                    return new ODataEntityResource(this, { segments, query });
                }
                else {
                    return new ODataEntitySetResource(this, { segments, query });
                }
            case PathSegmentNames.navigationProperty:
                return new ODataNavigationPropertyResource(this, { segments, query });
            case PathSegmentNames.singleton:
                return new ODataSingletonResource(this, { segments, query });
            case PathSegmentNames.action:
                return new ODataActionResource(this, { segments, query });
            case PathSegmentNames.function:
                return new ODataFunctionResource(this, { segments, query });
        }
        throw new Error('No Resource for json');
    }
    /**
     * Build a metadata resource.
     * @returns ODataMetadataResource
     */
    metadata() {
        return ODataMetadataResource.factory(this);
    }
    /**
     * Build a batch resource.
     * @returns ODataBatchResource
     */
    batch() {
        return ODataBatchResource.factory(this);
    }
    /**
     * Build a singleton resource.
     * @param path Name of the singleton
     * @returns
     */
    singleton(path) {
        const entitySet = this.findEntitySetByName(path);
        const schema = entitySet !== undefined
            ? this.findStructuredTypeForType(entitySet.entityType)
            : undefined;
        return ODataSingletonResource.factory(this, { path, schema });
    }
    /**
     * Build an entity set resource.
     * @param path Name of the entity set
     * @returns
     */
    entitySet(path) {
        const entitySet = this.findEntitySetByName(path);
        const schema = entitySet !== undefined
            ? this.findStructuredTypeForType(entitySet.entityType)
            : undefined;
        return ODataEntitySetResource.factory(this, { path, schema });
    }
    /**
     * Unbound Action
     * @param  {string} path?
     * @returns ODataActionResource
     */
    action(path) {
        const schema = this.findCallableForType(path);
        return ODataActionResource.factory(this, { path, schema });
    }
    /**
     * Unbound Function
     * @param  {string} path?
     * @returns ODataFunctionResource
     */
    function(path) {
        const schema = this.findCallableForType(path);
        return ODataFunctionResource.factory(this, { path, schema });
    }
    //request(req: ODataRequest<any>): Observable<any> {
    request(method, resource, options) {
        let req = ODataRequest.factory(this, method, resource, {
            body: options.body,
            etag: options.etag,
            context: options.context,
            headers: options.headers,
            params: options.params,
            responseType: options.responseType,
            observe: (options.observe === 'events' ? 'events' : 'response'),
            withCount: options.withCount,
            bodyQueryOptions: options.bodyQueryOptions,
            reportProgress: options.reportProgress,
            fetchPolicy: options.fetchPolicy,
            withCredentials: options.withCredentials,
        });
        let res$ = this.requester !== undefined ? this.requester(req) : NEVER;
        res$ = res$.pipe(map((res) => res.type === HttpEventType.Response
            ? ODataResponse.fromHttpResponse(req, res)
            : res));
        if (this.errorHandler !== undefined)
            res$ = res$.pipe(catchError(this.errorHandler));
        if (options.observe === 'events') {
            return res$;
        }
        res$ = this.cache.handleRequest(req, res$);
        switch (options.observe || 'body') {
            case 'body':
                switch (options.responseType) {
                    case 'entities':
                        return res$.pipe(map((res) => res.entities()));
                    case 'entity':
                        return res$.pipe(map((res) => res.entity()));
                    case 'property':
                        return res$.pipe(map((res) => res.property()));
                    case 'value':
                        return res$.pipe(map((res) => res.value()));
                    default:
                        // Other responseTypes (arraybuffer, blob, json, text) return body
                        return res$.pipe(map((res) => res.body));
                }
            case 'response':
                // The response stream was requested directly, so return it.
                return res$;
            default:
                // Guard against new future observe types being added.
                throw new Error(`Unreachable: unhandled observe type ${options.observe}}`);
        }
    }
    findSchemaForType(type) {
        const schemas = this.schemas.filter((s) => s.isNamespaceOf(type));
        if (schemas.length === 0)
            return undefined;
        if (schemas.length === 1)
            return schemas[0];
        return schemas
            .sort((s1, s2) => s1.namespace.length - s2.namespace.length)
            .pop();
    }
    findEnumTypeForType(type) {
        if (this.memo.forType.enum.has(type)) {
            return this.memo.forType.enum.get(type);
        }
        const enumType = this.findSchemaForType(type)?.findEnumTypeForType(type);
        this.memo.forType.enum.set(type, enumType);
        return enumType;
    }
    findStructuredTypeForType(type) {
        if (this.memo.forType.structured.has(type)) {
            return this.memo.forType.structured.get(type);
        }
        const structuredType = this.findSchemaForType(type)?.findStructuredTypeForType(type);
        this.memo.forType.structured.set(type, structuredType);
        return structuredType;
    }
    findCallableForType(type, bindingType) {
        const key = bindingType !== undefined ? `${bindingType}/${type}` : type;
        if (this.memo.forType.callable.has(key)) {
            return this.memo.forType.callable.get(key);
        }
        const callable = this.findSchemaForType(type)?.findCallableForType(type, bindingType);
        this.memo.forType.callable.set(key, callable);
        return callable;
    }
    findEntitySetForType(type) {
        if (this.memo.forType.entitySet.has(type)) {
            return this.memo.forType.entitySet.get(type);
        }
        const entitySet = this.findSchemaForType(type)?.findEntitySetForType(type);
        this.memo.forType.entitySet.set(type, entitySet);
        return entitySet;
    }
    findModelForType(type) {
        return this.findStructuredTypeForType(type)?.model;
    }
    modelForType(type) {
        let Model = this.findModelForType(type);
        if (Model === undefined) {
            // Build Ad-hoc model
            let schema = this.findStructuredTypeForType(type);
            if (schema === undefined)
                throw Error(`No schema for ${type}`);
            Model = class extends ODataModel {
            };
            // Build Meta
            Model.buildMeta({ schema });
            // Configure
            Model.meta.configure({
                options: this.options.parserOptions,
                parserForType: (type) => this.parserForType(type),
                findOptionsForType: (type) => this.findOptionsForType(type),
            });
            // Store New Model for next time
            schema.model = Model;
        }
        return Model;
    }
    findCollectionForType(type) {
        return this.findStructuredTypeForType(type)?.collection;
    }
    collectionForType(type) {
        let Collection = this.findCollectionForType(type);
        if (Collection === undefined) {
            let schema = this.findStructuredTypeForType(type);
            if (schema === undefined)
                throw Error('');
            Collection = class extends ODataCollection {
            };
            Collection.model = this.modelForType(type);
            // Store New Collection for next time
            schema.collection = Collection;
        }
        return Collection;
    }
    findServiceForType(type) {
        return this.findEntitySetForType(type)?.service;
    }
    findEntitySetForEntityType(entityType) {
        if (this.memo.forType.entitySet.has(entityType)) {
            return this.memo.forType.entitySet.get(entityType);
        }
        const entitySet = this.schemas
            .reduce((acc, schema) => [...acc, ...schema.entitySets], [])
            .find((e) => e.entityType === entityType);
        this.memo.forType.entitySet.set(entityType, entitySet);
        return entitySet;
    }
    findServiceForEntityType(entityType) {
        return this.findEntitySetForEntityType(entityType)?.service;
    }
    findEnumTypeByName(name) {
        if (this.memo.byName.enum.has(name)) {
            return this.memo.byName.enum.get(name);
        }
        const enumType = this.schemas
            .reduce((acc, schema) => [...acc, ...schema.enums], [])
            .find((e) => e.name === name);
        this.memo.byName.enum.set(name, enumType);
        return enumType;
    }
    findStructuredTypeByName(name) {
        if (this.memo.byName.structured.has(name)) {
            return this.memo.byName.structured.get(name);
        }
        const structuredType = this.schemas
            .reduce((acc, schema) => [...acc, ...schema.entities], [])
            .find((e) => e.name === name);
        this.memo.byName.structured.set(name, structuredType);
        return structuredType;
    }
    findCallableByName(name, bindingType) {
        const key = bindingType !== undefined ? `${bindingType}/${name}` : name;
        if (this.memo.byName.callable.has(key)) {
            return this.memo.byName.callable.get(key);
        }
        const callable = this.schemas
            .reduce((acc, schema) => [...acc, ...schema.callables], [])
            .find((c) => c.name === name &&
            (bindingType === undefined || c.binding()?.type === bindingType));
        this.memo.byName.callable.set(key, callable);
        return callable;
    }
    findEntitySetByName(name) {
        if (this.memo.byName.entitySet.has(name)) {
            return this.memo.byName.entitySet.get(name);
        }
        const schema = this.schemas
            .reduce((acc, schema) => [...acc, ...schema.entitySets], [])
            .find((e) => e.name === name);
        this.memo.byName.entitySet.set(name, schema);
        return schema;
    }
    findModelByName(name) {
        return this.findStructuredTypeByName(name)?.model;
    }
    findCollectionByName(name) {
        return this.findStructuredTypeByName(name)?.collection;
    }
    findServiceByName(name) {
        return this.findEntitySetByName(name)?.service;
    }
    parserForType(type, bindingType) {
        const key = bindingType !== undefined ? `${bindingType}/${type}` : type;
        if (this.memo.forType.parser.has(key)) {
            return this.memo.forType.parser.get(key);
        }
        // None Parser by default
        let parser = NONE_PARSER;
        if (this.parsers.has(type)) {
            // Edm, Base Parsers
            parser = this.parsers.get(type);
        }
        else if (!type.startsWith('Edm.')) {
            // EnumType, ComplexType and EntityType Parsers
            let value = this.findCallableForType(type, bindingType) ||
                this.findEnumTypeForType(type) ||
                this.findStructuredTypeForType(type);
            parser = value?.parser;
        }
        // Set Parser for next time
        this.memo.forType.parser.set(key, parser);
        return parser;
    }
    findOptionsForType(type) {
        // Strucutred Options
        if (this.memo.forType.options.has(type)) {
            return this.memo.forType.options.get(type);
        }
        const st = this.findStructuredTypeForType(type);
        const options = st?.model?.meta;
        // Set Options for next time
        this.memo.forType.options.set(type, options);
        return options;
    }
}

class ODataSettings {
    constructor(...configs) {
        this.apis = configs.map((config) => new ODataApi(config));
        if (this.apis.length > 1) {
            if (this.apis.some((c) => c.name === undefined))
                throw new Error('Multiple APIs: Needs configuration names');
            if (this.apis.filter((c) => c.default).length > 1)
                throw new Error('Multiple APIs: Needs only one default api');
        }
        // If not default setup first config as default api
        if (this.apis.every((c) => !c.default))
            this.apis[0].default = true;
    }
    configure(settings) {
        this.apis.forEach((api) => api.configure(settings));
    }
    defaultApi() {
        return this.apis.find((c) => c.default);
    }
    findApiByName(name) {
        return this.apis.find((c) => c.name === name);
    }
    apiByName(name) {
        const api = this.findApiByName(name);
        if (api === undefined)
            throw new Error(`No API for name: ${name}`);
        return api;
    }
    findApiForTypes(types) {
        return this.apis.find((c) => c.schemas.some((s) => types.some((type) => s.isNamespaceOf(type))));
    }
    findApiForType(type) {
        return this.findApiForTypes([type]);
    }
    apiForType(type) {
        const api = this.findApiForType(type);
        if (api === undefined)
            throw new Error(`No API for type: ${type}`);
        return api;
    }
    //#region Configs shortcuts
    enumTypeForType(type) {
        let values = this.apis
            .map((api) => api.findEnumTypeForType(type))
            .filter((e) => e);
        if (values.length === 0)
            throw Error(`No Enum for type ${type} was found`);
        if (values.length > 1)
            throw Error('Multiple APIs: More than one value was found');
        return values[0];
    }
    structuredTypeForType(type) {
        let values = this.apis
            .map((api) => api.findStructuredTypeForType(type))
            .filter((e) => e);
        if (values.length === 0)
            throw Error(`No Structured for type ${type} was found`);
        if (values.length > 1)
            throw Error('Multiple APIs: More than one value was found');
        return values[0];
    }
    callableForType(type, bindingType) {
        let values = this.apis
            .map((api) => api.findCallableForType(type, bindingType))
            .filter((e) => e);
        if (values.length === 0)
            throw Error(`No Callable for type ${type} was found`);
        if (values.length > 1)
            throw Error('Multiple APIs: More than one value was found');
        return values[0];
    }
    entitySetForType(type) {
        let values = this.apis
            .map((api) => api.findEntitySetForType(type))
            .filter((e) => e);
        if (values.length === 0)
            throw Error(`No EntitySet for type ${type} was found`);
        if (values.length > 1)
            throw Error('Multiple APIs: More than one value was found');
        return values[0];
    }
    parserForType(type) {
        let values = this.apis
            .map((api) => api.parserForType(type))
            .filter((e) => e);
        if (values.length === 0)
            throw Error(`No Parser for type ${type} was found`);
        if (!type.startsWith('Edm.') && values.length > 1)
            throw Error('Multiple APIs: More than one value was found');
        return values[0];
    }
    modelForType(type) {
        let values = this.apis
            .map((api) => api.findModelForType(type))
            .filter((e) => e);
        if (values.length === 0)
            throw Error(`No Model for type ${type} was found`);
        if (values.length > 1)
            throw Error('Multiple APIs: More than one value was found');
        return values[0];
    }
    collectionForType(type) {
        let values = this.apis
            .map((api) => api.findCollectionForType(type))
            .filter((e) => e);
        if (values.length === 0)
            throw Error(`No Collection for type ${type} was found`);
        if (values.length > 1)
            throw Error('Multiple APIs: More than one value was found');
        return values[0];
    }
    serviceForType(type) {
        let values = this.apis
            .map((api) => api.findServiceForType(type))
            .filter((e) => e);
        if (values.length === 0)
            throw Error(`No Service for type ${type} was found`);
        if (values.length > 1)
            throw Error('Multiple APIs: More than one value was found');
        return values[0];
    }
    serviceForEntityType(type) {
        let values = this.apis
            .map((api) => api.findServiceForEntityType(type))
            .filter((e) => e);
        if (values.length === 0)
            throw Error(`No Service for type ${type} was found`);
        if (values.length > 1)
            throw Error('Multiple APIs: More than one value was found');
        return values[0];
    }
    enumTypeByName(name) {
        let values = this.apis
            .map((api) => api.findEnumTypeByName(name))
            .filter((e) => e);
        if (values.length === 0)
            throw Error(`No Enum for name ${name} was found`);
        if (values.length > 1)
            throw Error('Multiple APIs: More than one value was found');
        return values[0];
    }
    structuredTypeByName(name) {
        let values = this.apis
            .map((api) => api.findStructuredTypeByName(name))
            .filter((e) => e);
        if (values.length === 0)
            throw Error(`No Structured for name ${name} was found`);
        if (values.length > 1)
            throw Error('Multiple Structured: More than one value was found');
        return values[0];
    }
    callableByName(name, bindingType) {
        let values = this.apis
            .map((api) => api.findCallableByName(name, bindingType))
            .filter((e) => e);
        if (values.length === 0)
            throw Error(`No Callable for name ${name} was found`);
        if (values.length > 1)
            throw Error('Multiple Callable: More than one value was found');
        return values[0];
    }
    entitySetByName(name) {
        let values = this.apis
            .map((api) => api.findEntitySetByName(name))
            .filter((e) => e);
        if (values.length === 0)
            throw Error(`No EntitySet for name ${name} was found`);
        if (values.length > 1)
            throw Error('Multiple EntitySet: More than one value was found');
        return values[0];
    }
    modelByName(name) {
        let values = this.apis
            .map((api) => api.findModelByName(name))
            .filter((e) => e);
        if (values.length === 0)
            throw Error(`No Model for name ${name} was found`);
        if (values.length > 1)
            throw Error('Multiple APIs: More than one value was found');
        return values[0];
    }
    collectionByName(name) {
        let values = this.apis
            .map((api) => api.findCollectionByName(name))
            .filter((e) => e);
        if (values.length === 0)
            throw Error(`No Collection for name ${name} was found`);
        if (values.length > 1)
            throw Error('Multiple APIs: More than one value was found');
        return values[0];
    }
    serviceByName(name) {
        let values = this.apis
            .map((api) => api.findServiceByName(name))
            .filter((e) => e);
        if (values.length === 0)
            throw Error(`No Service for name ${name} was found`);
        if (values.length > 1)
            throw Error('Multiple APIs: More than one value was found');
        return values[0];
    }
}

function addBody(options, body) {
    return {
        body,
        etag: options.etag,
        fetchPolicy: options.fetchPolicy,
        headers: options.headers,
        observe: options.observe,
        params: options.params,
        reportProgress: options.reportProgress,
        responseType: options.responseType,
        withCredentials: options.withCredentials,
    };
}
class ODataClient {
    constructor(http, settings, injector) {
        this.http = http;
        this.settings = settings;
        this.injector = injector;
        this.settings.configure({
            requester: (req) => this.http.request(req.method, `${req.url}`, {
                body: req.body,
                context: req.context,
                headers: req.headers,
                observe: req.observe,
                params: req.params,
                reportProgress: req.reportProgress,
                responseType: req.responseType,
                withCredentials: req.withCredentials,
            }),
        });
    }
    //#region Resolve Building Blocks
    /**
     * Resolve the api for the given value.
     * Where value is: string type or an string name or an instance of resource.
     * @param value The value to resolve.
     * @returns The api for the value.
     */
    apiFor(value) {
        let api = undefined;
        if (value instanceof ODataResource)
            api = this.settings.findApiForTypes(value.types());
        else if (typeof value === 'string')
            api =
                this.settings.findApiByName(value) ||
                    this.settings.findApiForType(value);
        return api || this.settings.defaultApi();
    }
    /**
     * Resolve the parser for the given string type.
     * @param type The string type of the parser.
     * @returns The parser for the given type.
     */
    parserForType(type) {
        return this.settings.parserForType(type);
    }
    /**
     * Resolve the enum type for the given string type.
     * @param type The string type of the enum type.
     * @returns The enum type for the given type.
     */
    enumTypeForType(type) {
        return this.settings.enumTypeForType(type);
    }
    /**
     * Resolve the structured type for the given string type.
     * @param type The string type of the structured type.
     * @returns The structured type for the given type.
     */
    structuredTypeForType(type) {
        return this.settings.structuredTypeForType(type);
    }
    /**
     * Resolve the callable for the given string type.
     * @param type The string type of the callable.
     * @returns The callable for the given type.
     */
    callableForType(type) {
        return this.settings.callableForType(type);
    }
    /**
     * Resolve the entity set for the given string type.
     * @param type The string type of the entity set.
     * @returns The entity set for the given type.
     */
    entitySetForType(type) {
        return this.settings.entitySetForType(type);
    }
    /**
     * Resolve the model for the given string type.
     * @param type The string type of the model.
     * @returns The model for the given type.
     */
    modelForType(type) {
        return this.settings.modelForType(type);
    }
    /**
     * Resolve the collection for the given string type.
     * @param type The string type of the collection.
     * @returns The collection for the given type.
     */
    collectionForType(type) {
        return this.settings.collectionForType(type);
    }
    /**
     * Resolve the service for the given string type.
     * @param type The string type of the service.
     * @returns The service for the given type.
     */
    serviceForType(type) {
        return this.injector.get(this.settings.serviceForType(type));
    }
    /**
     * Resolve the service for the given string entity type.
     * @param type The string entity type binding to the service.
     * @returns The service for the given entity type.
     */
    serviceForEntityType(type) {
        return this.injector.get(this.settings.serviceForEntityType(type));
    }
    enumTypeByName(name) {
        return this.settings.enumTypeByName(name);
    }
    structuredTypeByName(name) {
        return this.settings.structuredTypeByName(name);
    }
    callableByName(name) {
        return this.settings.callableByName(name);
    }
    entitySetByName(name) {
        return this.settings.entitySetByName(name);
    }
    modelByName(name) {
        return this.settings.modelByName(name);
    }
    collectionByName(name) {
        return this.settings.collectionByName(name);
    }
    serviceByName(name) {
        return this.injector.get(this.settings.serviceByName(name));
    }
    fromJSON(json, apiNameOrType) {
        return this.apiFor(apiNameOrType).fromJSON(json);
    }
    // Requests
    /**
     * Build a resource for the metadata.
     * @param apiName The name of the API.
     * @returns The metadata resource.
     */
    metadata(apiName) {
        return this.apiFor(apiName).metadata();
    }
    /**
     * Build a resource for the batch.
     * @param apiName The name of the API.
     * @returns The batch resource.
     */
    batch(apiName) {
        return this.apiFor(apiName).batch();
    }
    /**
     * Build a resource for the singleton.
     * @param path The full path to the singleton.
     * @param apiNameOrType The name of the API or the type of the singleton.
     * @returns The singleton resource.
     */
    singleton(path, apiNameOrType) {
        return this.apiFor(apiNameOrType).singleton(path);
    }
    /**
     * Build a resource for the entity set.
     * @param path The full path to the entity set.
     * @param apiNameOrType The name of the API or the type of the entity set.
     * @returns The entity set resource.
     */
    entitySet(path, apiNameOrType) {
        return this.apiFor(apiNameOrType).entitySet(path);
    }
    /**
     * Build a resource for unbound action.
     * @param path The full path to the action.
     * @param apiNameOrType The name of the API or the type of the entity.
     * @returns The unbound action resource.
     */
    action(path, apiNameOrType) {
        return this.apiFor(apiNameOrType).action(path);
    }
    /**
     * Build a resource for unbound function.
     * @param path The full path to the function.
     * @param apiNameOrType The name of the API or the type of the callable.
     * @returns The unbound function resource.
     */
    function(path, apiNameOrType) {
        return this.apiFor(apiNameOrType).function(path);
    }
    request(method, resource, options) {
        let api = this.apiFor(resource);
        return api.request(method, resource, options);
    }
    delete(resource, options = {}) {
        return this.request('DELETE', resource, addBody(options, null));
    }
    get(resource, options = {}) {
        return this.request('GET', resource, options);
    }
    head(resource, options = {}) {
        return this.request('HEAD', resource, options);
    }
    jsonp(resource, callbackParam) {
        return this.request('JSONP', resource, {
            body: null,
            params: new HttpParams().append(callbackParam, 'JSONP_CALLBACK'),
            observe: 'body',
            responseType: 'json',
        });
    }
    options(resource, options = {}) {
        return this.request('OPTIONS', resource, options);
    }
    patch(resource, body, options = {}) {
        return this.request('PATCH', resource, addBody(options, body));
    }
    post(resource, body, options = {}) {
        return this.request('POST', resource, addBody(options, body));
    }
    put(resource, body, options = {}) {
        return this.request('PUT', resource, addBody(options, body));
    }
}
ODataClient.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.1", ngImport: i0, type: ODataClient, deps: [{ token: i1.HttpClient }, { token: ODataSettings }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
ODataClient.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.1", ngImport: i0, type: ODataClient, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.1", ngImport: i0, type: ODataClient, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: ODataSettings }, { type: i0.Injector }]; } });

const ODATA_CONFIGURATIONS = new InjectionToken('odata.configuraions');

class ODataBaseService {
    constructor(client, name, apiNameOrEntityType) {
        this.client = client;
        this.name = name;
        this.apiNameOrEntityType = apiNameOrEntityType;
    }
    get api() {
        return this.client.apiFor(this.apiNameOrEntityType);
    }
    callFunction(params, resource, responseType, options = {}) {
        resource.query((q) => q.apply(options));
        return resource.call(params, {
            responseType: responseType,
            ...options,
        });
    }
    callAction(params, resource, responseType, options = {}) {
        resource.query((q) => q.apply(options));
        return resource.call(params, {
            responseType: responseType,
            ...options,
        });
    }
    fetchNavigationProperty(resource, responseType, options = {}) {
        resource.query((q) => q.apply(options));
        return resource.fetch({ responseType: responseType, ...options });
    }
}

class ODataEntityService extends ODataBaseService {
    /**
     * The schema for the structured type.
     */
    get structuredTypeSchema() {
        return this.apiNameOrEntityType !== undefined
            ? this.api.findStructuredTypeForType(this.apiNameOrEntityType)
            : undefined;
    }
}

class ODataEntitySetService extends ODataEntityService {
    /**
     * Get the entity set resource for this service.
     */
    entities() {
        return this.client.entitySet(this.name, this.apiNameOrEntityType);
    }
    /**
     * Get the entity resource for this service.
     * @param key The entity key.
     */
    entity(key) {
        return this.entities().entity(key);
    }
    attach(model) {
        if (model instanceof ODataModel) {
            model.attach(this.entities().entity());
        }
        else if (model instanceof ODataCollection) {
            model.attach(this.entities());
        }
    }
    /**
     * The schema for the entity set.
     */
    get entitySetSchema() {
        return this.api.findEntitySetByName(this.name);
    }
    /**
     * Get all entities from the entity set.
     * @param options The options for the request.
     */
    fetchAll(options) {
        return this.entities().fetchAll(options);
    }
    /**
     * Get entities from the entity set.
     * @param withCount Get the count of the entities.
     * @param options The options for the request.
     */
    fetchMany(top, options) {
        return this.entities().fetchMany(top, options);
    }
    /**
     * Get an entity from the entity set.
     * @param key The entity key.
     * @param etag The etag for the entity.
     * @param options The options for the request.
     */
    fetchOne(options) {
        return this.entities().fetchOne(options);
    }
    /**
     * Create an entity in the entity set.
     * @param attrs The attributes for the entity.
     * @param options The options for the request.
     */
    create(attrs, options) {
        return this.entities().create(attrs, options);
    }
    /**
     * Update an entity in the entity set.
     * @param key The entity key.
     * @param attrs The attributes for the entity.
     * @param etag The etag for the entity.
     * @param options The options for the request.
     */
    update(key, attrs, options) {
        const res = this.entity(key);
        if (!res.hasKey())
            return throwError(() => new Error('update: Resource without key'));
        return res.update(attrs, options);
    }
    /**
     * Patch an entity in the entity set.
     * @param key The entity key.
     * @param attrs The attributes for the entity.
     * @param etag The etag for the entity.
     * @param options The options for the request.
     */
    modify(key, attrs, options) {
        const res = this.entity(key);
        if (!res.hasKey())
            return throwError(() => new Error('modify: Resource without key'));
        return res.modify(attrs, options);
    }
    /**
     * Delete an entity in the entity set.
     * @param key The entity key.
     * @param etag The etag for the entity.
     * @param options The options for the request.
     */
    destroy(key, options) {
        const res = this.entity(key);
        if (!res.hasKey())
            return throwError(() => new Error('destroy: Resource without key'));
        return res.destroy(options);
    }
    //#region Shortcuts
    /**
     * Get or create an entity in the entity set.
     * @param key The entity key.
     * @param attrs The attributes for the entity.
     * @param etag The etag for the entity.
     * @param options The options for the request.
     */
    fetchOrCreate(key, attrs, { etag, ...options } = {}) {
        return this.entity(key).fetch({ etag, ...options }).pipe(catchError((error) => {
            if (error.status === 404)
                return this.create(attrs, options);
            else
                return throwError(() => error);
        }));
    }
    /**
     * Save an entity in the entity set.
     * @param attrs The attributes for the entity.
     * @param method The method to use.
     * @param etag The etag for the entity.
     * @param options The options for the request.
     */
    save(attrs, { etag, method, ...options } = {}) {
        let schema = this.structuredTypeSchema;
        if (method === undefined && schema !== undefined && schema.isCompoundKey())
            return throwError(() => new Error('save: Composite key require a specific method, use create/update/patch'));
        let key = schema && schema.resolveKey(attrs);
        if (method === undefined)
            method = key !== undefined ? 'update' : 'create';
        if ((method === 'update' || method === 'modify') && key === undefined)
            return throwError(() => new Error("save: Can't update/patch entity without key"));
        return method === 'create'
            ? this.create(attrs, options)
            : method === 'modify'
                ? this.modify(key, attrs, { etag, ...options })
                : this.update(key, attrs, { etag, ...options });
    }
}

/**
 * OData Singleton Service
 * www.odata.org/getting-started/advanced-tutorial/#singleton
 */
class ODataSingletonService extends ODataEntityService {
    /**
     * Get the entity resource for this service.
     * @param key The entity key.
     */
    entity() {
        return this.client.singleton(this.name, this.apiNameOrEntityType);
    }
    /**
     * Attach an existing model to this service.
     * @param model The model to attach.
     */
    attach(model) {
        model.attach(this.entity());
    }
    /**
     * The schema for the singleton.
     */
    get singletonSchema() {
        return this.api.findEntitySetByName(this.name);
    }
    /**
     * Update the singleton entity
     * @param attrs The attributes for the entity.
     * @param etag The etag for the entity.
     * @param options The options for the request.
     */
    update(attrs, options) {
        const res = this.entity();
        return res.update(attrs, options);
    }
    /**
     * Patch the singleton entity
     * @param attrs The attributes for the entity.
     * @param etag The etag for the entity.
     * @param options The options for the request.
     */
    patch(attrs, options) {
        const res = this.entity();
        return res.modify(attrs, options);
    }
}

class ODataServiceFactory {
    constructor(client) {
        this.client = client;
    }
    /**
     * Factory method to create an entity set service.
     * @param entitySetName Name of the entity set.
     * @param apiNameOrEntityType Name of the API or the type of the entity.
     */
    entitySet(entitySetName, apiNameOrEntityType) {
        return new (class extends ODataEntitySetService {
        })(this.client, entitySetName, apiNameOrEntityType);
    }
    /** Factory method to create a singleton service.
     * @param singletonName Name of the singleton.
     * @param apiNameOrEntityType Name of the API or the type of the entity.
     */
    singleton(singletonName, apiNameOrEntityType) {
        return new (class extends ODataSingletonService {
        })(this.client, singletonName, apiNameOrEntityType);
    }
}
ODataServiceFactory.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.1", ngImport: i0, type: ODataServiceFactory, deps: [{ token: ODataClient }], target: i0.ɵɵFactoryTarget.Injectable });
ODataServiceFactory.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.1", ngImport: i0, type: ODataServiceFactory });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.1", ngImport: i0, type: ODataServiceFactory, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: ODataClient }]; } });

function createSettings(configs) {
    return new ODataSettings(...configs);
}
class ODataModule {
    static forRoot(...configs) {
        return {
            ngModule: ODataModule,
            providers: [
                { provide: ODATA_CONFIGURATIONS, useValue: configs },
                {
                    provide: ODataSettings,
                    useFactory: createSettings,
                    deps: [ODATA_CONFIGURATIONS],
                },
                ODataClient,
                ODataServiceFactory,
            ],
        };
    }
}
ODataModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.1", ngImport: i0, type: ODataModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ODataModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.1.1", ngImport: i0, type: ODataModule, imports: [HttpClientModule] });
ODataModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.1.1", ngImport: i0, type: ODataModule, providers: [ODataClient, ODataServiceFactory], imports: [HttpClientModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.1", ngImport: i0, type: ODataModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [HttpClientModule],
                    providers: [ODataClient, ODataServiceFactory],
                }]
        }] });

/*
 * Public API Surface of angular-odata
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BUBBLING, Dates, Durations, EDM_PARSERS, Enums, Http, INCLUDE_DEEP, INCLUDE_SHALLOW, ITEM_ROOT, Model, ModelField, NONE_PARSER, ODATA_CONFIGURATIONS, OData, ODataActionResource, ODataAnnotations, ODataApi, ODataBaseService, ODataBatchRequest, ODataBatchResource, ODataCache, ODataCallable, ODataCallableParser, ODataClient, ODataCollection, ODataCountResource, ODataEntitiesAnnotations, ODataEntityAnnotations, ODataEntityContainer, ODataEntityResource, ODataEntitySet, ODataEntitySetResource, ODataEntitySetService, ODataEntityTypeKey, ODataEnumType, ODataEnumTypeFieldParser, ODataEnumTypeParser, ODataFunctionResource, ODataInMemoryCache, ODataInStorageCache, ODataMediaResource, ODataMetadata, ODataMetadataResource, ODataModel, ODataModelEvent, ODataModelField, ODataModelOptions, ODataModelState, ODataModule, ODataNavigationPropertyResource, ODataParameterParser, ODataPathSegments, ODataPathSegmentsHandler, ODataPropertyAnnotations, ODataPropertyResource, ODataQueryOptionHandler, ODataQueryOptions, ODataQueryOptionsHandler, ODataReferenceResource, ODataReferential, ODataRequest, ODataResource, ODataResponse, ODataSchema, ODataServiceFactory, ODataSettings, ODataSingletonResource, ODataSingletonService, ODataStructuredType, ODataStructuredTypeFieldParser, ODataStructuredTypeParser, ODataValueResource, Objects, PathSegmentNames, QueryCustomTypes, QueryOptionNames, RESERVED_FIELD_NAMES, SegmentHandler, StandardAggregateMethods, Strings, Types, Urls, alias, binary, buildPathAndQuery, createSettings, duration, isQueryCustomType, normalizeValue, raw };
